/**
 * 🚀 Multi Campaign Creator
 * Hauptformular für die Erstellung von Multi-Campaign Jobs mit mehreren Slots
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation } from 'react-query';
import { 
  Plus, X, Users, Server, Settings, 
  Play, Layers, Target, BarChart3, AlertCircle,
  ChevronDown, ChevronUp, Copy, Shuffle, Activity, Clock, Zap, Link
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import CampaignSlotEditor from './CampaignSlotEditor';
import SmartReplicateModal from './SmartReplicateModal';
import AwsRedirectConfig from './AwsRedirectConfig';
import SlotRedirectConfig from './SlotRedirectConfig';
import SearchAppRedirectConfig from './SearchAppRedirectConfig';
import DoorOpenerConfig from './DoorOpenerConfig';
import FingerprintConfig from './FingerprintConfig';
import AdminLinkPanel from '../Jobs/AdminLinkPanel';
import {
  MULTI_CAMPAIGN_DEFAULT_AWS_REGIONS,
  MULTI_CAMPAIGN_EXCLUDED_AWS_REGIONS,
  pickAwsRegionsFromLinkTypes,
  sanitizeAwsRegions,
} from '../../constants/awsRegions';
import { useAuth } from '../../services/AuthService';
import {
  generatePresetRules,
  resolveSubjectFromNameForSegment
} from '../../utils/creatorGeneratorUtils';

const sanitizeMultiCampaignAwsRegions = sanitizeAwsRegions;

const resolveMultiCampaignAwsRegion = (cfg, segmentIndex = 0) => {
  const configured = Array.isArray(cfg?.regions) && cfg.regions.length > 0
    ? cfg.regions
    : (cfg?.region ? [cfg.region] : []);
  const pool = sanitizeMultiCampaignAwsRegions(configured);
  return pool[segmentIndex % pool.length];
};

const splitMessageVariantsFromContent = (content) => {
  const raw = String(content || '');
  if (!raw.trim()) return [];
  const delimiter = /<!--\s*(?:letter|template)\s*-->/i;
  if (delimiter.test(raw)) {
    return raw
      .split(delimiter)
      .map(part => String(part || '').trim())
      .filter(part => part.length > 0);
  }
  return [raw.trim()];
};

// 📁 Folder-Picker mit webkitdirectory - laesst User ganze Ordner mit zigtausenden Templates auswaehlen
// ohne einzeln zu markieren. React warnt bei webkitdirectory als JSX-Attribut, deshalb via ref gesetzt.
const HtmlFolderPicker = ({ onFilesSelected, disabled }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.setAttribute('webkitdirectory', '');
      ref.current.setAttribute('directory', '');
      ref.current.setAttribute('mozdirectory', '');
    }
  }, []);
  return (
    <label
      className={`inline-flex items-center gap-1 px-3 py-1 border rounded text-xs whitespace-nowrap ${
        disabled
          ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-400 cursor-not-allowed'
          : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 cursor-pointer'
      }`}
      title="Ganzen Ordner mit HTML-Templates auswählen (auch bei 10k+ Dateien)"
    >
      📁 Ordner wählen
      <input
        ref={ref}
        type="file"
        onChange={onFilesSelected}
        disabled={disabled}
        className="hidden"
      />
    </label>
  );
};

const distributeTemplatesEvenly = (templates, segmentsCount) => {
  if (!Array.isArray(templates) || templates.length === 0 || segmentsCount <= 0) {
    return Array.from({ length: Math.max(segmentsCount, 0) }, () => []);
  }
  const total = templates.length;
  if (total >= segmentsCount) {
    // Mehr Templates als Segmente: aufteilen
    const base = Math.floor(total / segmentsCount);
    const remainder = total % segmentsCount;
    const distribution = [];
    let cursor = 0;
    for (let i = 0; i < segmentsCount; i++) {
      const count = base + (i < remainder ? 1 : 0);
      distribution.push(templates.slice(cursor, cursor + count));
      cursor += count;
    }
    return distribution;
  } else {
    // Weniger Templates als Segmente: Round-Robin (jedes Segment bekommt 1 Template)
    return Array.from({ length: segmentsCount }, (_, i) => [templates[i % total]]);
  }
};

// 🎯 Segmented Slots View Component
const SegmentedSlotsView = ({ slots, providerSegments, smtpServers, onUpdateSlot, onRemoveSlot }) => {
  const [expandedSegments, setExpandedSegments] = useState(new Set());
  
  // Gruppiere Slots nach provider_segment_id
  const segmentGroups = {};
  slots.forEach(slot => {
    const segId = slot.provider_segment_id;
    if (!segmentGroups[segId]) {
      segmentGroups[segId] = [];
    }
    segmentGroups[segId].push(slot);
  });
  
  const toggleSegment = (segmentId) => {
    const newExpanded = new Set(expandedSegments);
    if (newExpanded.has(segmentId)) {
      newExpanded.delete(segmentId);
    } else {
      newExpanded.add(segmentId);
    }
    setExpandedSegments(newExpanded);
  };
  
  return (
    <div className="space-y-4">
      {Object.entries(segmentGroups).map(([segmentId, segmentSlots]) => {
        const segment = providerSegments.find(s => s.segment_number === parseInt(segmentSlots[0]?.name?.match(/Slot \d+/)?.[0]?.split(' ')[0]) || s.provider_name === segmentSlots[0]?.name?.split(' ')[0]);
        const segmentConfig = segment?.config || {};
        const isExpanded = expandedSegments.has(segmentId);
        
        // Berechne Segment-Stats
        const totalRecipients = segmentSlots.reduce((sum, s) => sum + (s.recipient_count || 0), 0);
        const minRate = Math.min(...segmentSlots.map(s => s.emails_per_hour));
        const maxRate = Math.max(...segmentSlots.map(s => s.emails_per_hour));
        const avgRate = Math.round(segmentSlots.reduce((sum, s) => sum + s.emails_per_hour, 0) / segmentSlots.length);
        const streams = segmentConfig.versand?.streams_count || 1;
        
        // Geschätzte Dauer (sehr grob)
        const estimatedHours = Math.ceil(totalRecipients / avgRate);
        
        const segmentName = segmentSlots[0]?.name?.split(' ')[0] || `Segment ${segmentId}`;
        const variantGroups = [...new Set(segmentSlots.map(s => s.ai_content_variant_group))];
        
        return (
          <div key={segmentId} className="border border-gray-200 dark:border-dark-copper rounded-lg overflow-hidden">
            {/* Segment Header */}
            <div 
              onClick={() => toggleSegment(segmentId)}
              className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-dark-surface dark:to-dark-card p-4 cursor-pointer hover:from-blue-100 hover:to-purple-100 dark:hover:from-dark-card dark:hover:to-dark-surface transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      📧 Segment: {segmentName}
                    </h4>
                    <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded-full">
                      {segmentSlots.length} Slots
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <Users className="h-4 w-4 text-gray-500" />
                      <span className="text-gray-700 dark:text-gray-200">
                        <strong>{totalRecipients.toLocaleString()}</strong> Empfänger
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Activity className="h-4 w-4 text-gray-500" />
                      <span className="text-gray-700 dark:text-gray-200">
                        <strong>{streams}</strong> Streams
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Zap className="h-4 w-4 text-gray-500" />
                      <span className="text-gray-700 dark:text-gray-200">
                        {minRate === maxRate ? (
                          <><strong>{avgRate}</strong>/h</>
                        ) : (
                          <><strong>{minRate}-{maxRate}</strong>/h</>
                        )}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-gray-500" />
                      <span className="text-gray-700 dark:text-gray-200">
                        ~<strong>{estimatedHours}h</strong> Dauer
                      </span>
                    </div>
                  </div>
                  
                  <div className="mt-2 flex items-center space-x-4 text-xs text-gray-600 dark:text-gray-400">
                    {segmentConfig.versand?.pause_after_each_slot && (
                      <span>⏸️ {segmentConfig.versand.pause_after_each_slot}s Pause/Slot</span>
                    )}
                    {segmentConfig.sending_pattern?.jitter?.enabled && (
                      <span>🔄 Jitter ±{segmentConfig.sending_pattern.jitter.max_ms}ms</span>
                    )}
                    {segmentConfig.headers?.custom_headers && Object.keys(segmentConfig.headers.custom_headers).length > 0 && (
                      <span>🎨 {Object.keys(segmentConfig.headers.custom_headers).length} Custom Headers</span>
                    )}
                    {segmentConfig.smtp?.server_ids && segmentConfig.smtp.server_ids.length > 0 && (
                      <span>📨 {segmentConfig.smtp.server_ids.length} SMTP-Server</span>
                    )}
                    {variantGroups.length > 0 && (
                      <span>🤖 {variantGroups.length} AI-Inputs: {variantGroups.map(v => v?.toUpperCase()).join(', ')}</span>
                    )}
                  </div>
                </div>
                
                <div className="ml-4">
                  {isExpanded ? (
                    <ChevronUp className="h-6 w-6 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-6 w-6 text-gray-400" />
                  )}
                </div>
              </div>
            </div>
            
            {/* Segment Slots (expanded) */}
            {isExpanded && (
              <div className="p-4 space-y-3 bg-gray-50 dark:bg-dark-card">
                {segmentSlots.map((slot, index) => (
                  <CampaignSlotEditor
                    key={slot.id}
                    slot={slot}
                    slotNumber={slot.slot_number}
                    centralRecipients={[]}
                    domainAnalysis={null}
                    smtpServers={smtpServers}
                    globalAttachments={[]}
                    onUpdate={(updates) => onUpdateSlot(slot.id, updates)}
                    onRemove={() => onRemoveSlot(slot.id)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// 🔗 Sequential AWS/Search.app Config - Eigene Komponente mit eigenem State
const SequentialAwsSearchAppConfig = ({ autoAiConfig, setAutoAiConfig, isAdmin }) => {
  const [awsSlots, setAwsSlots] = useState([]);
  const [searchappSlots, setSearchappSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAwsSlot, setSelectedAwsSlot] = useState(null);
  const [selectedSearchappSlot, setSelectedSearchappSlot] = useState(null);
  const [useCustomDomains, setUseCustomDomains] = useState(false);
  const [customDomains, setCustomDomains] = useState('');
  
  // Lade AWS Slots
  useEffect(() => {
    if (isAdmin) {
      loadSlots();
    }
  }, [isAdmin]);
  
  const loadSlots = async () => {
    setLoading(true);
    try {
      const [awsRes, searchRes] = await Promise.all([
        axios.get('/api/aws-redirect/slots'),
        axios.get('/api/searchapp-redirect/slots')
      ]);
      setAwsSlots(awsRes.data.data?.slots || awsRes.data.slots || []);
      setSearchappSlots(searchRes.data.data?.slots || searchRes.data.slots || []);
    } catch (error) {
      console.error('Failed to load redirect slots:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // AWS Config in autoAiConfig speichern
  const awsConfig = autoAiConfig.aws_redirects || { enabled: false };
  const searchConfig = autoAiConfig.searchapp_redirects || { enabled: false };
  
  const updateAwsConfig = (updates) => {
    setAutoAiConfig(prev => ({
      ...prev,
      aws_redirects: { ...prev.aws_redirects, ...updates }
    }));
  };
  
  const updateSearchConfig = (updates) => {
    setAutoAiConfig(prev => ({
      ...prev,
      searchapp_redirects: { ...prev.searchapp_redirects, ...updates }
    }));
  };
  
  if (!isAdmin) return null;
  
  return (
    <div className="space-y-4 mt-4 pt-4 border-t border-purple-300">
      <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 flex items-center">
        <Link className="h-4 w-4 mr-2 text-green-600" />
        🔗 Redirect-Links (Campaign-Level)
      </h4>
      
      {/* AWS S3 Redirects */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-4 rounded-lg border border-blue-300">
        <div className="flex items-center justify-between mb-3">
          <span className="font-medium text-gray-800 dark:text-gray-200">🪣 AWS S3 Redirects</span>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={awsConfig.enabled || false}
              onChange={(e) => updateAwsConfig({ enabled: e.target.checked })}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded cursor-pointer"
            />
            <span className="text-sm text-gray-700 dark:text-gray-200">Aktiviert</span>
          </label>
        </div>
        
        {awsConfig.enabled && (
          <div className="space-y-3">
            {loading ? (
              <div className="text-sm text-gray-500">Lade Slots...</div>
            ) : awsSlots.length > 0 ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Redirect-Slot wählen
                </label>
                <select
                  value={selectedAwsSlot ? awsSlots.indexOf(selectedAwsSlot) : ''}
                  onChange={(e) => {
                    const slot = awsSlots[parseInt(e.target.value)];
                    setSelectedAwsSlot(slot);
                    if (slot) {
                      updateAwsConfig({
                        slot: slot,
                        domains: slot.domains,
                        target_url: slot.target_url
                      });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">-- Slot wählen --</option>
                  {awsSlots.map((slot, idx) => (
                    <option key={idx} value={idx}>
                      {slot.type === 'landing' ? '🌐' : '🔀'} {slot.target_url} ({slot.type === 'landing' ? `Landing · ${slot.total_domains || slot.domains?.length || 0} Domains` : `${slot.total_domains || slot.domains?.length || 0} Domains`})
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="text-sm text-orange-600">Keine AWS-Slots verfügbar</div>
            )}
            
            {selectedAwsSlot && (
              <div className="text-xs text-green-600">
                ✅ {selectedAwsSlot.domains?.length || 0} Domains ausgewählt
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Links pro
              </label>
              <select
                value={awsConfig.links_per_mode || 'per_slot'}
                onChange={(e) => updateAwsConfig({ links_per_mode: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="per_slot">Pro Slot (1 Linkset pro Slot)</option>
                <option value="per_smtp">Pro SMTP (1 Linkset pro aktivem SMTP)</option>
              </select>
            </div>
          </div>
        )}
      </div>
      
      {/* Search.app Redirects */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 p-4 rounded-lg border border-green-300">
        <div className="flex items-center justify-between mb-3">
          <span className="font-medium text-gray-800 dark:text-gray-200">🔍 Search.app Redirects</span>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={searchConfig.enabled || false}
              onChange={(e) => updateSearchConfig({ enabled: e.target.checked })}
              className="h-4 w-4 text-green-600 border-gray-300 rounded cursor-pointer"
            />
            <span className="text-sm text-gray-700 dark:text-gray-200">Aktiviert</span>
          </label>
        </div>
        
        {searchConfig.enabled && (
          <div className="space-y-3">
            {/* Custom Domains Toggle */}
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={useCustomDomains}
                onChange={(e) => setUseCustomDomains(e.target.checked)}
                className="h-4 w-4 text-green-600 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700 dark:text-gray-200">Custom Domains eingeben</span>
            </label>
            
            {useCustomDomains ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Domains (eine pro Zeile)
                </label>
                <textarea
                  value={customDomains}
                  onChange={(e) => {
                    setCustomDomains(e.target.value);
                    const domains = e.target.value.split('\n').map(d => d.trim()).filter(d => d);
                    updateSearchConfig({ domains: domains });
                  }}
                  placeholder="domain1.search.app&#10;domain2.search.app"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm h-24"
                />
              </div>
            ) : (
              loading ? (
                <div className="text-sm text-gray-500">Lade Slots...</div>
              ) : searchappSlots.length > 0 ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Redirect-Slot wählen
                  </label>
                  <select
                    value={selectedSearchappSlot ? searchappSlots.indexOf(selectedSearchappSlot) : ''}
                    onChange={(e) => {
                      const slot = searchappSlots[parseInt(e.target.value)];
                      setSelectedSearchappSlot(slot);
                      if (slot) {
                        updateSearchConfig({
                          slot: slot,
                          domains: slot.domains
                        });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="">-- Slot wählen --</option>
                    {searchappSlots.map((slot, idx) => (
                      <option key={idx} value={idx}>
                        {slot.type === 'landing' ? '🌐' : '🔀'} {slot.name || `Slot ${idx + 1}`} ({slot.type === 'landing' ? `Landing · ${slot.domains?.length || 0} Domains` : `${slot.domains?.length || 0} Domains`})
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="text-sm text-orange-600">Keine Search.app-Slots verfügbar</div>
              )
            )}
            
            {(selectedSearchappSlot || (searchConfig.domains && searchConfig.domains.length > 0)) && (
              <div className="text-xs text-green-600">
                ✅ {searchConfig.domains?.length || selectedSearchappSlot?.domains?.length || 0} Domains konfiguriert
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Status-Anzeige */}
      {(awsConfig.enabled || searchConfig.enabled) && (
        <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded text-sm text-green-800 dark:text-green-200">
          ✅ Redirect-Links werden beim Generieren der AI-Slots automatisch erstellt
        </div>
      )}
    </div>
  );
};

const PARALLEL_SEGMENT_PRESETS = [4, 6, 8, 10, 15];
const GMX_WEBMAIL_DEFAULT_RATE_PER_HOUR = 2000;
const GMX_WEBMAIL_DEFAULT_PARALLEL_SEGMENTS = 6;
const GMX_WEBMAIL_DEFAULT_JITTER = { enabled: true, min_ms: 0, max_ms: 1000 };
const APP_WEBMAIL_SEND_METHODS = ['gmx_webmail', 'rambler_webmail'];

const isAppWebmailSendMethod = (sendMethod) => (
  APP_WEBMAIL_SEND_METHODS.includes(sendMethod)
);

const getSendMethodLabel = (sendMethod) => {
  if (sendMethod === 'graph') return 'Graph API';
  if (sendMethod === 'gmx_webmail') return 'GMX-Webmail';
  if (sendMethod === 'rambler_webmail') return 'Rambler-Webmail';
  if (sendMethod === 'webmail') return 'Webmail';
  return 'SMTP';
};

const getDefaultParallelSegments = () => 6;

const getDefaultRateLimitPerHour = (sendMethod) => (
  isAppWebmailSendMethod(sendMethod) ? GMX_WEBMAIL_DEFAULT_RATE_PER_HOUR : 3000
);

const parseCampaignConfig = (rawConfig) => {
  if (!rawConfig) return {};
  if (typeof rawConfig === 'object') return rawConfig;
  try {
    return JSON.parse(rawConfig);
  } catch {
    return {};
  }
};

const normalizeSendMethod = (sendMethod) => (
  ['smtp', 'webmail', 'graph', 'gmx_webmail', 'rambler_webmail'].includes(sendMethod)
    ? sendMethod
    : 'smtp'
);

const isGraphEligibleSmtpServer = (server) => {
  const hasValue = (value) => (
    typeof value === 'string'
      ? value.trim().length > 0
      : value !== null && value !== undefined && value !== false
  );

  const oauthReady = server?.graph_oauth_ready === true
    || Number(server?.graph_oauth_ready) === 1
    || (hasValue(server?.oauth2_client_id) && hasValue(server?.oauth2_refresh_token));
  if (!oauthReady) {
    return false;
  }

  const accountEmail = String(server?.from_email || server?.email || server?.username || '')
    .trim()
    .toLowerCase();
  const atIndex = accountEmail.lastIndexOf('@');
  const accountDomain = atIndex >= 0 ? accountEmail.slice(atIndex + 1) : '';
  const host = String(server?.host || '')
    .trim()
    .toLowerCase()
    .replace(/^smtps?:\/\//, '')
    .split('/')[0]
    .replace(/:\d+$/, '')
    .replace(/\.$/, '');

  const hasMicrosoftDomain = /^(outlook|hotmail|live|msn)\.[a-z]{2,}(?:\.[a-z]{2,})?$/.test(accountDomain);
  const hasMicrosoftHost = host === 'smtp-mail.outlook.com'
    || host === 'smtp.office365.com'
    || host.endsWith('.office365.com');

  return hasMicrosoftDomain || hasMicrosoftHost;
};

const isGmxWebmailEligibleSmtpServer = (server) => {
  if (Number(server?.is_active ?? 1) === 0) return false;
  if (server?.gmx_webmail_ready === true || Number(server?.gmx_webmail_ready) === 1) {
    return true;
  }
  const fromEmail = String(server?.from_email || server?.email || server?.username || '')
    .trim()
    .toLowerCase();
  const host = String(server?.host || '')
    .trim()
    .toLowerCase()
    .replace(/^smtps?:\/\//, '')
    .split('/')[0]
    .replace(/:\d+$/, '');
  return (
    /@gmx\.(com|us)$/.test(fromEmail)
    || /@mail\.com$/.test(fromEmail)
    || host === 'mail.gmx.com'
    || host === 'smtp.gmx.com'
    || host.endsWith('.gmx.com')
    || host === 'smtp.mail.com'
  );
};

const isRamblerWebmailEligibleSmtpServer = (server) => {
  if (Number(server?.is_active ?? 1) === 0) return false;
  if (
    server?.rambler_webmail_ready === true
    || Number(server?.rambler_webmail_ready) === 1
  ) {
    return true;
  }
  const username = String(server?.username || server?.from_email || '')
    .trim()
    .toLowerCase();
  const fromEmail = String(server?.from_email || server?.username || '')
    .trim()
    .toLowerCase();
  const hasPassword = String(server?.password || server?.smtp_password || '').trim() !== '';
  return (
    hasPassword
    && /@rambler\.ru$/.test(username)
    && /@rambler\.ru$/.test(fromEmail)
  );
};

const MultiCampaignCreator = ({ onClose, onSuccess, editCampaign = null }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [campaignData, setCampaignData] = useState({
    name: '',
    description: '',
    execution_mode: 'sequential',
    central_recipients: [],
    global_attachments: [],
    slots: [],
    
    // 📧 Global List-Unsubscribe (für alle Slots)
    list_unsubscribe_enabled: false,
    list_unsubscribe_url: '{Domain}/unsubscribe',
    list_unsubscribe_method: 'oneclick',

    // ✨ ZW-Injection (global, nicht pro Segment)
    zerowidth_subject: true,
    zw_subject_pct: 100,
    zerowidth_fromname: true,
    zerowidth_content: false,
    garbage_code: false,
    use_multipart: true,
    zw_textpart: true,
    multipart_dice_enabled: false,
    tld_rotation: false,
    segment_rules: [],

    // 🎨 Segmented HTML Upload (global)
    segmented_template_subject: '',
    segmented_template_from_name: '',
    subject_fromname_rules: [],
    
    // ⏸️ Campaign-Level Pausen
    pause_after_each_slot: null,
    pause_after_n_slots: null,
    pause_after_n_mails: null,
    
    // 🔌 External API - Kundenauftrag
    is_customer_order: false,
    
    // 🎯 Segmented-Parallel (NEU!)
    provider_segments: [],  // [{provider_name, recipients, config, ai_variants, slots}]
    
    // 🔗 AWS/Search.app Links für Sequential Campaigns
    sequential_aws_enabled: false,
    sequential_aws_domains: [],
    sequential_aws_target_url: '',
    sequential_aws_links_per: 'per_slot',  // 'per_slot' oder 'per_smtp'
    sequential_searchapp_enabled: false,
    sequential_searchapp_domains: [],
    
    // 🌐 Versandmethode
    send_method: 'smtp',  // 'smtp', 'webmail', 'graph', 'gmx_webmail' oder 'rambler_webmail'
    selected_webmail_accounts: [],
    webmail_parallel_threads: 2,  // Max 2 parallele Sessions pro Account (getestet!)
    webmail_text_type: 'html',  // 'html' oder 'plain'
    rr_parallel_segments: 6,
    random_runtime_enabled: true,
    random_messages_per_connection: false
  });
  // 🌍 Proxy-Auswahl entfernt:
  // Proxy wird global pro SMTP aus der DB genutzt (711 Session in smtp_servers.custom_proxy_*).
  // Frontend sendet keine proxy_settings mehr, Backend ignoriert proxy_settings ebenfalls.
  
  // 🎯 Segmented-Parallel States (NEU!)
  const [providerSegments, setProviderSegments] = useState([]);
  const [segmentAnalyzing, setSegmentAnalyzing] = useState(false);
  const [currentSegment, setCurrentSegment] = useState(null);
  const [segmentConfigModalOpen, setSegmentConfigModalOpen] = useState(false);
  const [smartReplicateModalOpen, setSmartReplicateModalOpen] = useState(false);

  const configuredParallelSegments = campaignData.rr_parallel_segments
    ?? getDefaultParallelSegments(campaignData.execution_mode, campaignData.send_method);
  const segmentCount = providerSegments.length;
  const effectiveParallelSegments = segmentCount > 0
    ? Math.min(configuredParallelSegments, segmentCount)
    : configuredParallelSegments;

  const renderParallelSegmentsControl = () => (
    <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-700">
      <label className="flex items-center gap-2 mb-2 cursor-pointer">
        <input
          type="checkbox"
          checked={campaignData.random_runtime_enabled !== false}
          onChange={(e) => setCampaignData(prev => ({ ...prev, random_runtime_enabled: e.target.checked }))}
          className="h-3.5 w-3.5 text-indigo-600 border-gray-300 rounded"
        />
        <span className="text-xs font-semibold text-indigo-800 dark:text-indigo-200">
          Segment-Parallelität shufflen
        </span>
        <span className="text-[10px] text-gray-500 dark:text-gray-400">
          3–25 parallele Segmente · neue Phase alle 5–25 Minuten
        </span>
      </label>
      <label className="flex items-center gap-2 mb-2 cursor-pointer">
        <input
          type="checkbox"
          checked={campaignData.random_messages_per_connection === true}
          onChange={(e) => setCampaignData(prev => ({ ...prev, random_messages_per_connection: e.target.checked }))}
          className="h-3.5 w-3.5 text-indigo-600 border-gray-300 rounded"
        />
        <span className="text-xs font-semibold text-indigo-800 dark:text-indigo-200">
          Connections / Mails shufflen
        </span>
        <span className="text-[10px] text-gray-500 dark:text-gray-400">
          zufällige Quote von 1–n Mails je Verbindung · danach Reconnect
        </span>
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-indigo-800 dark:text-indigo-200 shrink-0">Parallel-Segmente</span>
        {PARALLEL_SEGMENT_PRESETS.map((preset) => {
          const exceedsSegments = segmentCount > 0 && preset > segmentCount;
          return (
            <button
              key={preset}
              type="button"
              disabled={exceedsSegments || campaignData.random_runtime_enabled !== false}
              title={campaignData.random_runtime_enabled !== false
                ? 'Wird nur verwendet, wenn das Segment-Shuffling ausgeschaltet ist'
                : (exceedsSegments ? `Nur ${segmentCount} Segmente vorhanden` : undefined)}
              onClick={() => setCampaignData(prev => ({ ...prev, rr_parallel_segments: preset }))}
              className={`px-2.5 py-1 rounded text-xs font-bold border transition-colors ${
                configuredParallelSegments === preset
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : exceedsSegments
                    ? 'bg-gray-100 dark:bg-dark-surface text-gray-400 border-gray-200 dark:border-gray-700 cursor-not-allowed opacity-50'
                    : 'bg-white dark:bg-dark-card text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/30'
              }`}
            >
              {preset}
            </button>
          );
        })}
        <span className="text-[10px] text-gray-500 dark:text-gray-400">
          {segmentCount > 0
            ? (effectiveParallelSegments < configuredParallelSegments
              ? `${configuredParallelSegments} gewählt · effektiv max. ${effectiveParallelSegments} (${segmentCount} Segmente)`
              : `max. ${effectiveParallelSegments} gleichzeitig (${segmentCount} Segmente)`)
            : `max. ${effectiveParallelSegments} gleichzeitig (nach Segmentierung begrenzt)`}
        </span>
      </div>
    </div>
  );

  // Wenn editCampaign übergeben wird ODER sessionStorage-Daten vorhanden, Daten laden
  useEffect(() => {
    // Prüfe sessionStorage für Copy-Daten
    const copyData = sessionStorage.getItem('multiCampaignCopyData');
    if (copyData && !editCampaign) {
      const parsed = JSON.parse(copyData);
      const parsedConfig = parseCampaignConfig(parsed.campaign_config);
      setCampaignData(prev => ({
        ...prev,
        name: parsed.name || '',
        description: parsed.description || '',
        execution_mode: parsed.execution_mode || 'sequential',
        central_recipients: parsed.central_recipients || [],
        global_attachments: parsed.global_attachments || [],
        slots: parsed.slots || [],
        list_unsubscribe_enabled: parsed.list_unsubscribe_enabled || false,
        list_unsubscribe_url: parsed.list_unsubscribe_url || '{Domain}/unsubscribe',
        list_unsubscribe_method: parsed.list_unsubscribe_method || 'oneclick',
        is_customer_order: parsed.is_customer_order || false,
        send_method: normalizeSendMethod(parsedConfig.send_method),
        rr_parallel_segments: parsedConfig.rr_parallel_segments
          ?? getDefaultParallelSegments(parsed.execution_mode, normalizeSendMethod(parsedConfig.send_method)),
        random_runtime_enabled: parsedConfig.random_runtime_enabled === undefined
          ? !isAppWebmailSendMethod(normalizeSendMethod(parsedConfig.send_method))
          : parsedConfig.random_runtime_enabled !== false,
        random_messages_per_connection: parsedConfig.random_messages_per_connection === true
      }));
      
      if (parsed.central_recipients && parsed.central_recipients.length > 0) {
        setRecipientText(parsed.central_recipients.join('\n'));
      }
      
      sessionStorage.removeItem('multiCampaignCopyData');  // Cleanup
      toast.success(`Campaign-Kopie geladen: ${parsed.slots?.length || 0} Slots`);
      setCurrentStep(4);  // Gehe direkt zu Review
    } else if (editCampaign) {
      const editConfig = parseCampaignConfig(editCampaign.campaign_config);
      setCampaignData(prev => ({
        ...prev,
        name: editCampaign.name || '',
        description: editCampaign.description || '',
        execution_mode: editCampaign.execution_mode || 'sequential',
        central_recipients: editCampaign.central_recipients || [],
        global_attachments: editCampaign.global_attachments || [],
        slots: editCampaign.slots || [],
        is_customer_order: editCampaign.is_customer_order || false,
        send_method: normalizeSendMethod(editConfig.send_method),
        rr_parallel_segments: editConfig.rr_parallel_segments
          ?? getDefaultParallelSegments(editCampaign.execution_mode, normalizeSendMethod(editConfig.send_method)),
        random_runtime_enabled: editConfig.random_runtime_enabled === undefined
          ? !isAppWebmailSendMethod(normalizeSendMethod(editConfig.send_method))
          : editConfig.random_runtime_enabled !== false,
        random_messages_per_connection: editConfig.random_messages_per_connection === true
      }));
      
      if (editCampaign.central_recipients && editCampaign.central_recipients.length > 0) {
        setRecipientText(editCampaign.central_recipients.join('\n'));
      }

      // Proxy-Settings wurden entfernt (Proxy läuft pro SMTP aus DB, keine Campaign-Overrides mehr)
    }
  }, [editCampaign, isAdmin]);

  // Alte useEffects entfernt - werden durch neues Proxy-System ersetzt

  const [currentStep, setCurrentStep] = useState(1); // 1: Basic, 2: Recipients, 3: Slots, 4: Review
  const [recipientText, setRecipientText] = useState('');
  const [domainAnalysis, setDomainAnalysis] = useState(null);
  const [slotSuggestions, setSlotSuggestions] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [segmentedHtmlTemplates, setSegmentedHtmlTemplates] = useState([]);
  const [openerHtmlTemplates, setOpenerHtmlTemplates] = useState([]);
  const [segmentStripHtmlWrapper, setSegmentStripHtmlWrapper] = useState(false);
  const [htmlLinkConfig, setHtmlLinkConfig] = useState(null);
  const [segmentationRecipientsSnapshot, setSegmentationRecipientsSnapshot] = useState([]);
  
  // 🎯 NEUE FEATURES: Automatische Slot-Erstellung
  const [autoSlotMode, setAutoSlotMode] = useState(false);
  const [autoSlotSize, setAutoSlotSize] = useState(100);
  const [autoSlotSmtpMode, setAutoSlotSmtpMode] = useState('rotation');
  const [bankGenBank, setBankGenBank] = useState('spk');
  const [bankGenCount, setBankGenCount] = useState(4);
  const [htmlGenScript, setHtmlGenScript] = useState('');
  const [htmlGenCount, setHtmlGenCount] = useState(100);
  const [htmlGenCta, setHtmlGenCta] = useState('{{storycta}}');
  const [htmlGenLogo, setHtmlGenLogo] = useState('');
  const [htmlGenQr, setHtmlGenQr] = useState('{{qrcta}}');
  const [htmlGenIncludeLogo, setHtmlGenIncludeLogo] = useState(false);
  const handleHtmlGenLogoToggle = (enabled) => {
    setHtmlGenIncludeLogo(enabled);
    if (enabled) setHtmlGenLogo(current => current || '{{brandlogo}}');
  };
  const [htmlGeneratorCatalog, setHtmlGeneratorCatalog] = useState([]);
  const [subjectFromPools, setSubjectFromPools] = useState([]);
  const [multipartPct] = useState(30); // eslint-disable-line no-unused-vars

  useEffect(() => {
    Promise.all([
      axios.get('/api/creator/html-generators'),
      axios.get('/api/creator/subject-from/pools')
    ]).then(([htmlResponse, poolResponse]) => {
      setHtmlGeneratorCatalog(htmlResponse.data?.data?.generators || []);
      setSubjectFromPools(poolResponse.data?.data?.pools || []);
    }).catch((error) => {
      console.error('Creator generator catalog failed:', error);
    });
  }, []);
  const [autoSlotTemplate, setAutoSlotTemplate] = useState({
    subject: '',
    message: '',
    from_name: '',
    emails_per_hour: 3800
  });
  
  // 🎯 GLOBAL RATE CONFIG (für alle Segmente gleichzeitig)
  const [globalRateConfig, setGlobalRateConfig] = useState({
    pattern: 'constant',
    baseRate: 3000,
    streams: 1,
    jitter: { enabled: false, min_ms: -500, max_ms: 500 }
  });

  const handleSendMethodChange = (sendMethod) => {
    const isAppWebmail = isAppWebmailSendMethod(sendMethod);
    setCampaignData(prev => ({
      ...prev,
      send_method: sendMethod,
      ...(isAppWebmail ? {
        rr_parallel_segments: GMX_WEBMAIL_DEFAULT_PARALLEL_SEGMENTS,
        random_runtime_enabled: false,
        random_messages_per_connection: false,
        slots: (prev.slots || []).map(slot => ({
          ...slot,
          emails_per_hour: GMX_WEBMAIL_DEFAULT_RATE_PER_HOUR
        }))
      } : {})
    }));

    if (isAppWebmail) {
      setGlobalRateConfig(prev => ({
        ...prev,
        baseRate: GMX_WEBMAIL_DEFAULT_RATE_PER_HOUR,
        jitter: { ...GMX_WEBMAIL_DEFAULT_JITTER }
      }));
      setProviderSegments(prev => prev.map(segment => ({
        ...segment,
        config: {
          ...(segment.config || {}),
          versand: {
            ...(segment.config?.versand || {}),
            rate_limit_per_hour: GMX_WEBMAIL_DEFAULT_RATE_PER_HOUR
          },
          sending_pattern: {
            ...(segment.config?.sending_pattern || {}),
            type: 'steady',
            jitter: { ...GMX_WEBMAIL_DEFAULT_JITTER }
          }
        }
      })));
    }
  };

  const RATE_PRESETS = {
    constant: { label: 'Constant (Gleiche Rate)', phases: [{ from: 0, to: 1, rate: 1.0 }] },
    pyramid: { label: 'Pyramid Warmup (Safe)', phases: [{ from: 0, to: 0.2, rate: 0.27 }, { from: 0.2, to: 0.8, rate: 1.33 }, { from: 0.8, to: 1, rate: 0.5 }] },
    inverse_pyramid: { label: 'Inverse Pyramid (Fast)', phases: [{ from: 0, to: 0.3, rate: 2.0 }, { from: 0.3, to: 0.7, rate: 1.0 }, { from: 0.7, to: 1, rate: 0.5 }] },
    wave: { label: 'Wave (2 Peaks)', phases: [{ from: 0, to: 0.25, rate: 0.83 }, { from: 0.25, to: 0.5, rate: 1.67 }, { from: 0.5, to: 0.75, rate: 0.83 }, { from: 0.75, to: 1, rate: 1.67 }] },
    flash_blast: { label: 'Flash Blast (Launch)', phases: [{ from: 0, to: 0.1, rate: 0.17 }, { from: 0.1, to: 0.6, rate: 2.67 }, { from: 0.6, to: 1, rate: 1.0 }] },
    ghost_protocol: { label: 'Ghost Protocol (7d Warmup)', phases: [{ from: 0, to: 0.5, rate: 0.02 }, { from: 0.5, to: 0.8, rate: 0.05 }, { from: 0.8, to: 1, rate: 0.07 }] },
    stealth_drip: { label: 'Stealth Drip (8h Spread)', phases: [{ from: 0, to: 1, rate: 0.05 }] },
    chaos_theory: { label: 'Chaos Theory (Random)', phases: 'random' },
    morning_rush: { label: 'Morning Rush (B2B)', phases: [{ from: 0, to: 0.5, rate: 1.67 }, { from: 0.5, to: 1, rate: 0.83 }] },
    evening_blast: { label: 'Evening Blast (B2C)', phases: [{ from: 0, to: 0.3, rate: 0.5 }, { from: 0.3, to: 0.8, rate: 2.0 }, { from: 0.8, to: 1, rate: 0.67 }] }
  };

  const calculateRateForPosition = (pattern, progress, baseRate) => {
    const preset = RATE_PRESETS[pattern];
    if (!preset) return baseRate;
    if (preset.phases === 'random') {
      return Math.round(baseRate * (0.1 + Math.random() * 1.6));
    }
    for (const phase of preset.phases) {
      if (progress >= phase.from && progress < phase.to) {
        return Math.round(baseRate * phase.rate);
      }
    }
    const lastPhase = preset.phases[preset.phases.length - 1];
    return Math.round(baseRate * lastPhase.rate);
  };

  const applyGlobalRateToSegments = () => {
    setProviderSegments(prev => prev.map((seg, idx) => {
      const progress = prev.length > 1 ? idx / (prev.length - 1) : 0.5;
      const rate = calculateRateForPosition(globalRateConfig.pattern, progress, globalRateConfig.baseRate);
      return {
        ...seg,
        config: {
          ...seg.config,
          versand: {
            ...(seg.config?.versand || {}),
            rate_limit_per_hour: rate,
            streams_count: globalRateConfig.streams
          },
          sending_pattern: {
            type: globalRateConfig.pattern,
            jitter: { ...globalRateConfig.jitter }
          }
        }
      };
    }));
    toast.success(`Rate-Preset "${RATE_PRESETS[globalRateConfig.pattern]?.label}" auf ${providerSegments.length} Segmente angewendet`);
  };

  // 🤖 AUTO-AI-SLOT-GENERATOR
  const [autoAiMode, setAutoAiMode] = useState(false);
  const [autoAiGenerating, setAutoAiGenerating] = useState(false);
  const [autoAiProgress, setAutoAiProgress] = useState({ current: 0, total: 0 });
  const [useFastMode, setUseFastMode] = useState(true);
  const [autoAiConfig, setAutoAiConfig] = useState({
    mode: 'manual', // manual, per_domain
    slot_count: 10,
    slot_calculation_mode: 'manual',  // 🎯 manual, aggressive, max_optimal, balanced, conservative
    recipients_per_slot: 1000,
    templates_per_slot: 1,  // 🔧 Default: 1 Template (nicht 3)
    smtp_mode: 'rotation',  // rotation, sequential, mixed, segmented
    template: {
      subject: '',
      message: '',
      from_name: '',
      emails_per_hour: 2500,
      dynamic_from_email: '',  // 📧 Dynamische Absender-E-Mail
      use_dynamic_from_email: false  // 📧 Checkbox
    },
    // ⏸️ Pause-Optionen für Auto-AI
    add_pause_after_each_slot: false,
    pause_duration_after_each: 30,  // Sekunden
    add_pause_after_n_slots: false,
    pause_after_n: 3,
    pause_duration_after_n: 60,
    // 🎨 HTML-Obfuskations-Optionen für AI-Prompt
    obfuscation: {
      enabled: true,  // Obfuskation aktivieren
      garbage_comments: true,  // HTML-Kommentare mit Garbage
      color_variation: true,  // ±8% Farbvariation
      tag_diversity: true  // Verschiedene HTML5-Tags nutzen
    },
    // 🔧 Runtime-Obfuscation (Backend processed, nicht AI!)
    runtime_obfuscation: {
      zerofront_enabled: false,  // MadCat-Style ZeroFont (User muss {zf:Text} im Template nutzen)
      shuffle_attributes: true,  // HTML-Attribute shufflen (immer an)
      shuffle_css: true,  // CSS-Properties shufflen (immer an)
      precedence_header: true,  // Precedence: first-class Header
      warmup_enabled: false,  // SMTP Warm-Up
      warmup_duration: 5  // Minuten
    },
    // 📊 SMTP-Server-Segmentierung
    smtp_segmentation: [],  // [{ server_id, start_slot, end_slot }]
    // 🌐 Provider-Segmentierung
    provider_segmentation_enabled: false,
    provider_segmentation: [],  // [{ provider, start_slot, end_slot }]
    detected_providers: []  // Automatisch erkannte Provider
  });

  // SMTP Server laden
  const { data: smtpServers } = useQuery('smtpServers', async () => {
    const response = await axios.get('/api/smtp');
    return response.data.data || [];
  });

  const graphEligibleSmtpServers = useMemo(
    () => (Array.isArray(smtpServers) ? smtpServers : []).filter(isGraphEligibleSmtpServer),
    [smtpServers]
  );
  const gmxWebmailEligibleSmtpServers = useMemo(
    () => (Array.isArray(smtpServers) ? smtpServers : []).filter(isGmxWebmailEligibleSmtpServer),
    [smtpServers]
  );
  const ramblerWebmailEligibleSmtpServers = useMemo(
    () => (Array.isArray(smtpServers) ? smtpServers : []).filter(isRamblerWebmailEligibleSmtpServer),
    [smtpServers]
  );
  const selectableSmtpServers = useMemo(
    () => {
      if (campaignData.send_method === 'graph') return graphEligibleSmtpServers;
      if (campaignData.send_method === 'gmx_webmail') return gmxWebmailEligibleSmtpServers;
      if (campaignData.send_method === 'rambler_webmail') {
        return ramblerWebmailEligibleSmtpServers;
      }
      return Array.isArray(smtpServers) ? smtpServers : [];
    },
    [
      campaignData.send_method,
      graphEligibleSmtpServers,
      gmxWebmailEligibleSmtpServers,
      ramblerWebmailEligibleSmtpServers,
      smtpServers
    ]
  );

  // Webmail Accounts laden
  const { data: webmailAccounts } = useQuery('webmailAccounts', async () => {
    const response = await axios.get('/api/webmailer');
    return response.data.data || [];
  });

  // Domain-Analyse Mutation
  const analyzeDomainsMutation = useMutation(
    async (recipients) => {
      const response = await axios.post('/api/domain-segmentation/analyze', {
        recipients
      });
      return response.data.data;
    },
    {
      onSuccess: (data) => {
        setDomainAnalysis(data);
        toast.success(`${data.total} Empfänger analysiert - ${Object.keys(data.domains).length} Domains gefunden`);
      },
      onError: (error) => {
        toast.error('Domain-Analyse fehlgeschlagen');
        console.error('Domain analysis error:', error);
      }
    }
  );

  // Multi-Campaign erstellen/bearbeiten Mutation
  const createCampaignMutation = useMutation(
    async (data) => {
      if (editCampaign) {
        // Bearbeiten
        const response = await axios.put(`/api/multi-campaigns/${editCampaign.campaign_id}`, data);
        return response.data.data;
      } else {
        // Neu erstellen
        const response = await axios.post('/api/multi-campaigns', data);
        return response.data.data;
      }
    },
    {
      onSuccess: (data) => {
        const action = editCampaign ? 'aktualisiert' : 'erstellt';
        toast.success(`Multi-Campaign "${data.name}" erfolgreich ${action}!`);
        onSuccess && onSuccess(data);
        onClose();
      },
      onError: (error) => {
        const action = editCampaign ? 'Aktualisierung' : 'Erstellung';
        toast.error(`Multi-Campaign ${action} fehlgeschlagen`);
        console.error('Campaign mutation error:', error);
      }
    }
  );

  // Empfänger aus Text parsen
  const parseRecipients = (text) => {
    if (!text) return [];
    
    const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
    const matches = text.match(emailRegex) || [];
    return matches; // ✅ Duplikate NICHT entfernen - alle E-Mails beibehalten
  };

  // Empfänger-Text ändern
  const handleRecipientsChange = (text) => {
    setRecipientText(text);
    const recipients = parseRecipients(text);
    setCampaignData(prev => ({
      ...prev,
      central_recipients: recipients
    }));

    // Auto-Analyse bei genügend Empfängern
    if (recipients.length >= 10) {
      analyzeDomainsMutation.mutate(recipients);
    }
    
    // Reset Provider-Segmente wenn Recipients ändern
    if (campaignData.execution_mode === 'segmented_parallel' || campaignData.execution_mode === 'smtp_segmented_parallel' || campaignData.execution_mode === 'smtp_round_robin' || campaignData.execution_mode === 'burst_parallel') {
      setProviderSegments([]);
      setSegmentedHtmlTemplates([]);
      setSegmentationRecipientsSnapshot([]);
    }
  };

  // Test-Mails: nicht mehr in Empfängerliste, sondern per Segment-Interval
  // Gibt die unveränderte Liste zurück + die Testmail-Config für Segment-Insertion
  const buildRecipientsWithTests = (baseRecipients) => {
    let recipientsWithTests = Array.isArray(baseRecipients) ? [...baseRecipients] : [];
    let testMailsInserted = 0;
    // Testmails werden jetzt als erster Empfänger pro X. Segment eingefügt (im Backend/Segment-Builder)
    // Hier geben wir nur die Config weiter, keine Manipulation der Empfängerliste
    return { recipientsWithTests, testMailsInserted };
  };
  const getTestMailConfig = () => {
    if (!campaignData.test_emails_enabled || !campaignData.test_email_addresses) return null;
    const testEmails = campaignData.test_email_addresses.split('\n').map(a => a.trim()).filter(a => a);
    if (!testEmails.length) return null;
    return { emails: testEmails, segmentInterval: campaignData.test_email_interval || 10 };
  };
  
  // 🎯 Provider-Segmentierung Handler
  const handleAnalyzeAndSegment = async () => {
    if (campaignData.central_recipients.length === 0) {
      toast.error('Bitte zuerst Empfänger eingeben');
      return;
    }
    
    setSegmentAnalyzing(true);
    
    try {
      // 🧪 Test-Mails einfügen (falls aktiviert)
      const {
        recipientsWithTests,
        testMailsInserted
      } = buildRecipientsWithTests(campaignData.central_recipients);
      if (testMailsInserted > 0) {
        toast.success(`✅ ${testMailsInserted} Test-Mails eingefügt (${recipientsWithTests.length} gesamt)`);
      }
      
      const response = await axios.post('/api/provider-segments/analyze', {
        recipients: recipientsWithTests
      });
      
      if (response.data.success) {
        const segments = response.data.data.segments;
        setSegmentationRecipientsSnapshot(recipientsWithTests);
        setSegmentationRecipientsSnapshot(recipientsWithTests);
        
        // Initialisiere mit Default-Config
        const isBurst = campaignData.execution_mode === 'burst_parallel';
        const initialized = segments.map((seg, idx) => ({
          ...seg,
          segment_number: idx + 1,
          config: {
            versand: isBurst ? {
              slot_size: seg.recipient_count || 100,
              total_slots: 1,
              streams_count: 1,
              rate_limit_per_hour: 9999,
              pause_after_each_slot: 0,
              use_pooled_connections: false,
              max_connections_per_smtp: 1,
              max_messages_per_connection: 1,
              burst_mode: true,
              pause_between_mails_ms: campaignData.burst_pause_ms || 2000
            } : {
              rate_limit_per_hour: getDefaultRateLimitPerHour(campaignData.send_method),
              streams_count: 1,
              use_pooled_connections: true,
              slot_size: 100
            }
          },
          ai_variants: []
        }));
        
        let initializedWithTemplates = initialized;
        if (segmentedHtmlTemplates.length > 0) {
          const distribution = distributeTemplatesEvenly(segmentedHtmlTemplates, initialized.length);
          initializedWithTemplates = initialized.map((seg, idx) => ({
            ...seg,
            html_templates: distribution[idx] || [],
            config: {
              ...seg.config,
              html_templates: distribution[idx] || [],
              template_rotation_mode: 'equal_distribution'
            }
          }));
        }

        setProviderSegments(initializedWithTemplates);
        toast.success(`✅ ${segments.length} Provider-Segmente`);
      }
    } catch (error) {
      toast.error('Segmentierung fehlgeschlagen');
    } finally {
      setSegmentAnalyzing(false);
    }
  };
  
  // 📧 SMTP-Segmentierung Handler (NEU!)
  const handleSmtpAnalyzeAndSegment = async () => {
    if (campaignData.central_recipients.length === 0) {
      toast.error('Bitte zuerst Empfänger eingeben');
      return;
    }
    
    // 🌐 Bei Webmail: Webmail-Accounts prüfen statt SMTP
    const isWebmail = campaignData.send_method === 'webmail';
    const isGmxWebmail = campaignData.send_method === 'gmx_webmail';
    const isRamblerWebmail = campaignData.send_method === 'rambler_webmail';
    
    if (isWebmail) {
      const activeWebmailAccounts = webmailAccounts?.filter(a => a.is_active) || [];
      if (activeWebmailAccounts.length === 0) {
        toast.error('Keine aktiven Webmail-Accounts verfügbar');
        return;
      }
    } else {
      if (selectableSmtpServers.length === 0) {
        const accountLabel = campaignData.send_method === 'graph'
          ? 'Graph-fähigen SMTP-Accounts'
          : (campaignData.send_method === 'gmx_webmail'
            ? 'geeigneten GMX.com-/GMX.us-/mail.com-Accounts'
            : campaignData.send_method === 'rambler_webmail'
              ? 'geeigneten Rambler.ru-Accounts'
            : 'SMTP-Servern');
        toast.error(`Keine ${accountLabel} verfügbar`);
        return;
      }
    }
    
    setSegmentAnalyzing(true);
    
    try {
      // 🧪 Test-Mails einfügen (falls aktiviert)
      const {
        recipientsWithTests,
        testMailsInserted
      } = buildRecipientsWithTests(campaignData.central_recipients);
      if (testMailsInserted > 0) {
        toast.success(`✅ ${testMailsInserted} Test-Mails eingefügt (${recipientsWithTests.length} gesamt)`);
      }
      
      let response;
      
      if (isWebmail) {
        // 🌐 Webmail-Segmentierung
        const selectedIds = campaignData.selected_webmail_accounts.length > 0 
          ? campaignData.selected_webmail_accounts 
          : webmailAccounts.filter(a => a.is_active).map(a => a.id);
        
        response = await axios.post('/api/webmail-segments/analyze', {
          recipients: recipientsWithTests,
          webmail_account_ids: selectedIds
        });
      } else {
        // 📧 SMTP-Segmentierung
        const smtpIds = selectableSmtpServers.map(s => s.id);
        
        response = await axios.post('/api/smtp-segments/analyze', {
          recipients: recipientsWithTests,
          smtp_ids: smtpIds,
          send_method: campaignData.send_method || 'smtp'
        });
      }
      
      if (response.data.success) {
        const segments = response.data.data.segments;
        setSegmentationRecipientsSnapshot(recipientsWithTests);
        
        // Initialisiere mit Default-Config (unterschiedlich für SMTP vs Webmail)
        const initialized = segments.map((seg, idx) => {
          // 🌐 Webmail-spezifische Config
          if (isWebmail) {
            return {
              ...seg,
              segment_number: idx + 1,
              provider_type: 'webmail',
              config: seg.config || {
                send_method: 'webmail',
                webmail: {
                  account_id: seg.webmail_account_id,
                  account_email: seg.provider_name,
                  parallel_threads: 2,
                  text_type: campaignData.webmail_text_type || 'html'
                },
                versand: {
                  slot_size: 50,  // Kleinere Slots für Webmail
                  total_slots: Math.ceil(seg.recipient_count / 50),
                  streams_count: 1,
                  rate_limit_per_hour: 300,  // ~5 Mails/min pro Account
                  pause_after_each_slot: 15,
                  cooldown_between_batches: 12500
                },
                sending_pattern: {
                  type: 'steady',
                  jitter: { enabled: false, min_ms: 0, max_ms: 0 }
                }
              },
              ai_variants: []
            };
          }
          
          // 📧 SMTP-Config (Standard) — identisch für SMTP-, Graph- und App-Webmail-Pools
          return {
            ...seg,
            segment_number: idx + 1,
            provider_type: 'smtp',  // 📧 Explizit SMTP setzen für Smart Replicate
            config: seg.config || {
              smtp: {
                server_ids: seg.smtp_ids || [],
                domain_assignments: {}
              },
              versand: campaignData.execution_mode === 'burst_parallel' ? {
                slot_size: seg.recipient_count || 100,
                total_slots: 1,
                streams_count: 1,
                rate_limit_per_hour: 9999,
                pause_after_each_slot: 0,
                use_pooled_connections: false,
                max_connections_per_smtp: 1,
                max_messages_per_connection: 1,
                burst_mode: true,
                pause_between_mails_ms: campaignData.burst_pause_ms || 2000
              } : campaignData.execution_mode === 'smtp_round_robin' ? {
                slot_size: 1,
                total_slots: seg.recipient_count || 1,
                streams_count: 1,
                rate_limit_per_hour: getDefaultRateLimitPerHour(campaignData.send_method),
                pause_after_each_slot: 0,
                use_pooled_connections: true,
                max_connections_per_smtp: 1,
                max_messages_per_connection: 1000
              } : {
                slot_size: 100,
                total_slots: Math.ceil(seg.recipient_count / 100),
                streams_count: 1,
                rate_limit_per_hour: getDefaultRateLimitPerHour(campaignData.send_method),
                pause_after_each_slot: 0,
                use_pooled_connections: true,
                max_connections_per_smtp: 1,
                max_messages_per_connection: 1000
              },
              sending_pattern: {
                type: 'steady',
                jitter: isAppWebmailSendMethod(campaignData.send_method)
                  ? { ...GMX_WEBMAIL_DEFAULT_JITTER }
                  : { enabled: false, min_ms: -500, max_ms: 500 }
              },
              headers: {
                shuffle_mode: 'none',
                custom_headers: {},
                client_distribution: { mobile: 35, desktop: 40, webmail: 25 }
              }
            },
            ai_variants: []
          };
        });
        
        let initializedWithTemplates = initialized;
        if (segmentedHtmlTemplates.length > 0) {
          const distribution = distributeTemplatesEvenly(segmentedHtmlTemplates, initialized.length);
          initializedWithTemplates = initialized.map((seg, idx) => ({
            ...seg,
            html_templates: distribution[idx] || [],
            config: {
              ...seg.config,
              html_templates: distribution[idx] || [],
              template_rotation_mode: 'equal_distribution'
            }
          }));
        }

        setProviderSegments(initializedWithTemplates);
        toast.success(`✅ ${segments.length} ${
          isWebmail
            ? 'Webmail'
            : (isGmxWebmail
              ? 'GMX-Webmail'
              : (isRamblerWebmail ? 'Rambler-Webmail' : 'SMTP'))
        }-Segmente erstellt`);
      }
    } catch (error) {
      console.error('SMTP-Segmentierung fehlgeschlagen:', error);
      toast.error('Segmentierung fehlgeschlagen: ' + (error.response?.data?.error || error.message));
    } finally {
      setSegmentAnalyzing(false);
    }
  };

  const stripHtmlWrapper = (content) => {
    if (!segmentStripHtmlWrapper) return content;
    if (!content) return content;
    let cleaned = String(content);
    cleaned = cleaned.replace(/<!DOCTYPE[^>]*>/gi, '');
    cleaned = cleaned.replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '');
    cleaned = cleaned.replace(/<\/?html[^>]*>/gi, '');
    cleaned = cleaned.replace(/<body[^>]*>/gi, '');
    cleaned = cleaned.replace(/<\/body>/gi, '');
    cleaned = cleaned.replace(/<meta[^>]*>/gi, '');
    return cleaned.trim();
  };

  const applyHtmlTemplatesToSegments = (templates) => {
    if (!providerSegments || providerSegments.length === 0) {
      toast.error('Bitte zuerst Segmente erstellen');
      return;
    }
    const distribution = distributeTemplatesEvenly(templates, providerSegments.length);
    setProviderSegments(prev => prev.map((seg, idx) => ({
      ...seg,
      html_templates: distribution[idx] || [],
      config: {
        ...seg.config,
        html_templates: distribution[idx] || [],
        template_rotation_mode: 'equal_distribution'
      }
    })));
    setSegmentedHtmlTemplates(templates);
  };

  const buildPrepareFastHtmlGenerator = () => {
    if (!htmlGenScript) return null;
    return {
      script_id: htmlGenScript,
      count: htmlGenCount,
      cta_macro: htmlGenCta,
      ...(htmlGenIncludeLogo ? { logo_macro: htmlGenLogo || '{{brandlogo}}' } : {}),
      qr_macro: htmlGenQr,
      include_logo: htmlGenIncludeLogo,
      strip_html_wrapper: segmentStripHtmlWrapper,
      randomize_headers: true
    };
  };

  const handleGenerateSubjectFromRules = async () => {
    const total = providerSegments.length || 1000;
    const toastId = toast.loading('Generiere Subject/From-Rules im Backend...');
    try {
      const response = await axios.post('/api/creator/subject-from/generate-rules', {
        pool_key: bankGenBank,
        rule_count: bankGenCount,
        total_segments: total
      });
      const data = response.data?.data;
      const rules = data?.subject_fromname_rules || [];
      if (rules.length === 0) {
        toast.error('Keine Subject/From-Rules erzeugt', { id: toastId });
        return;
      }
      setCampaignData(prev => ({
        ...prev,
        subject_fromname_rules: rules,
        segmented_template_subject: '',
        segmented_template_from_name: ''
      }));
      const combinations = Number(data.combination_estimate) || 0;
      const formatted = combinations > 1e9
        ? `${(combinations / 1e9).toFixed(1)} Mrd`
        : combinations > 1e6
          ? `${(combinations / 1e6).toFixed(1)} Mio`
          : combinations.toLocaleString();
      toast.success(
        `${data.label || bankGenBank}: ${rules.length} Rules — ca. ${formatted} Kombinationen`,
        { id: toastId }
      );
    } catch (error) {
      toast.error(error.response?.data?.error || error.message, { id: toastId });
    }
  };

  const handleSegmentHtmlUpload = async (event) => {
    if (htmlGeneratorActive) {
      toast.error('HTML-Upload deaktiviert: Layout-Generator hat Priorität (Backend bei Fast-Prepare)');
      event.target.value = '';
      return;
    }
    // Bei webkitdirectory kommen auch Nicht-HTML-Dateien (z.B. Thumbs.db, .DS_Store) mit;
    // wir filtern client-seitig auf sinnvolle Endungen damit der Ordner-Picker nicht alles einsaugt.
    const ALLOWED_EXT = /\.(html?|txt)$/i;
    const allFiles = Array.from(event.target.files || []);
    const files = allFiles.filter(f => ALLOWED_EXT.test(f.name || ''));
    const skipped = allFiles.length - files.length;
    if (files.length === 0) {
      if (skipped > 0) {
        toast.error(`Keine .html/.htm/.txt Dateien gefunden (${skipped} ignoriert)`);
      }
      event.target.value = '';
      return;
    }

    const total = files.length;
    const toastId = toast.loading(`Lese 0 / ${total.toLocaleString()} Dateien ...`);
    // Incremental dedup via Set spart Peak-Memory gegenueber "erst alles sammeln, dann normalizeVariants"
    const variantSet = new Set();
    const errors = [];
    let lastToastUpdate = 0;

    const readAsText = (file) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(reader.error || new Error('read error'));
      reader.readAsText(file);
    });

    try {
      // Bounded concurrency: max 32 FileReader gleichzeitig statt aller 20k parallel.
      // Jeder Worker zieht das naechste File vom gemeinsamen Index, verarbeitet es direkt
      // (parse + strip + add-to-set) und verwirft den Raw-String sofort damit der GC greifen kann.
      const CONCURRENCY = 32;
      const YIELD_EVERY = 200;
      let index = 0;
      let processed = 0;

      const perFile = async (file) => {
        try {
          const content = await readAsText(file);
          const parts = splitMessageVariantsFromContent(content).map(stripHtmlWrapper);
          for (const p of parts) {
            const trimmed = String(p || '').trim();
            if (trimmed) variantSet.add(trimmed);
          }
        } catch (err) {
          errors.push({ name: file?.name, err: err?.message || String(err) });
        }
      };

      const workers = Array.from({ length: Math.min(CONCURRENCY, total) }, async () => {
        while (true) {
          const i = index++;
          if (i >= total) return;
          await perFile(files[i]);
          processed++;
          const now = Date.now();
          if (now - lastToastUpdate > 150 || processed === total) {
            lastToastUpdate = now;
            toast.loading(
              `Lese ${processed.toLocaleString()} / ${total.toLocaleString()} Dateien ...`,
              { id: toastId }
            );
          }
          // Browser-Event-Loop Luft verschaffen, damit die UI nicht einfriert
          if (processed % YIELD_EVERY === 0) {
            await new Promise(resolve => setTimeout(resolve, 0));
          }
        }
      });

      await Promise.all(workers);

      const importedVariants = Array.from(variantSet);

      if (importedVariants.length === 0) {
        toast.error('Keine gültigen HTML-Templates im Upload gefunden', { id: toastId });
        return;
      }

      if (errors.length > 0) {
        console.warn(`[handleSegmentHtmlUpload] ${errors.length} Dateien konnten nicht gelesen werden`, errors.slice(0, 10));
      }

      const suffix = errors.length > 0 ? ` — ${errors.length} Fehler` : '';
      const skipSuffix = skipped > 0 ? ` — ${skipped} Nicht-HTML übersprungen` : '';

      if (providerSegments.length > 0) {
        applyHtmlTemplatesToSegments(importedVariants);
        toast.success(
          `${importedVariants.length.toLocaleString()} HTML-Templates importiert${suffix}${skipSuffix}`,
          { id: toastId }
        );
      } else {
        setSegmentedHtmlTemplates(importedVariants);
        toast.success(
          `${importedVariants.length.toLocaleString()} HTML-Templates gespeichert (werden beim Segmentieren verteilt)${suffix}${skipSuffix}`,
          { id: toastId }
        );
      }
    } catch (error) {
      console.error('Failed to read uploaded templates:', error);
      toast.error('Upload fehlgeschlagen', { id: toastId });
    } finally {
      event.target.value = '';
    }
  };

  const handleOpenerHtmlUpload = async (event) => {
    const ALLOWED_EXT = /\.(html?|txt)$/i;
    const allFiles = Array.from(event.target.files || []);
    const files = allFiles.filter(f => ALLOWED_EXT.test(f.name || ''));
    if (files.length === 0) { event.target.value = ''; return; }
    const toastId = toast.loading(`Lese ${files.length} Opener-Templates...`);
    const variantSet = new Set();
    try {
      for (const file of files) {
        const content = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result || '')); r.onerror = () => rej(r.error); r.readAsText(file); });
        const parts = splitMessageVariantsFromContent(content).map(stripHtmlWrapper);
        for (const p of parts) { const t = String(p || '').trim(); if (t) variantSet.add(t); }
      }
      const templates = Array.from(variantSet);
      if (templates.length === 0) { toast.error('Keine gültigen Opener-Templates gefunden', { id: toastId }); return; }
      setOpenerHtmlTemplates(templates);
      setCampaignData(prev => ({ ...prev, thread_opener_html_templates: templates }));
      toast.success(`${templates.length} Opener-HTML-Templates importiert`, { id: toastId });
    } catch (err) {
      toast.error('Opener-Upload fehlgeschlagen', { id: toastId });
    } finally { event.target.value = ''; }
  };

  const htmlGeneratorActive = Boolean(htmlGenScript);
  const htmlUploadActive = htmlGeneratorActive
    || segmentedHtmlTemplates.length > 0
    || providerSegments.some(segment => Array.isArray(segment.html_templates) && segment.html_templates.length > 0);
  const normalizeLinkCount = (value) => {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
  };
  const buildSegmentRedirectConfigsFromHtmlLinks = () => {
    if (!htmlLinkConfig || !htmlLinkConfig.link_config) return null;
    const linkCounts = htmlLinkConfig.link_counts || {};
    const linkConfig = htmlLinkConfig.link_config || {};
    const types = ['logo', 'cta', 'unsubscribe'];
    const cfgFor = (type) => linkConfig[type] || null;
    const providerFor = (type) => cfgFor(type)?.provider || null;

    const awsTypes = types.filter((type) => providerFor(type) === 'aws');
    const awsHybridTypes = types.filter(
      (type) => providerFor(type) === 'hybrid-aws-share.google'
    );
    const searchTypes = types.filter((type) => {
      const provider = providerFor(type);
      return provider === 'searchapp'
        || provider === 'searchapp-s3'
        || provider === 'share.google+searchapp'
        || provider === 'hybrid-share.google'
        || provider === 'hybrid-aws-share.google';
    });
    const shareGoogleTypes = types.filter((type) => {
      const provider = providerFor(type);
      return provider === 'share.google'
        || provider === 'share.google+searchapp'
        || provider === 'hybrid-share.google';
    });
    const storyWrapTypes = types.filter((type) => {
      const provider = providerFor(type);
      return provider === 'story-wrap' || provider === 'search-app-wrap';
    });

    const pickRegions = (list) => sanitizeMultiCampaignAwsRegions(
      pickAwsRegionsFromLinkTypes(linkConfig, list)
    );
    const pickRegion = (list) => (
      pickRegions(list)[0] || MULTI_CAMPAIGN_DEFAULT_AWS_REGIONS[0]
    );
    const pickLinkStyle = (list) => {
      for (const type of list) {
        const style = cfgFor(type)?.link_style;
        if (style) return style;
      }
      return 'SHORT';
    };
    // pickTargetMode, collectCustomTargets, collectSlotDomains replaced by per-type logic below
    const buildCounts = (list) => ({
      logo: list.includes('logo') ? normalizeLinkCount(linkCounts.logo) : 0,
      cta: list.includes('cta') ? normalizeLinkCount(linkCounts.cta) : 0,
      unsubscribe: list.includes('unsubscribe') ? normalizeLinkCount(linkCounts.unsubscribe) : 0
    });

    // Pro Typ: eigene Domains/Targets sammeln (nicht mischen!)
    const perTypeDomains = {};
    const perTypeCustomTargets = {};
    types.forEach((type) => {
      const cfg = cfgFor(type);
      if (!cfg) return;
      if (cfg.target === 'custom' && Array.isArray(cfg.custom_targets)) {
        perTypeCustomTargets[type] = cfg.custom_targets.filter(Boolean);
      } else if (cfg.slot?.domains && Array.isArray(cfg.slot.domains)) {
        perTypeDomains[type] = cfg.slot.domains.filter(Boolean);
      }
    });

    // Alle Domains/Targets pro Provider-Gruppe zusammenführen (aber pro Typ getrennt für Request)
    const mergeDomainsForGroup = (group) => {
      const allDomains = [];
      const allCustom = [];
      group.forEach((type) => {
        if (perTypeCustomTargets[type]?.length > 0) {
          allCustom.push(...perTypeCustomTargets[type]);
        }
        if (perTypeDomains[type]?.length > 0) {
          allDomains.push(...perTypeDomains[type]);
        }
      });
      const uniqueDomains = Array.from(new Set(allDomains));
      const uniqueCustom = Array.from(new Set(allCustom));
      // Wenn NUR custom targets → use_custom_targets=true
      // Wenn NUR slot domains → use_custom_targets=false
      // Wenn BEIDE → alles als slot domains behandeln (API akzeptiert beides)
      const hasOnlyCustom = uniqueCustom.length > 0 && uniqueDomains.length === 0;
      return {
        domains: hasOnlyCustom ? [] : [...uniqueDomains, ...uniqueCustom],
        customTargets: hasOnlyCustom ? uniqueCustom : [],
        hasCustom: hasOnlyCustom
      };
    };

    const configs = {};
    if (awsTypes.length > 0) {
      const merged = mergeDomainsForGroup(awsTypes);
      const allTargetPool = merged.hasCustom ? merged.customTargets : merged.domains;
      configs.aws_redirects = {
        enabled: true,
        links_per_mode: 'per_segment',
        regions: pickRegions(awsTypes),
        region: pickRegion(awsTypes),
        counts: buildCounts(awsTypes),
        use_custom_targets: merged.hasCustom,
        custom_targets: merged.customTargets,
        domains: merged.domains,
        slot: { domains: allTargetPool.length > 0 ? allTargetPool : merged.domains },
        links: null
      };
    }
    if (awsHybridTypes.length > 0) {
      configs.aws_hybrid_redirects = {
        enabled: true,
        links_per_mode: 'per_segment',
        regions: pickRegions(awsHybridTypes),
        region: pickRegion(awsHybridTypes),
        counts: buildCounts(awsHybridTypes),
        source: 'search.app',
        chain: 'aws>search.app>target',
        links: null
      };
    }
    if (searchTypes.length > 0) {
      const merged = mergeDomainsForGroup(searchTypes);
      const allTargetPool = merged.hasCustom ? merged.customTargets : merged.domains;
      const hasHybrid = searchTypes.some((type) => providerFor(type) === 'searchapp-s3');
      configs.searchapp_redirects = {
        enabled: true,
        links_per_mode: 'per_segment',
        use_hybrid_mode: hasHybrid,
        hybrid_region: pickRegion(searchTypes),
        link_style: pickLinkStyle(searchTypes),
        counts: buildCounts(searchTypes),
        enabled_link_types: {
          logo: searchTypes.includes('logo'),
          cta: searchTypes.includes('cta'),
          unsubscribe: searchTypes.includes('unsubscribe')
        },
        use_custom_targets: merged.hasCustom,
        custom_targets: merged.customTargets,
        domains: merged.domains,
        slot: { domains: allTargetPool.length > 0 ? allTargetPool : merged.domains },
        links: null
      };
    }
    if (shareGoogleTypes.length > 0) {
      const merged = mergeDomainsForGroup(shareGoogleTypes);
      const allTargetPool = merged.hasCustom ? merged.customTargets : merged.domains;
      const isHybrid = shareGoogleTypes.some((type) => providerFor(type) === 'hybrid-share.google');
      configs.share_google_redirects = {
        enabled: true,
        links_per_mode: 'per_segment',
        use_hybrid_searchapp: isHybrid,
        counts: buildCounts(shareGoogleTypes),
        use_custom_targets: merged.hasCustom,
        custom_targets: merged.customTargets,
        domains: merged.domains,
        target_url: allTargetPool[0] || merged.domains[0] || '',
        slot: { domains: allTargetPool.length > 0 ? allTargetPool : merged.domains },
        links: null
      };
    }
    if (providerFor('logo') === 'brandlogo') {
      const logoCfg = cfgFor('logo');
      configs.brandlogo_redirects = {
        enabled: true,
        logo_brand: logoCfg?.logo_brand || 'pobalogo',
        brand_provider: logoCfg?.brand_provider || 'share.google',
        count: normalizeLinkCount(linkCounts.logo) || 1,
        links: null
      };
    }
    if (storyWrapTypes.length > 0) {
      const merged = mergeDomainsForGroup(storyWrapTypes);
      const allTargetPool = merged.hasCustom ? merged.customTargets : merged.domains;
      configs.story_wrap_redirects = {
        enabled: true,
        links_per_mode: 'per_segment',
        regions: pickRegions(storyWrapTypes),
        region: pickRegion(storyWrapTypes),
        counts: buildCounts(storyWrapTypes),
        use_custom_targets: merged.hasCustom,
        custom_targets: merged.customTargets,
        domains: merged.domains,
        slot: { domains: allTargetPool.length > 0 ? allTargetPool : merged.domains },
        links: null
      };
    }
    return Object.keys(configs).length > 0 ? configs : null;
  };
  const validateSegmentRecipientDistribution = (segments, allRecipients) => {
    const baseRecipients = Array.isArray(allRecipients) ? allRecipients : [];
    const countEmails = (list) => {
      const map = new Map();
      list.forEach((email) => {
        if (!email) return;
        map.set(email, (map.get(email) || 0) + 1);
      });
      return map;
    };
    const baseCounts = countEmails(baseRecipients);
    const segmentCounts = countEmails(
      segments.flatMap((segment) => Array.isArray(segment.recipients) ? segment.recipients : [])
    );

    const missing = [];
    const extra = [];

    for (const [email, baseCount] of baseCounts.entries()) {
      const segCount = segmentCounts.get(email) || 0;
      if (segCount < baseCount) {
        missing.push(`${email} (${baseCount - segCount}x)`);
      } else if (segCount > baseCount) {
        extra.push(`${email} (${segCount - baseCount}x)`);
      }
    }

    for (const [email, segCount] of segmentCounts.entries()) {
      if (!baseCounts.has(email)) {
        extra.push(`${email} (${segCount}x)`);
      }
    }

    return {
      ok: missing.length === 0 && extra.length === 0,
      missing,
      extra
    };
  };
  const renderHtmlLinkPanel = () => {
    if (!htmlUploadActive) return null;
    const linkCounts = htmlLinkConfig?.link_counts || {};
    const linkConfig = htmlLinkConfig?.link_config || {};
    const providerSummary = ['logo', 'cta', 'unsubscribe']
      .map((key) => `${key.toUpperCase()}: ${linkConfig[key]?.provider || '-'}`)
      .join(' • ');
    const countSummary = ['logo', 'cta', 'unsubscribe']
      .map((key) => `${key.toUpperCase()}: ${Number(linkCounts[key]) || 0}`)
      .join(' • ');

    return (
      <div className="mt-4">
        <AdminLinkPanel
          isAdmin={isAdmin}
          enableQrCta
          enableMixedStoryMode={isAdmin && useFastMode}
          excludedAwsRegions={MULTI_CAMPAIGN_EXCLUDED_AWS_REGIONS}
          onLinksConfigured={(config) => {
            setHtmlLinkConfig(config);
            toast.success('Link-Konfiguration gespeichert');
          }}
        />
        {htmlLinkConfig && (
          <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">
            ✅ Link-Konfiguration aktiv ({providerSummary}) • Counts: {countSummary}
          </div>
        )}
      </div>
    );
  };

  // 🌐 PROVIDER-ERKENNUNG aus Recipients (intelligent gruppiert)
  const detectProvidersFromRecipients = (recipients) => {
    // Definiere bekannte große Provider mit ihren Varianten
    const majorProviders = {
      'Gmail': ['gmail.com', 'googlemail.com'],
      'Outlook': ['outlook.com', 'outlook.de', 'hotmail.com', 'hotmail.de', 'live.com', 'live.de', 'msn.com'],
      'GMX': ['gmx.de', 'gmx.net', 'gmx.at', 'gmx.ch', 'gmx.fr', 'gmx.es', 'gmx.it', 'gmx.com', 'gmx.us'],
      'Mail.com': ['mail.com'],
      'Web.de': ['web.de'],
      'T-Online': ['t-online.de', 'magenta.de'],
      'Yahoo': ['yahoo.com', 'yahoo.de', 'yahoo.co.uk', 'ymail.com'],
      'AOL': ['aol.com', 'aol.de'],
      '1&1': ['1und1.de', '1and1.com'],
      'Freenet': ['freenet.de'],
      'Arcor': ['arcor.de', 'vodafonemail.de'],
      'iCloud': ['icloud.com', 'me.com', 'mac.com']
    };
    
    const providerMap = {};
    const minCountForOwnCategory = 10; // Mindestanzahl für eigene Kategorie
    
    recipients.forEach(email => {
      const domain = email.split('@')[1]?.toLowerCase();
      if (!domain) return;
      
      // Finde passenden Major Provider
      let providerName = null;
      for (const [name, domains] of Object.entries(majorProviders)) {
        if (domains.includes(domain)) {
          providerName = name;
          break;
        }
      }
      
      // Wenn kein Major Provider gefunden, markiere als potentiell "Rest"
      if (!providerName) {
        providerName = `__unknown__${domain}`;
      }
      
      if (!providerMap[providerName]) {
        providerMap[providerName] = {
          provider: providerName,
          count: 0,
          emails: [],
          domains: new Set()
        };
      }
      
      providerMap[providerName].count++;
      providerMap[providerName].emails.push(email);
      providerMap[providerName].domains.add(domain);
    });
    
    // Gruppiere kleine Provider als "Rest"
    const providers = [];
    let restProvider = {
      provider: 'Rest',
      count: 0,
      emails: [],
      domains: new Set()
    };
    
    Object.values(providerMap).forEach(prov => {
      if (prov.provider.startsWith('__unknown__')) {
        // Kleine unbekannte Provider
        if (prov.count >= minCountForOwnCategory) {
          // Genug E-Mails für eigene Kategorie
          const cleanDomain = prov.provider.replace('__unknown__', '');
          providers.push({
            provider: cleanDomain,
            count: prov.count,
            emails: prov.emails,
            domains: prov.domains
          });
        } else {
          // Zu wenig - kommt in "Rest"
          restProvider.count += prov.count;
          restProvider.emails.push(...prov.emails);
          prov.domains.forEach(d => restProvider.domains.add(d));
        }
      } else {
        // Bekannter Major Provider
        providers.push(prov);
      }
    });
    
    // Füge "Rest" hinzu, wenn es E-Mails gibt
    if (restProvider.count > 0) {
      providers.push(restProvider);
    }
    
    // Sortiere nach Anzahl (größte zuerst)
    return providers.sort((a, b) => b.count - a.count);
  };
  
  // 🌐 PROVIDER AUTO-DETECTION beim Laden der Recipients
  useEffect(() => {
    if (campaignData.central_recipients && campaignData.central_recipients.length > 0) {
      const detected = detectProvidersFromRecipients(campaignData.central_recipients);
      setAutoAiConfig(prev => ({
        ...prev,
        detected_providers: detected
      }));
    }
  }, [campaignData.central_recipients]);

  // 🎯 AUTOMATISCHE SLOTS GENERIEREN
  const generateAutoSlots = () => {
    const totalRecipients = campaignData.central_recipients.length;
    const slotSize = autoSlotSize;
    const slotCount = Math.ceil(totalRecipients / slotSize);
    
    if (totalRecipients === 0) {
      toast.error('Keine Empfänger vorhanden für automatische Slot-Erstellung');
      return;
    }
    
    const newSlots = [];
    const availableSmtpServers = selectableSmtpServers;
    
    for (let i = 0; i < slotCount; i++) {
      const startIndex = i * slotSize;
      const endIndex = Math.min(startIndex + slotSize, totalRecipients);
      const actualSize = endIndex - startIndex;
      
      // 🎯 SMTP-ZUORDNUNG basierend auf gewähltem Modus
      let slotSmtpIds = [];
      
      switch (autoSlotSmtpMode) {
        case 'sequential':
          // Slot 1 → SMTP 1, Slot 2 → SMTP 2, etc.
          if (availableSmtpServers.length > 0) {
            const smtpIndex = i % availableSmtpServers.length;
            slotSmtpIds = [availableSmtpServers[smtpIndex].id];
          }
          break;
          
        case 'mixed':
          // Zufällige SMTP-Zuordnung pro Slot
          if (availableSmtpServers.length > 0) {
            const randomIndex = Math.floor(Math.random() * availableSmtpServers.length);
            slotSmtpIds = [availableSmtpServers[randomIndex].id];
          }
          break;
          
        default: // 'rotation'
          // Alle SMTPs pro Slot (Standard-Rotation)
          slotSmtpIds = availableSmtpServers.map(smtp => smtp.id);
          break;
      }
      
      const newSlot = {
        id: Date.now() + i,
        name: `Auto-Slot ${i + 1}`,
        description: `Automatisch erstellt: ${actualSize} Empfänger (${startIndex + 1}-${endIndex}) - SMTP: ${autoSlotSmtpMode}`,
        recipient_source: 'central_portion',  // 🔧 FIX: Verwende central_portion für korrekte Berechnung
        max_recipients: actualSize,
        recipient_percentage: Math.round((actualSize / totalRecipients) * 100),
        
        // 🔧 FIX: Korrekte Empfänger-Zuordnung
        recipients: campaignData.central_recipients.slice(startIndex, endIndex),
        
        // 🔧 FIX: Template-Daten korrekt übernehmen
        subject: autoSlotTemplate.subject,
        from_name: autoSlotTemplate.from_name,
        emails_per_hour: autoSlotTemplate.emails_per_hour,
        reply_to_email: '',
        
        // 🔧 FIX: HTML-Templates korrekt setzen
        html_templates: [autoSlotTemplate.message],
        template_rotation_mode: 'equal_distribution',
        
        // 🔍 DEBUG: Prüfe ob HTML übertragen wird
        debug_html_length: autoSlotTemplate.message.length,
        
        // SMTP-Zuordnung
        smtp_server_ids: slotSmtpIds,
        
        // Standard-Werte
        attachments: [],
        domain_filters: []
      };
      
      newSlots.push(newSlot);
    }
    
    setCampaignData(prev => ({
      ...prev,
      slots: newSlots
    }));
    
    toast.success(`✅ ${slotCount} Auto-Slots erstellt (${slotSize} Empfänger pro Slot)`);
    setAutoSlotMode(false);
  };

  // 📋 TEMPLATE KOPIEREN - Kopiert Template von einem Slot zu anderen
  const copySlotTemplate = (sourceSlotId) => {
    const sourceSlot = campaignData.slots.find(slot => slot.id === sourceSlotId);
    if (!sourceSlot) return;
    
    const templateData = {
      subject: sourceSlot.subject,
      message: sourceSlot.message || sourceSlot.html_templates?.[0] || '',
      from_name: sourceSlot.from_name,
      emails_per_hour: sourceSlot.emails_per_hour,
      reply_to_email: sourceSlot.reply_to_email
    };
    
    setAutoSlotTemplate(templateData);
    toast.success(`📋 Template von "${sourceSlot.name}" kopiert`);
  };

  // 🤖 AUTO-AI-SLOT-GENERATOR - Erstellt Slots mit AI-Templates
  const generateAutoAiSlots = async () => {
    console.log('🤖 generateAutoAiSlots aufgerufen!');
    const totalRecipients = campaignData.central_recipients.length;
    
    if (totalRecipients === 0) {
      toast.error('Keine Empfänger für Auto-AI-Slots');
      return;
    }
    
    if (!autoAiConfig.template.subject || !autoAiConfig.template.message || !autoAiConfig.template.from_name) {
      toast.error('Bitte alle Template-Felder ausfüllen!');
      return;
    }
    
    console.log('🤖 Generiere Slots...', autoAiConfig);
    
    // Zeige Progress-Bar
    setAutoAiGenerating(true);
    
    let slotCount, recipientsPerSlot;
    
    // Extrahiere Domain → Server Mapping
    const domainToServers = {};
    selectableSmtpServers.forEach(smtp => {
      const fullDomain = smtp.from_email.split('@')[1];
      const parts = fullDomain.split('.');
      const rootDomain = parts.slice(-2).join('.');
      
      if (!domainToServers[rootDomain]) {
        domainToServers[rootDomain] = [];
      }
      domainToServers[rootDomain].push(smtp);
    });
    
    const uniqueDomains = Object.keys(domainToServers);
    
    if (autoAiConfig.mode === 'per_domain') {
      slotCount = uniqueDomains.length;
      recipientsPerSlot = Math.ceil(totalRecipients / slotCount);
      toast.success(`🌐 ${slotCount} Domains gefunden - erstelle ${slotCount} AI-Slots`);
    } else {
      slotCount = autoAiConfig.slot_count;
      recipientsPerSlot = Math.ceil(totalRecipients / slotCount);  // 🔧 FIX: Berechne basierend auf TATSÄCHLICHEN Empfängern!
      console.log(`📊 ${slotCount} Slots: Je ${recipientsPerSlot} Empfänger`);
    }
    
    const newSlots = [];
    const templatesPerSlot = autoAiConfig.templates_per_slot;
    
    // 🌐 PROVIDER-SEGMENTIERUNG: Vorbereitung mit Email-Tracking
    const providerSlotDistribution = {};
    const providerUsedEmailCounts = {}; // Track wie viele E-Mails jedes Providers bereits verteilt wurden
    const providerSlotCounters = {}; // Track wie viele Slots jeder Provider bereits bekommen hat (für Round-Robin!)
    
    if (autoAiConfig.provider_segmentation_enabled && autoAiConfig.provider_segmentation.length > 0) {
      // 🎯 WICHTIG: "Rest"-E-Mails auf große Provider verteilen!
      const restProvider = autoAiConfig.detected_providers.find(p => p.provider === 'Rest');
      const restEmails = restProvider ? restProvider.emails : [];
      
      console.log(`📧 Rest-E-Mails gefunden: ${restEmails.length}`);
      
      // Finde alle Provider die in der Segmentierung verwendet werden (ohne "Rest")
      const usedProviderNames = [...new Set(autoAiConfig.provider_segmentation.map(seg => seg.provider))].filter(name => name !== 'Rest');
      
      // WICHTIG: Erstelle KOPIEN der Provider-Objekte (nicht die Originale modifizieren!)
      const usedProviders = usedProviderNames
        .map(name => {
          const original = autoAiConfig.detected_providers.find(p => p.provider === name);
          if (!original) return null;
          // Erstelle eine TIEFE KOPIE
          return {
            provider: original.provider,
            count: original.count,
            emails: [...original.emails], // KOPIE des Arrays!
            domains: new Set(original.domains)
          };
        })
        .filter(p => p !== null);
      
      console.log(`🌐 Verwendete Provider (ohne Rest): ${usedProviders.map(p => p.provider).join(', ')}`);
      
      // 🔧 FIX: Verteile Rest-E-Mails GLEICHMÄSSIG über alle Provider
      // Statt sie am Anfang zu konkatenieren, werden sie INTERLEAVED eingemischt!
      if (restEmails.length > 0 && usedProviders.length > 0) {
        const totalUsedEmails = usedProviders.reduce((sum, p) => sum + p.count, 0);
        let restEmailIndex = 0;
        
        usedProviders.forEach((provider, idx) => {
          const proportion = provider.count / totalUsedEmails;
          const restEmailsForThisProvider = Math.floor(restEmails.length * proportion);
          
          if (restEmailsForThisProvider > 0) {
            // Nehme die Rest-E-Mails für diesen Provider
            const providerRestEmails = restEmails.slice(restEmailIndex, restEmailIndex + restEmailsForThisProvider);
            restEmailIndex += restEmailsForThisProvider;
            
            // INTERLEAVE: Mische Rest-E-Mails gleichmäßig zwischen Original-E-Mails ein
            const originalEmails = provider.emails;
            const interleaved = [];
            
            let originalIndex = 0;
            let restIndex = 0;
            
            // Einfachere Methode: Chunk-basiert interleaven
            const chunkSize = Math.ceil(originalEmails.length / (providerRestEmails.length + 1));
            for (let i = 0; i < providerRestEmails.length + 1; i++) {
              // Füge einen Chunk Original-E-Mails hinzu
              const chunkEnd = Math.min(originalIndex + chunkSize, originalEmails.length);
              interleaved.push(...originalEmails.slice(originalIndex, chunkEnd));
              originalIndex = chunkEnd;
              
              // Füge eine Rest-E-Mail hinzu (außer beim letzten Chunk)
              if (restIndex < providerRestEmails.length) {
                interleaved.push(providerRestEmails[restIndex]);
                restIndex++;
              }
            }
            
            provider.emails = interleaved;
            provider.count = provider.emails.length;
            
            console.log(`  ➕ ${provider.provider}: +${restEmailsForThisProvider} Rest-E-Mails INTERLEAVED → ${provider.count} gesamt`);
          }
        });
        
        // Verteile übrige Rest-E-Mails auf ersten Provider
        if (restEmailIndex < restEmails.length) {
          const remaining = restEmails.slice(restEmailIndex);
          // Auch diese gleichmäßig verteilen
          const chunkSize = Math.ceil(usedProviders[0].emails.length / (remaining.length + 1));
          const interleaved = [];
          let originalIndex = 0;
          
          for (let i = 0; i < remaining.length + 1; i++) {
            const chunkEnd = Math.min(originalIndex + chunkSize, usedProviders[0].emails.length);
            interleaved.push(...usedProviders[0].emails.slice(originalIndex, chunkEnd));
            originalIndex = chunkEnd;
            
            if (i < remaining.length) {
              interleaved.push(remaining[i]);
            }
          }
          
          usedProviders[0].emails = interleaved;
          usedProviders[0].count = usedProviders[0].emails.length;
          console.log(`  ➕ ${usedProviders[0].provider}: +${remaining.length} übrige Rest-E-Mails INTERLEAVED → ${usedProviders[0].count} gesamt`);
        }
      }
      
      // Zähle zuerst wie viele Slots jeder Provider INSGESAMT bekommt
      const providerTotalSlotCounts = {};
      autoAiConfig.provider_segmentation.forEach(seg => {
        const provider = seg.provider;
        if (provider === 'Rest') return; // Überspringe "Rest" in Segmentierung
        
        if (!providerTotalSlotCounts[provider]) {
          providerTotalSlotCounts[provider] = 0;
        }
        providerTotalSlotCounts[provider] += (seg.end_slot - seg.start_slot + 1);
      });
      
      // Jetzt berechne emailsPerSlot basierend auf TOTAL Slots für diesen Provider
      autoAiConfig.provider_segmentation.forEach(seg => {
        const provider = seg.provider;
        if (provider === 'Rest') return; // Überspringe "Rest" in Segmentierung
        
        // Finde Provider-Daten (mit hinzugefügten Rest-E-Mails) aus den KOPIEN!
        const detectedProvider = usedProviders.find(p => p.provider === provider);
        if (detectedProvider && detectedProvider.emails && detectedProvider.emails.length > 0) {
          const totalEmails = detectedProvider.emails.length;
          const totalSlotsForProvider = providerTotalSlotCounts[provider];
          const emailsPerSlot = Math.ceil(totalEmails / totalSlotsForProvider);
          
          // Speichere einmalig die Distribution für jeden Provider
          if (!providerSlotDistribution[provider]) {
            providerSlotDistribution[provider] = {
              emails: detectedProvider.emails,
              emailsPerSlot: emailsPerSlot,
              totalEmails: totalEmails,
              totalSlots: totalSlotsForProvider
            };
            
            providerUsedEmailCounts[provider] = 0; // Initialisierung
            providerSlotCounters[provider] = 0; // Slot-Counter für Round-Robin
            
            console.log(`🌐 Provider ${provider}: ${totalEmails} E-Mails (inkl. Rest) → ${totalSlotsForProvider} Slots (≈${emailsPerSlot} E-Mails/Slot)`);
          }
        } else {
          console.error(`❌ Provider ${provider} hat keine E-Mails!`);
        }
      });
    }
    
    for (let i = 0; i < slotCount; i++) {
      const slotNumber = i + 1;  // Slots beginnen bei 1
      let slotRecipients = [];
      
      // 🌐 PROVIDER-SEGMENTIERUNG anwenden (falls aktiviert)
      if (autoAiConfig.provider_segmentation_enabled && autoAiConfig.provider_segmentation.length > 0) {
        // Finde Provider-Segmentierung für diesen Slot
        const applicableSegmentation = autoAiConfig.provider_segmentation.find(
          seg => slotNumber >= seg.start_slot && slotNumber <= seg.end_slot
        );
        
        if (applicableSegmentation && applicableSegmentation.provider) {
          const selectedProvider = applicableSegmentation.provider;
          const distribution = providerSlotDistribution[selectedProvider];
          
          if (distribution) {
            // Verwende den SLOT-COUNTER (wie viele Slots dieser Provider bereits bekommen hat)
            const currentProviderSlotIndex = providerSlotCounters[selectedProvider];
            const providerStartIndex = currentProviderSlotIndex * distribution.emailsPerSlot;
            const providerEndIndex = Math.min(providerStartIndex + distribution.emailsPerSlot, distribution.totalEmails);
            
            // Sicherheitscheck: Sind die Indizes gültig?
            if (providerStartIndex >= 0 && providerStartIndex < distribution.totalEmails) {
              slotRecipients = distribution.emails.slice(providerStartIndex, providerEndIndex);
              providerUsedEmailCounts[selectedProvider] += slotRecipients.length;
              providerSlotCounters[selectedProvider]++; // Erhöhe Slot-Counter für diesen Provider
              
              console.log(`🌐 Slot ${slotNumber}: ${selectedProvider} - ${slotRecipients.length} E-Mails (Provider-Slot #${currentProviderSlotIndex + 1}/${distribution.totalSlots}, Index ${providerStartIndex}-${providerEndIndex}, gesamt ${providerUsedEmailCounts[selectedProvider]}/${distribution.totalEmails})`);
            } else {
              console.warn(`⚠️ Slot ${slotNumber}: ${selectedProvider} - Provider-Slot #${currentProviderSlotIndex + 1} Index ${providerStartIndex} außerhalb des Bereichs (max: ${distribution.totalEmails})`);
            }
          } else {
            console.error(`❌ Slot ${slotNumber}: Provider ${selectedProvider} nicht in Distribution gefunden!`);
          }
        } else {
          console.warn(`⚠️ Slot ${slotNumber}: Keine Provider-Segmentierung zugeordnet - Slot wird übersprungen!`);
        }
      } else {
        // KEIN Provider-Segmentierung: Normale Aufteilung
        const startIndex = i * recipientsPerSlot;
        const endIndex = Math.min(startIndex + recipientsPerSlot, totalRecipients);
        slotRecipients = campaignData.central_recipients.slice(startIndex, endIndex);
      }
      
      const actualSize = slotRecipients.length;
      
      if (actualSize <= 0) {
        console.warn(`⚠️ Slot ${slotNumber}: KEINE Recipients - Slot wird übersprungen!`);
        continue; // Slot wird nicht erstellt!
      }
      
      // 📊 SMTP-ZUORDNUNG basierend auf Modus
      let slotSmtpIds = [];
      let slotDomain = '';
      
      if (autoAiConfig.mode === 'per_domain') {
        // Pro Domain: Verwende nur Server dieser Domain
        slotDomain = uniqueDomains[i];
        slotSmtpIds = (domainToServers[slotDomain] || []).map(s => s.id);
      } else {
        // Manual: SMTP-Verteilung basierend auf smtp_mode
        if (autoAiConfig.smtp_mode === 'segmented' && autoAiConfig.smtp_segmentation.length > 0) {
          // 📊 SMTP-SEGMENTIERUNG: Finde zuständigen Server für diesen Slot
          const applicableSegmentation = autoAiConfig.smtp_segmentation.find(
            seg => slotNumber >= seg.start_slot && slotNumber <= seg.end_slot
          );
          
          if (applicableSegmentation && applicableSegmentation.server_id) {
            slotSmtpIds = [applicableSegmentation.server_id];
            console.log(`📊 Slot ${slotNumber}: SMTP-Server ${applicableSegmentation.server_id} (Segmentierung)`);
          } else {
            // Fallback: Alle Server
            slotSmtpIds = selectableSmtpServers.map(s => s.id);
            console.warn(`⚠️ Slot ${slotNumber}: Keine SMTP-Segmentierung gefunden - verwende alle Server`);
          }
        } else if (autoAiConfig.smtp_mode === 'rotation') {
          slotSmtpIds = selectableSmtpServers.map(s => s.id);  // Alle Server
        } else if (autoAiConfig.smtp_mode === 'sequential') {
          slotSmtpIds = [selectableSmtpServers[i % selectableSmtpServers.length]?.id].filter(Boolean);  // 1 Server pro Slot
        } else {
          // Mixed: Zufällige Auswahl
          const serverCount = Math.floor(Math.random() * (selectableSmtpServers.length / 2)) + 1;
          slotSmtpIds = selectableSmtpServers
            .sort(() => Math.random() - 0.5)
            .slice(0, serverCount)
            .map(s => s.id);
        }
      }
      
      const newSlot = {
        id: Date.now() + i + Math.random(),
        name: autoAiConfig.mode === 'per_domain' ? `AI-Slot ${slotDomain}` : `AI-Slot ${slotNumber}`,
        description: `AI: ${actualSize} Empfänger, ${templatesPerSlot} Templates${autoAiConfig.mode === 'per_domain' ? ` (Domain: ${slotDomain})` : ''}`,
        recipient_source: 'own_list',  // 🔧 Verwende own_list für gefilterte Recipients
        max_recipients: actualSize,
        recipient_percentage: Math.round((actualSize / totalRecipients) * 100),
        recipients: slotRecipients,  // 🌐 Gefilterte Recipients
        
        slot_type: 'send',  // ⏸️ Normal Send-Slot
        
        subject: autoAiConfig.template.subject,
        from_name: autoAiConfig.template.from_name,
        emails_per_hour: autoAiConfig.template.emails_per_hour,
        reply_to_email: '',
        dynamic_from_email: autoAiConfig.template.dynamic_from_email || '',  // 📧 Dynamische Absender-E-Mail
        use_dynamic_from_email: autoAiConfig.template.use_dynamic_from_email || false,  // 📧 Checkbox
        
        // 🤖 AI-PRE-GENERATED Templates (AI-Randomisierung zur Laufzeit DEAKTIVIERT!)
        html_templates: Array(templatesPerSlot).fill(autoAiConfig.template.message),
        template_rotation_mode: 'equal_distribution',
        ai_randomization_enabled: false,  // 🔧 FALSE: Templates werden VOR Versand generiert!
        ai_randomization_frequency: null,  // 🔧 NULL: Keine Laufzeit-Randomisierung
        
        // 📧 List-Unsubscribe von Global-Settings übernehmen
        list_unsubscribe_enabled: campaignData.list_unsubscribe_enabled,
        list_unsubscribe_url: campaignData.list_unsubscribe_url,
        list_unsubscribe_method: campaignData.list_unsubscribe_method,
        
        // ⚡ Runtime-Obfuscation Settings
        precedence_header: autoAiConfig.runtime_obfuscation.precedence_header,
        warmup_enabled: autoAiConfig.runtime_obfuscation.warmup_enabled,
        warmup_duration: autoAiConfig.runtime_obfuscation.warmup_duration,
        
        // 🔗 AWS-Links für Sequential Auto-AI-Slots (aus autoAiConfig)
        aws_redirects: autoAiConfig.aws_redirects?.enabled ? {
          enabled: true,
          domains: autoAiConfig.aws_redirects.domains || autoAiConfig.aws_redirects.slot?.domains || [],
          target_url: autoAiConfig.aws_redirects.target_url || autoAiConfig.aws_redirects.slot?.target_url || '',
          links_per_mode: autoAiConfig.aws_redirects.links_per_mode || 'per_slot'
        } : undefined,
        
        // 🔍 Search.app für Sequential (aus autoAiConfig)
        searchapp_redirects: autoAiConfig.searchapp_redirects?.enabled ? {
          enabled: true,
          domains: autoAiConfig.searchapp_redirects.domains || []
        } : undefined,
        
        smtp_server_ids: slotSmtpIds,
        attachments: [],
        domain_filters: []
      };
      
      newSlots.push(newSlot);
      
      // ⏸️ PAUSE-SLOT EINFÜGEN (nach jedem N Slot)
      if (autoAiConfig.add_pause_after_n_slots && (i + 1) % autoAiConfig.pause_after_n === 0 && i < slotCount - 1) {
        const pauseSlot = {
          id: Date.now() + i + Math.random() + 0.5,
          slot_type: 'pause',
          pause_duration: autoAiConfig.pause_duration_after_n,
          name: `⏸️ Pause ${autoAiConfig.pause_duration_after_n}s`,
          description: `Automatische Pause nach Slot ${i + 1}`,
          recipient_source: 'own_list',
          recipients: [],
          html_templates: [''],
          subject: '',
          from_name: '',
          smtp_server_ids: [],
          emails_per_hour: 0
        };
        newSlots.push(pauseSlot);
      }
    }
    
    // 📊 SUMMARY: Zeige Statistik der Slot-Erstellung
    console.log(`\n📊 ===== SLOT-ERSTELLUNG ABGESCHLOSSEN =====`);
    console.log(`📋 Geplante Slots: ${slotCount}`);
    console.log(`✅ Erstellte Slots: ${newSlots.length}`);
    
    if (autoAiConfig.provider_segmentation_enabled) {
      console.log(`\n🌐 PROVIDER-SEGMENTIERUNG:`);
      Object.keys(providerSlotDistribution).forEach(provider => {
        const dist = providerSlotDistribution[provider];
        const used = providerUsedEmailCounts[provider];
        const slotsUsed = providerSlotCounters[provider];
        const remaining = dist.totalEmails - used;
        console.log(`  ${provider}: ${used}/${dist.totalEmails} E-Mails verteilt (${slotsUsed}/${dist.totalSlots} Slots) ${remaining > 0 ? `⚠️ ${remaining} übrig` : '✅'}`);
      });
      
      const totalDistributed = Object.values(providerUsedEmailCounts).reduce((sum, count) => sum + count, 0);
      const totalAvailable = Object.values(providerSlotDistribution).reduce((sum, dist) => sum + dist.totalEmails, 0);
      console.log(`\n📧 GESAMT: ${totalDistributed}/${totalAvailable} E-Mails verteilt`);
      
      if (totalDistributed < totalAvailable) {
        console.warn(`⚠️ WARNUNG: ${totalAvailable - totalDistributed} E-Mails wurden NICHT verteilt!`);
        toast.warning(`⚠️ ${totalAvailable - totalDistributed} E-Mails konnten nicht verteilt werden (zu viele Slots für verfügbare E-Mails)`);
      }
    }
    console.log(`=============================================\n`);
    
    // ⏸️ Campaign-Level Pausen setzen (wenn aktiviert)
    if (autoAiConfig.add_pause_after_each_slot) {
      setCampaignData(prev => ({
        ...prev,
        pause_after_each_slot: autoAiConfig.pause_duration_after_each
      }));
      toast.success(`⏸️ Campaign-Level: ${autoAiConfig.pause_duration_after_each}s Pause nach jedem Slot`);
    }
    
    // 🤖 JETZT: Claude AI-Versionen generieren BEVOR Slots erstellt werden!
    // 🔧 FIX: Nur Send-Slots zählen für AI-Versionen (Pause-Slots haben keine Templates)
    const sendSlotCount = slotCount;  // Send-Slots (vor Pause-Slots)
    const totalAiVersions = sendSlotCount * templatesPerSlot;
    setAutoAiProgress({ current: 0, total: totalAiVersions });
    
    toast.success(`🤖 Generiere ${totalAiVersions} AI-Versionen mit Claude... (${sendSlotCount} Slots × ${templatesPerSlot} Templates)`);
    
    try {
      // 🤖 Starte Backend-Generierung
      const generateResponse = await axios.post('/api/multi-campaigns/generate-ai-slots', {
        slot_config: {
          mode: autoAiConfig.mode,
          slot_count: sendSlotCount,  // 🔧 Nur Send-Slots
          recipients_per_slot: autoAiConfig.recipients_per_slot,
          templates_per_slot: autoAiConfig.templates_per_slot,
          smtp_mode: autoAiConfig.smtp_mode,  // 📊 SMTP-Modus
          smtp_segmentation: autoAiConfig.smtp_segmentation,  // 📊 SMTP-Segmentierung
          provider_segmentation_enabled: autoAiConfig.provider_segmentation_enabled,  // 🌐 Provider-Segmentierung aktiviert
          provider_segmentation: autoAiConfig.provider_segmentation  // 🌐 Provider-Segmentierung
        },
        template: autoAiConfig.template,
        central_recipients: campaignData.central_recipients,
        smtp_servers: selectableSmtpServers,
        obfuscation: autoAiConfig.obfuscation  // 🎨 Obfuskations-Optionen
      });
      
      const generationId = generateResponse.data.data.generation_id;
      console.log(`🤖 Backend-Generierung gestartet: ${generationId}`);
      
      // Poll für Progress
      const pollInterval = setInterval(async () => {
        try {
          const progressResponse = await axios.get(`/api/multi-campaigns/ai-slots-progress/${generationId}`);
          const { status, progress, total, slots_data } = progressResponse.data.data;
          
          setAutoAiProgress({ current: progress || 0, total: total || 0 });
          
          if (status === 'completed') {
            clearInterval(pollInterval);
            
            console.log('✅ Generation completed, checking slots_data...', slots_data);
            
            // Verwende die vom Backend generierten Slots
            if (slots_data && slots_data.aiVersions && slots_data.aiVersions.length > 0) {
              const aiVersions = slots_data.aiVersions;
              
              console.log(`✅ Backend liefert ${aiVersions.length} AI-Versionen`);
              console.log(`📊 newSlots.length = ${newSlots.length}, slotCount = ${slotCount}, templatesPerSlot = ${templatesPerSlot}`);
              console.log(`📊 slots_data.slots = ${slots_data.slots}, slots_data.templatesPerSlot = ${slots_data.templatesPerSlot}`);
              
              // 🔧 Verteile auf Send-Slots (skip Pause-Slots)
              let aiVersionIndex = 0;
              for (let i = 0; i < newSlots.length; i++) {
                // ⏸️ Skip Pause-Slots
                if (newSlots[i].slot_type === 'pause') {
                  console.log(`⏸️ Skipping pause slot ${i}: ${newSlots[i].name}`);
                  continue;
                }
                
                const versionStart = aiVersionIndex * templatesPerSlot;
                const slotVersions = aiVersions.slice(versionStart, versionStart + templatesPerSlot);
                
                console.log(`📝 Slot ${i} (AI-Index ${aiVersionIndex}): ${slotVersions.length} Versionen von ${versionStart} bis ${versionStart + templatesPerSlot}`);
                
                if (slotVersions.length > 0) {
                  // 🔧 Direkt verwenden - JSON.parse decoded automatisch korrekt
                  newSlots[i].html_templates = slotVersions.map(v => v.html);
                  newSlots[i].subject = slotVersions[0]?.subject || newSlots[i].subject;
                  newSlots[i].from_name = slotVersions[0]?.fromName || newSlots[i].from_name;
                }
                
                aiVersionIndex++;
              }
              
              console.log(`✅ Slots erstellt: ${newSlots.filter(s => s.slot_type === 'send').length} Send-Slots, ${newSlots.filter(s => s.slot_type === 'pause').length} Pause-Slots`);
              
              setCampaignData(prev => ({
                ...prev,
                slots: newSlots
              }));
              
              setAutoAiGenerating(false);
              toast.success(`✅ ${newSlots.filter(s => s.slot_type === 'send').length} AI-Slots mit ${totalAiVersions} Versionen erstellt!`);
              setAutoAiMode(false);
            } else {
              // Fallback: Keine slots_data oder leer
              console.error('❌ slots_data ist leer oder ungültig:', slots_data);
              setAutoAiGenerating(false);
              toast.error('AI-Daten konnten nicht geladen werden');
            }
            
          } else if (status === 'failed') {
            clearInterval(pollInterval);
            setAutoAiGenerating(false);
            toast.error('AI-Generierung fehlgeschlagen');
          }
        } catch (error) {
          console.error('Progress poll error:', error);
        }
      }, 2000);
      
      // Timeout nach 2 Stunden
      setTimeout(() => {
        clearInterval(pollInterval);
        setAutoAiGenerating(false);
        toast.error('AI-Generierung Timeout (2 Stunden überschritten)');
      }, 7200000);  // 2 Stunden
      
    } catch (error) {
      setAutoAiGenerating(false);
      toast.error('AI-Generierung fehlgeschlagen');
      console.error('AI-Generation error:', error);
      return;
    }
  };


  // 🔀 SLOTS MISCHEN - Ändert die Reihenfolge zufällig
  const shuffleSlots = () => {
    const shuffledSlots = [...campaignData.slots];
    for (let i = shuffledSlots.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledSlots[i], shuffledSlots[j]] = [shuffledSlots[j], shuffledSlots[i]];
    }
    
    // Namen neu nummerieren
    shuffledSlots.forEach((slot, index) => {
      slot.name = slot.name.replace(/\d+$/, index + 1);
    });
    
    setCampaignData(prev => ({
      ...prev,
      slots: shuffledSlots
    }));
    
    toast.success(`🔀 ${shuffledSlots.length} Slots gemischt`);
  };

  // Slot hinzufügen
  const addSlot = () => {
    const newSlot = {
      id: Date.now(),
      name: `Slot ${campaignData.slots.length + 1}`,
      description: '',
      recipient_source: 'domain_segmentation',
      recipients: [],
      domain_filters: [],
      max_recipients: null,
      html_templates: [''],
      template_rotation_mode: 'equal_distribution',
      template_weights: {},
      subject: '',
      from_name: '',
      reply_to_email: '',
      attachments: [],
      smtp_server_ids: [],
      emails_per_hour: 3800
    };

    setCampaignData(prev => ({
      ...prev,
      slots: [...prev.slots, newSlot]
    }));
  };

  // Slot entfernen
  const removeSlot = (slotId) => {
    setCampaignData(prev => ({
      ...prev,
      slots: prev.slots.filter(slot => slot.id !== slotId)
    }));
  };

  // Slot aktualisieren
  const updateSlot = (slotId, updates) => {
    setCampaignData(prev => ({
      ...prev,
      slots: prev.slots.map(slot => 
        slot.id === slotId ? { ...slot, ...updates } : slot
      )
    }));
  };

  // Vorgeschlagene Slots übernehmen
  const applySuggestedSlots = () => {
    if (!slotSuggestions || !slotSuggestions.suggested_slots) return;

    const newSlots = slotSuggestions.suggested_slots.map((suggestion, index) => ({
      id: Date.now() + index,
      name: suggestion.name,
      description: suggestion.description,
      recipient_source: 'domain_segmentation',
      recipients: [],
      domain_filters: suggestion.domains,
      max_recipients: suggestion.recipient_count,
      html_templates: [''],
      template_rotation_mode: 'equal_distribution',
      template_weights: {},
      subject: '',
      from_name: '',
      reply_to_email: '',
      attachments: [],
      smtp_server_ids: [],
      emails_per_hour: 3800
    }));

    setCampaignData(prev => ({
      ...prev,
      slots: newSlots
    }));

    toast.success(`${newSlots.length} Slots aus Vorschlägen erstellt`);
  };

  const handleSuggestSlots = async () => {
    if (!campaignData.central_recipients || campaignData.central_recipients.length === 0) {
      toast.error('Bitte zuerst Empfänger eingeben');
      return;
    }
    try {
      const targetSlotCount = autoAiConfig?.slot_count || 10;
      const response = await axios.post('/api/domain-segmentation/suggest-slots', {
        recipients: campaignData.central_recipients,
        target_slot_count: targetSlotCount
      });
      setSlotSuggestions(response.data.data);
      toast.success(`${response.data.data?.suggested_slots?.length || 0} Slot-Vorschläge generiert`);
    } catch (error) {
      console.error('Slot suggestions error:', error);
      toast.error('Slot-Vorschläge fehlgeschlagen');
    }
  };

  // Campaign erstellen
  const handleCreateCampaign = () => {
    // ✅ Segmented-Parallel & SMTP-Segmented-Parallel: Campaign wurde bereits in handleXxxParallelNext() erstellt!
    if (campaignData.execution_mode === 'segmented_parallel' || campaignData.execution_mode === 'smtp_segmented_parallel' || campaignData.execution_mode === 'smtp_round_robin' || campaignData.execution_mode === 'burst_parallel') {
      // Schließe Modal und rufe onSuccess auf
      if (campaignData.slots.length > 0) {
        toast.success('✅ Campaign erfolgreich erstellt!');
        onSuccess && onSuccess({ name: campaignData.name });
        onClose();
        return;
      } else {
        toast.error('⚠️ Keine Slots gefunden! Bitte gehe zu Schritt 2 und erstelle Provider-Segmente.');
        return;
      }
    }
    
    // Validierung für Sequential/Parallel
    if (!campaignData.name.trim()) {
      toast.error('Campaign-Name ist erforderlich');
      return;
    }

    if (campaignData.slots.length === 0) {
      toast.error('Mindestens ein Slot ist erforderlich');
      return;
    }

    // Zentrale Empfänger-Validierung
    if (campaignData.central_recipients && campaignData.central_recipients.length > 0) {
      const totalCentralRecipients = campaignData.central_recipients.length;
      let totalAssignedRecipients = 0;
      
      // Summiere alle zugewiesenen Empfänger aus den Slots
      campaignData.slots.forEach(slot => {
        // Zähle central_portion UND own_list (AI-Slots mit Recipients aus central_recipients)
        if (slot.recipient_source === 'central_portion' && slot.max_recipients) {
          totalAssignedRecipients += slot.max_recipients;
        } else if (slot.recipient_source === 'own_list' && slot.recipients && slot.recipients.length > 0) {
          // AI-Slots: Zähle die tatsächlichen Recipients
          totalAssignedRecipients += slot.recipients.length;
        }
      });
      
      // Prüfe ob alle Empfänger verteilt sind
      if (totalAssignedRecipients < totalCentralRecipients) {
        const remaining = totalCentralRecipients - totalAssignedRecipients;
        toast.error(`⚠️ Nicht alle Empfänger verteilt! ${remaining} von ${totalCentralRecipients} Empfängern sind noch nicht zugewiesen. Bitte passen Sie die Slot-Anteile an.`);
        return;
      }
      
      if (totalAssignedRecipients > totalCentralRecipients) {
        const excess = totalAssignedRecipients - totalCentralRecipients;
        toast.error(`⚠️ Zu viele Empfänger zugewiesen! ${excess} Empfänger zu viel. Gesamt verfügbar: ${totalCentralRecipients}`);
        return;
      }
      
      console.log(`✅ Zentrale Empfänger-Verteilung: ${totalAssignedRecipients}/${totalCentralRecipients} (100%)`);
    }

    // Slots validieren (SKIP für segmented_parallel - die haben AI-Content!)
    if (campaignData.execution_mode !== 'segmented_parallel') {
    for (const slot of campaignData.slots) {
        if (!slot.subject?.trim()) {
        toast.error(`Slot "${slot.name}": Betreff ist erforderlich`);
        return;
      }
        if (!slot.html_templates || !slot.html_templates[0] || !slot.html_templates[0].trim()) {
        toast.error(`Slot "${slot.name}": HTML-Template ist erforderlich`);
        return;
        }
      }
    }

    const payload = {
      ...campaignData
    };

    // 🌍 Proxy-Settings: DEAKTIVIERT (Proxy pro SMTP aus DB, kein Campaign-Override)

    createCampaignMutation.mutate(payload);
  };

  const isNextStepLocked = autoAiGenerating || segmentAnalyzing;

  // Schritt-Navigation
  const nextStep = async () => {
    if (isNextStepLocked) {
      return;
    }
    // 🎯 Spezial-Logik für segmented_parallel beim Wechsel von Step 2 → 3
    if (currentStep === 2 && campaignData.execution_mode === 'segmented_parallel') {
      if (useFastMode || htmlGeneratorActive) {
        await handlePrepareFast();
      } else {
        await handleSegmentedParallelNext();
      }
      return;
    }
    
    // 🎯 Spezial-Logik für smtp_segmented_parallel / smtp_round_robin beim Wechsel von Step 2 → 3
    if (currentStep === 2 && (campaignData.execution_mode === 'smtp_segmented_parallel' || campaignData.execution_mode === 'smtp_round_robin' || campaignData.execution_mode === 'burst_parallel')) {
      if (useFastMode || htmlGeneratorActive) {
        await handlePrepareFast();
      } else {
        await handleSmtpSegmentedParallelNext();
      }
      return;
    }
    
    // Normaler Step-Wechsel
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  // 📧 SMTP-Segmented-Parallel: AI-Generation + Slot-Creation (gleiche Logik wie Provider!)
  const handleSmtpSegmentedParallelNext = async () => {
    // Nutze die gleiche Logik wie handleSegmentedParallelNext!
    await handleSegmentedParallelNext();
  };
  
  // ⚡ Smart Replicate: Segment 1 auf alle kopieren (AI-randomisiert!)
  const handleSmartReplicate = async (options) => {
    if (providerSegments.length < 2) {
      toast.error('Mindestens 2 Segmente erforderlich');
      return;
    }

    if (htmlUploadActive) {
      toast.error('HTML-Upload aktiv: Smart-Replicate (AI-Content) ist deaktiviert');
      return;
    }
    
    if (!providerSegments[0].ai_variants || providerSegments[0].ai_variants.length === 0) {
      toast.error('Segment 1 muss AI-Varianten haben');
      return;
    }
    
    setSmartReplicateModalOpen(false);
    
    const parsedThroughput = options.target_throughput;
    const dynamicEmailMode = options.dynamic_email_mode || 'varied';
    const dynamicEmailPercentage = options.dynamic_email_percentage || 100;
    const fingerprintRotation = options.fingerprint_rotation || 'per_slot';
    const ehloRotation = options.ehlo_rotation || 'per_slot';
    const encodingDistribution = options.encoding_distribution || { 'quoted-printable': 50, 'base64': 30, '7bit': 20 };
    const useMultipart = options.use_multipart !== false;
    const multipartPercentage = options.multipart_percentage || 70;
    // 🔧 FIX: client_distribution aus SmartReplicateModal übernehmen!
    const clientDistribution = options.client_distribution || {
      outlook_web: 25, gmail_web: 20, iphone_mail: 18, 
      android_gmail: 15, thunderbird: 10, apple_mail: 7, clean: 5
    };
    
    setSegmentAnalyzing(true);
    const totalVariants = (providerSegments.length - 1) * providerSegments[0].ai_variants.length;
    
    // ✅ Erstelle einen persistenten Toast
    const toastId = toast.loading(`🤖 Starte AI-Randomisierung für ${totalVariants} Inputs...`);
    
    try {
      const sourceSegment = providerSegments[0];
      const targetSegments = providerSegments.slice(1);
      
      // 🤖 Pro Ziel-Segment: AI-Varianten paraphrasieren mit Claude
      const randomizedSegments = [];
      let processedCount = 0;

      const buildRandomizePromise = async ({
        segIdx,
        varIdx,
        sourceVariant,
        sequenceIndex,
        totalSegments,
        totalInputs,
        sourceVariantCount
      }) => {
        try {
          const response = await axios.post('/api/ai/randomize-content', {
            content: sourceVariant.input_message,
            subject: sourceVariant.input_subject,
            from_name: sourceVariant.input_from_name,
            is_html: sourceVariant.input_message.trim().startsWith('<'),
            email_index: sequenceIndex,
            job_id: `smart_replicate_${Date.now()}`,
            obfuscation: {
              enabled: true,
              zerofront: true,
              garbage_comments: true,
              color_variation: true,
              css_mixing: true,
              tag_diversity: true
            }
          });

          toast.loading(
            `🤖 Randomisiere: ${sequenceIndex}/${totalInputs} (${Math.round(sequenceIndex / totalInputs * 100)}%)\n` +
            `📧 Segment ${segIdx + 2}/${totalSegments}, Input ${varIdx + 1}/${sourceVariantCount}`,
            { id: toastId }
          );

          if (response.data.success) {
            return {
              segIdx,
              varIdx,
              data: {
                variant_group: sourceVariant.variant_group,
                input_subject: response.data.data.subject,
                input_message: response.data.data.html || response.data.data.text,
                input_from_name: response.data.data.fromName,
                slot_range_start: sourceVariant.slot_range_start,
                slot_range_end: sourceVariant.slot_range_end,
                total_variations: sourceVariant.total_variations || (sourceVariant.slot_range_end - sourceVariant.slot_range_start + 1),
                variation_level: sourceVariant.variation_level || 'medium',
                recipient_count: sourceVariant.recipient_count || 0,
                smtp_server_ids: targetSegments[segIdx]?.config?.smtp?.server_ids || sourceVariant.smtp_server_ids || []
              }
            };
          }

          return {
            segIdx,
            varIdx,
            data: {
              variant_group: sourceVariant.variant_group,
              input_subject: sourceVariant.input_subject,
              input_message: sourceVariant.input_message,
              input_from_name: sourceVariant.input_from_name,
              slot_range_start: sourceVariant.slot_range_start,
              slot_range_end: sourceVariant.slot_range_end,
              total_variations: sourceVariant.total_variations || (sourceVariant.slot_range_end - sourceVariant.slot_range_start + 1),
              variation_level: sourceVariant.variation_level || 'medium',
              recipient_count: sourceVariant.recipient_count || 0,
              smtp_server_ids: targetSegments[segIdx]?.config?.smtp?.server_ids || sourceVariant.smtp_server_ids || []
            }
          };
        } catch (error) {
          console.error('AI randomization failed:', error);
          return { segIdx, varIdx, data: { ...sourceVariant } };
        }
      };

      // 🚀 PARALLEL: Alle AI-Randomisierungen gleichzeitig (wie in working.js!)
      const allPromises = [];

      for (let segIdx = 0; segIdx < targetSegments.length; segIdx++) {
        for (let varIdx = 0; varIdx < sourceSegment.ai_variants.length; varIdx++) {
          const sourceVariant = sourceSegment.ai_variants[varIdx];
          const sequenceIndex = processedCount + 1;
          processedCount = sequenceIndex;

          allPromises.push(buildRandomizePromise({
            segIdx,
            varIdx,
            sourceVariant,
            sequenceIndex,
            totalSegments: providerSegments.length,
            totalInputs: totalVariants,
            sourceVariantCount: sourceSegment.ai_variants.length
          }));
        }
      }
      
      // 🚀 Warte auf ALLE Randomisierungen parallel!
      const results = await Promise.allSettled(allPromises);
      
      // Gruppiere Results nach Segment
      const segmentVariantsMap = {};
      results.forEach(result => {
        if (result.status === 'fulfilled' && result.value) {
          const { segIdx, varIdx, data } = result.value;
          if (!segmentVariantsMap[segIdx]) {
            segmentVariantsMap[segIdx] = [];
          }
          segmentVariantsMap[segIdx][varIdx] = data;
        }
      });
      
      // Erstelle randomisierte Segmente
      for (let segIdx = 0; segIdx < targetSegments.length; segIdx++) {
        const targetSeg = targetSegments[segIdx];
        const randomizedVariants = segmentVariantsMap[segIdx] || sourceSegment.ai_variants;
        
        // Config randomisieren
        let targetRate;
        if (parsedThroughput) {
          // 🎯 Berechne Rate basierend auf Ziel-Durchsatz
          const maxParallelSegments = Math.min(
            campaignData.rr_parallel_segments
              ?? getDefaultParallelSegments(campaignData.execution_mode, campaignData.send_method),
            providerSegments.length
          );
          targetRate = Math.round(parsedThroughput / maxParallelSegments);
        } else {
          // 🎲 Random ±20%
          const rateVariation = 0.8 + Math.random() * 0.4;
          const baseRate = sourceSegment.config?.versand?.rate_limit_per_hour || 3000;
          targetRate = Math.round(baseRate * rateVariation);
        }
        
        // Verwende globale streams_count Einstellung (nicht vom Source-Segment!)
        const globalStreamsCount = campaignData.streams_count || 1;
        const streamsDelta = 0; // Keine Variation mehr
        const baseStreams = globalStreamsCount;
        const patterns = ['morning_rush', 'wave', 'pyramid', 'steady', 'evening_blast', 'inverse_pyramid', 'aggressive_max', 'micro_burst'];
        const randomPattern = patterns[Math.floor(Math.random() * patterns.length)];
        
        // 📧 Dynamic From-Email generieren (nach Modus)
        const shouldHaveDynamicEmail = Math.random() * 100 < dynamicEmailPercentage;
        let dynamicFromEmail = null;
        let dynamicFromEmailEnabled = false;
        
        if (shouldHaveDynamicEmail) {
          const prefixes = ['team', 'support', 'contact', 'hello'];
          
          // 👤 Massive Listen für Namen-basierte E-Mails (Millionen Kombinationen!)
          const firstNames = [
            // Deutsche Namen (A-Z)
            'anna', 'andreas', 'anke', 'achim', 'andrea', 'armin', 'astrid', 'antje', 'axel', 'adalbert',
            'ben', 'bernd', 'birgit', 'boris', 'bettina', 'bjoern', 'beatrice', 'benjamin', 'britta', 'burkhard',
            'clara', 'christian', 'claudia', 'carsten', 'christine', 'clemens', 'cornelia', 'christoph', 'carina', 'claus',
            'david', 'daniel', 'diana', 'dirk', 'doris', 'daniela', 'dennis', 'dietmar', 'dagmar', 'dominik',
            'emma', 'erik', 'eva', 'elias', 'elena', 'elisa', 'emil', 'edith', 'eckhard', 'elke',
            'felix', 'florian', 'frank', 'franziska', 'friedrich', 'frieda', 'fabian', 'fiona', 'ferdinand', 'frederik',
            'greta', 'georg', 'gabriela', 'guenther', 'gisela', 'gerald', 'gudrun', 'gregor', 'gerlinde', 'gerhard',
            'hans', 'hannah', 'helena', 'heinrich', 'heike', 'herbert', 'helga', 'holger', 'hanna', 'hendrik',
            'ida', 'isabel', 'ingo', 'ilse', 'ines', 'ingrid', 'imke', 'ingmar', 'iris', 'ivonne',
            'jan', 'jonas', 'julia', 'juergen', 'johanna', 'jakob', 'jennifer', 'joachim', 'jessica', 'jens',
            'katja', 'klaus', 'kerstin', 'karl', 'katharina', 'karolin', 'konrad', 'kira', 'kurt', 'kristin',
            'leon', 'lisa', 'lars', 'lukas', 'laura', 'ludwig', 'lena', 'lothar', 'louisa', 'lennart',
            'maria', 'michael', 'markus', 'matthias', 'martina', 'martin', 'monika', 'max', 'melanie', 'moritz',
            'nick', 'nina', 'norbert', 'nadine', 'nils', 'nicole', 'niklas', 'natalie', 'nepomuk', 'nora',
            'olivia', 'oliver', 'oskar', 'otto', 'olga', 'ortwin', 'ortrud', 'otmar', 'ophelia', 'oswald',
            'paul', 'petra', 'philipp', 'patrick', 'patricia', 'peter', 'pia', 'pascal', 'pauline', 'pius',
            'quinn', 'quentin', 'quirina', 'quintus', 'quirina',
            'rosa', 'robert', 'rita', 'ralf', 'ramona', 'roland', 'renate', 'richard', 'rudolf', 'regina',
            'stefan', 'sabine', 'sebastian', 'sarah', 'simon', 'sandra', 'sven', 'susanne', 'silke', 'siegfried',
            'thomas', 'tina', 'tobias', 'tim', 'theresa', 'thorsten', 'tanja', 'till', 'thea', 'theodor',
            'ulrich', 'ursula', 'uwe', 'ulla', 'ulrike', 'udo', 'uta', 'urban', 'undine', 'ulf',
            'vera', 'viktor', 'volker', 'valentin', 'vanessa', 'verena', 'vincent', 'viktoria', 'viola', 'volkmar',
            'werner', 'wolfgang', 'walter', 'wilhelm', 'wilfried', 'wanda', 'wilma', 'wendelin', 'wiebke', 'willi',
            'xenia', 'xaver', 'xerxes', 'xandra', 'ximena',
            'yves', 'yvonne', 'yannik', 'yasmin', 'yannick', 'yara', 'yorick', 'yolanda', 'ylva', 'york',
            'zoe', 'zacharias', 'zara', 'zenon', 'zita', 'zoltan', 'zeno', 'zelda', 'zora', 'zeus',
            // Weitere beliebte Namen
            'alexander', 'alexandra', 'alice', 'amelie', 'anton', 'arthur', 'august', 'aurelia',
            'benedict', 'bela', 'bella', 'bruno', 'carla', 'carlotta', 'charlotte', 'clara',
            'damian', 'daria', 'deborah', 'delia', 'denise', 'diego', 'dominique', 'doreen',
            'edda', 'edgar', 'edmund', 'edward', 'edwin', 'eileen', 'eleonore', 'elfriede',
            'emilia', 'emily', 'erich', 'ernst', 'esther', 'eugen', 'fabienne', 'felicitas',
            'finn', 'flora', 'franka', 'franz', 'frida', 'fritz', 'gabriele', 'gerd',
            'gerda', 'gernot', 'gertrude', 'gilbert', 'gina', 'gloria', 'gottfried', 'greta',
            'gustav', 'hagen', 'hailey', 'hanna', 'harold', 'harriet', 'harry', 'hauke',
            'heidi', 'helmut', 'henning', 'henry', 'herta', 'hilda', 'hildegard', 'hilmar',
            'hubert', 'hugo', 'ian', 'ilona', 'irmgard', 'irma', 'isabella', 'ismael',
            'jacqueline', 'janina', 'jannik', 'jasmin', 'jean', 'jeannette', 'jerome', 'johann',
            'johannes', 'jonathan', 'joerg', 'josefine', 'josephine', 'josua', 'judith', 'julian',
            'juliane', 'jutta', 'kai', 'karin', 'karlheinz', 'karolina', 'kaspar', 'kassandra',
            'katrin', 'kay', 'kevin', 'kilian', 'kim', 'konstantin', 'korbinian', 'kornelia',
            'lars', 'lea', 'leander', 'leo', 'leonard', 'leonhard', 'leopold', 'leonie',
            'levi', 'liane', 'lidia', 'lilli', 'lilly', 'linda', 'linda', 'linus',
            'lionel', 'liselotte', 'liv', 'lore', 'lorenz', 'lotte', 'louis', 'louise',
            'luca', 'lucia', 'lucian', 'lucie', 'lucinda', 'ludmilla', 'luise', 'luna',
            'magdalena', 'maik', 'maja', 'malte', 'mandy', 'manfred', 'manuel', 'mara',
            'marcel', 'marco', 'margarete', 'margit', 'margot', 'marianne', 'marie', 'marion',
            'marius', 'marlene', 'marlon', 'marta', 'martha', 'mathilde', 'matteo', 'matthaeus',
            'maurice', 'maximilian', 'maya', 'meike', 'melina', 'melissa', 'merle', 'mia',
            'michelle', 'mila', 'milan', 'miranda', 'miriam', 'mirko', 'mona', 'monica'
          ];
          
          const lastNames = [
          // Top deutsche Nachnamen massiv erweitert (bereits Umlaut-konvertiert!)
          'mueller', 'schmidt', 'schneider', 'fischer', 'weber', 'meyer', 'wagner', 'becker', 'schulz', 'hoffmann',
          'schaefer', 'koch', 'bauer', 'richter', 'klein', 'wolf', 'schroeder', 'neumann', 'schwarz', 'zimmermann',
          'braun', 'krueger', 'hofmann', 'hartmann', 'lange', 'schmitt', 'werner', 'schmid', 'krause', 'meier',
          'lehmann', 'huber', 'mayer', 'herrmann', 'walter', 'koehler', 'maier', 'beck', 'koenig', 'kramer',
          'roth', 'frank', 'schulze', 'vogel', 'peters', 'jung', 'hahn', 'keller', 'friedrich', 'guenther',
          'arnold', 'wolff', 'berger', 'scholz', 'gross', 'hermann', 'horn', 'busch', 'ziegler', 'kraft',
          'freund', 'winter', 'stein', 'otto', 'sommer', 'haas', 'graf', 'heinrich', 'schulte', 'moeller',
          'winkler', 'engel', 'pohl', 'seidel', 'bergmann', 'thomas', 'vogt', 'sauer', 'pfeiffer', 'kaiser',
          'fuchs', 'lorenz', 'schubert', 'baumann', 'boehm', 'simon', 'franke', 'albrecht', 'schuster', 'peter',
          'lang', 'weiss', 'huebner', 'ernst', 'ritter', 'bayer', 'brandt', 'kunz', 'kuhn', 'frey',
          'schilling', 'hess', 'bock', 'wolter', 'vogel', 'kraus', 'jaeger', 'urban', 'wilhelm', 'riedel',
            // Weitere 200+ Namen
            'adam', 'adler', 'ahrens', 'alexander', 'anders', 'andreas', 'arndt', 'auer', 'bachmann', 'ballmann',
            'bartels', 'barth', 'baumgart', 'baumgarten', 'baumgartner', 'baur', 'becher', 'behrendt', 'behrens', 'bender',
            'benner', 'berg', 'bergmann', 'berndt', 'bertram', 'beyer', 'binder', 'bischoff', 'blum', 'bode',
            'boettcher', 'bosch', 'brandt', 'braun', 'breuer', 'brinkmann', 'brueck', 'brueckner', 'bruns', 'burger',
            'busse', 'decker', 'diehl', 'dietrich', 'dittrich', 'doering', 'dreher', 'drescher', 'dunkel', 'eberhard',
            'ebert', 'eckert', 'eder', 'egger', 'eggert', 'ehlers', 'ehrlich', 'eichhorn', 'eisele', 'emrich',
          'engel', 'engelhardt', 'erdmann', 'esser', 'faber', 'fahrenholz', 'falk', 'felder', 'fink', 'fleischer',
          'foerster', 'franke', 'franz', 'freier', 'frey', 'fries', 'fritsch', 'fritz', 'frost', 'fuchs',
          'fuhrmann', 'funk', 'gabriel', 'gaertner', 'geiger', 'geissler', 'gerber', 'gerlach', 'giese', 'glaser',
          'goerlich', 'goetz', 'graf', 'greiner', 'grimm', 'gross', 'grube', 'gruber', 'grundmann', 'haase',
            'hagen', 'hahn', 'hammer', 'hamm', 'hansen', 'hartmann', 'hauck', 'hauer', 'hausmann', 'hecht',
            'heidrich', 'heilmann', 'heim', 'hein', 'heinemann', 'heinen', 'heinrich', 'heinz', 'held', 'heller',
            'helm', 'hempel', 'henkel', 'henning', 'hermann', 'herrmann', 'herz', 'herzog', 'hess', 'heuer',
            'hildebrandt', 'hiller', 'hinz', 'hirsch', 'hochberg', 'hofer', 'hoffmann', 'hofmann', 'hohmann', 'holz',
            'holzer', 'hopf', 'hoppe', 'horn', 'huber', 'huebner', 'hummel', 'jacobs', 'jacobi', 'jaeger',
            'jansen', 'janssen', 'john', 'jordan', 'jung', 'junge', 'kahl', 'kaiser', 'kammer', 'kampf',
            'kann', 'kanter', 'kapp', 'karl', 'kaufmann', 'keil', 'keller', 'kellner', 'kern', 'kessler',
            'kiefer', 'kirchner', 'kirsch', 'kistner', 'klaas', 'klaus', 'kleber', 'klein', 'klemm', 'klingel',
          'klose', 'knappe', 'knauer', 'knoll', 'knopf', 'koch', 'koehler', 'kohl', 'kolb', 'koller',
          'koenig', 'konrad', 'korn', 'koerner', 'kraft', 'kramer', 'kraus', 'krause', 'krauss', 'krebs',
          'kremers', 'kretschmer', 'krieger', 'kroeger', 'krug', 'krueger', 'kuhn', 'kuehn', 'kuhne', 'kunz',
            'kurz', 'langer', 'lau', 'lauer', 'laube', 'lauber', 'lehmann', 'lenz', 'leopold', 'lerner',
            'lichtenberg', 'lindemann', 'linder', 'link', 'lipp', 'lohmann', 'lorenz', 'ludwig', 'lutz', 'maas',
            'maier', 'mair', 'mangold', 'mann', 'marquardt', 'martens', 'martin', 'marx', 'maurer', 'mayer',
          'meier', 'meister', 'menzel', 'merkel', 'metz', 'metzger', 'meyer', 'michel', 'mohr', 'moeller',
          'moritz', 'moser', 'mueller', 'nagel', 'naumann', 'neubauer', 'neubert', 'neumann', 'neundorf', 'nickel',
            'niemann', 'nolte', 'nowak', 'obermann', 'opitz', 'orth', 'ostermeyer', 'otto', 'pape', 'paul',
          'paulus', 'peters', 'petersen', 'pfeifer', 'pfeiffer', 'pfister', 'philipp', 'pieper', 'pohl', 'popp',
          'preuss', 'prinz', 'raab', 'rademacher', 'rapp', 'rau', 'rauch', 'rauscher', 'reich', 'reichel',
          'reimer', 'reinhardt', 'reiss', 'reiter', 'renner', 'richter', 'rieger', 'ritter', 'roemer', 'rosenberg',
          'roth', 'rothmann', 'runge', 'sack', 'salomon', 'sander', 'sauer', 'schade', 'schaefer', 'schamberger',
          'scherer', 'schiller', 'schindler', 'schlegel', 'schlueter', 'schmid', 'schmidt', 'schmitt', 'schmitz', 'schneider',
          'scholl', 'scholz', 'schoen', 'schoenberg', 'schott', 'schramm', 'schreber', 'schreiber', 'schroeder', 'schroeter',
            'schubert', 'schulte', 'schulz', 'schulze', 'schumacher', 'schumann', 'schuster', 'schwab', 'schwarz', 'schweitzer',
            'seeger', 'seidel', 'seifert', 'seiler', 'seitz', 'siegel', 'sievers', 'simon', 'sommer', 'sonne',
            'spies', 'springer', 'stadler', 'stahl', 'stark', 'staub', 'stauber', 'steffen', 'stein', 'steinberg',
            'steinmetz', 'stern', 'stieber', 'stock', 'stoll', 'stolz', 'strasser', 'strauss', 'strobel', 'tanner',
            'thiel', 'thoma', 'thomas', 'tiedemann', 'trunk', 'uhlig', 'ulbrich', 'ullmann', 'ullrich', 'ulrich',
            'unger', 'urban', 'vogel', 'vogt', 'voigt', 'volkmann', 'vollmer', 'wagner', 'wahl', 'walter',
          'walther', 'weber', 'wegner', 'weidmann', 'weiland', 'weiler', 'weis', 'weiss', 'weiss', 'wenzel',
          'werner', 'westphal', 'wiedemann', 'wieland', 'wilhelm', 'winkler', 'winter', 'wirth', 'wittmann', 'wolf',
          'wolff', 'wurm', 'zahn', 'zeller', 'zenker', 'ziegler', 'zimmer', 'zimmermann', 'zink', 'zoeller'
          ];
          
          switch (dynamicEmailMode) {
            case 'none':
              // Kein Dynamic From-Email
              break;
            case 'prefix_only':
              // Nur verschiedene Prefixes (keine Codes)
              dynamicFromEmail = prefixes[Math.floor(Math.random() * prefixes.length)];
              dynamicFromEmailEnabled = true;
              break;
            case 'short_codes':
              // Kurze Codes: prefix-($)3Z($)
              const shortCodes = ['($)3Z($)', '($)4Z($)'];
              dynamicFromEmail = `${prefixes[Math.floor(Math.random() * prefixes.length)]}-${shortCodes[Math.floor(Math.random() * shortCodes.length)]}`;
              dynamicFromEmailEnabled = true;
              break;
            case 'varied':
              // Mix aus allem (kürzer als vorher!)
              const variedCodes = ['($)3Z($)', '($)4Z($)'];
              const usePrefix = Math.random() > 0.3;  // 70% mit Prefix
              if (usePrefix) {
                dynamicFromEmail = `${prefixes[Math.floor(Math.random() * prefixes.length)]}-${variedCodes[Math.floor(Math.random() * variedCodes.length)]}`;
              } else {
                dynamicFromEmail = `${prefixes[Math.floor(Math.random() * prefixes.length)]}`;
              }
              dynamicFromEmailEnabled = true;
              break;
            case 'name_based':
              // 👤 Realen Namen generieren (Client-Side)
              const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
              const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
              dynamicFromEmail = `${firstName}.${lastName}`;
              dynamicFromEmailEnabled = true;
              break;
            default:
              // Fallback zu varied
              const defaultCodes = ['($)3Z($)', '($)4Z($)'];
              const useDefaultPrefix = Math.random() > 0.3;
              if (useDefaultPrefix) {
                dynamicFromEmail = `${prefixes[Math.floor(Math.random() * prefixes.length)]}-${defaultCodes[Math.floor(Math.random() * defaultCodes.length)]}`;
            } else {
                dynamicFromEmail = `${prefixes[Math.floor(Math.random() * prefixes.length)]}`;
            }
              dynamicFromEmailEnabled = true;
              break;
          }
        }
        
        // 📧 Headers werden jetzt durch Mail-Fingerprint ersetzt (nicht mehr separat generiert)
        
        randomizedSegments.push({
          ...targetSeg,
          config: {
            ...sourceSegment.config,
            smtp: {
              ...sourceSegment.config.smtp,
              server_ids: targetSeg.config?.smtp?.server_ids || targetSeg.smtp_ids || []  // ← WICHTIG: Original-SMTP beibehalten!
            },
            // 🌐 FIX: Webmail-Config für DIESES Segment setzen (nicht vom Source kopieren!)
            webmail: targetSeg.config?.webmail ? {
              account_id: targetSeg.config.webmail.account_id || targetSeg.webmail_account_id,
              account_email: targetSeg.config.webmail.account_email || targetSeg.provider_name,
              parallel_threads: targetSeg.config.webmail.parallel_threads || 2,
              text_type: targetSeg.config.webmail.text_type || 'html'
            } : (targetSeg.webmail_account_id ? {
              account_id: targetSeg.webmail_account_id,
              account_email: targetSeg.provider_name,
              parallel_threads: 2,
              text_type: 'html'
            } : sourceSegment.config.webmail),
            versand: {
              ...sourceSegment.config.versand,
              rate_limit_per_hour: targetRate,
              streams_count: Math.max(1, Math.min(6, baseStreams + streamsDelta))
            },
            sending_pattern: {
              type: randomPattern,
              jitter: {
                enabled: true,
                min_ms: -500 - Math.floor(Math.random() * 500),
                max_ms: 500 + Math.floor(Math.random() * 1000)
              }
            },
            fingerprint: {
              rotation_mode: fingerprintRotation,
              ehlo_rotation: ehloRotation,
              encoding_distribution: encodingDistribution,
              use_multipart: useMultipart,
              multipart_percentage: multipartPercentage,
              client_distribution: clientDistribution  // 🔧 FIX: Client-Verteilung hinzugefügt!
            },
            dynamic_from_email: dynamicFromEmail,
            dynamic_from_email_enabled: dynamicFromEmailEnabled
          },
          ai_variants: randomizedVariants  // ← Randomisierte AI-Inputs!
        });
      }
      
      // Update State
      setProviderSegments([providerSegments[0], ...randomizedSegments]);
      
      // ✅ Success Toast (update existing)
      toast.success(`✅ ${randomizedSegments.length} Segmente mit ${totalVariants} einzigartigen AI-Inputs!`, { id: toastId });
      
    } catch (error) {
      console.error('Smart replicate failed:', error);
      toast.error('Randomisierung fehlgeschlagen: ' + error.message, { id: toastId });
    } finally {
      setSegmentAnalyzing(false);
    }
  };

  const prepareSegmentsForHtmlUpload = async (segsOverride = null) => {
    const segs = segsOverride || providerSegments;
    const sfRulesCheck = campaignData.subject_fromname_rules || [];
    const globalSubject = String(campaignData.segmented_template_subject || '').trim();
    const globalFromName = String(campaignData.segmented_template_from_name || '').trim();
    if (sfRulesCheck.length === 0 && (!globalSubject || !globalFromName)) {
      toast.error('Bitte Betreff und From-Name für HTML-Uploads setzen');
      return null;
    }
    
    const sfRules = campaignData.subject_fromname_rules || [];
    const getSubjectFromNameForSegment = (segNum) => resolveSubjectFromNameForSegment(
      segNum,
      sfRules,
      campaignData.segmented_template_subject || '',
      campaignData.segmented_template_from_name || '',
    );

    // Templates per Round-Robin auf Segmente verteilen, falls noch nicht geschehen
    if (segs.some(segment => !Array.isArray(segment.html_templates) || segment.html_templates.length === 0)) {
      if (segmentedHtmlTemplates.length > 0) {
        const distribution = distributeTemplatesEvenly(segmentedHtmlTemplates, segs.length);
        segs.forEach((seg, idx) => {
          seg.html_templates = distribution[idx] || [];
        });
      } else {
        toast.error('Keine HTML-Templates vorhanden');
        return null;
      }
    }

    const updatedSegments = await Promise.all(segs.map(async (segment, segIdx) => {
      const segNum = segment.segment_number || (segIdx + 1);
      const { subject: baseSubject, fromName: baseFromName } = getSubjectFromNameForSegment(segNum);
      
      const templates = Array.isArray(segment.html_templates) ? segment.html_templates : [];
      if (templates.length === 0) {
        return {
          ...segment,
          config: {
            ...segment.config,
            zerowidth_subject: campaignData.zerowidth_subject,
            zerowidth_fromname: campaignData.zerowidth_fromname,
            zerowidth_content: campaignData.zerowidth_content,
            tld_rotation: !!campaignData.tld_rotation
          }
        };
      }

      const firstTemplate = templates[0] || '<div></div>';
      const isHtmlTemplate = String(firstTemplate || '').trim().startsWith('<');
      let randomizedSubject = baseSubject;
      let randomizedFromName = baseFromName;

      try {
        const response = await axios.post('/api/ai/randomize-content', {
          content: firstTemplate,
          subject: baseSubject,
          from_name: baseFromName,
          is_html: isHtmlTemplate,
          email_index: 0,
          job_id: `segment_html_${Date.now()}_${segment.segment_number || 'x'}`
        });
        if (response.data?.success) {
          randomizedSubject = response.data.data?.subject || baseSubject;
          randomizedFromName = response.data.data?.fromName || baseFromName;
        }
      } catch (error) {
        console.warn('Subject/From AI-Randomisierung fehlgeschlagen:', error);
      }

      const slotSize = segment.config?.versand?.slot_size || 100;
      const recipientCount = segment.recipient_count || segment.recipients?.length || 0;
      const totalSlots = segment.config?.versand?.total_slots || Math.max(1, Math.ceil(recipientCount / slotSize));
      const variant = {
        variant_group: 'html',
        input_subject: randomizedSubject,
        input_message: firstTemplate,
        input_from_name: randomizedFromName,
        slot_range_start: 1,
        slot_range_end: totalSlots,
        total_variations: totalSlots,
        variation_level: 'conservative',
        recipient_count: recipientCount,
        smtp_server_ids: segment.config?.smtp?.server_ids || []
      };

      return {
        ...segment,
        ai_variants: [variant],
        config: {
          ...segment.config,
          html_templates: templates,
          template_rotation_mode: 'equal_distribution',
          ai_content_lock: true,
          ...(() => {
            const sNum = segment.segment_number || (segs.indexOf(segment) + 1);
            const rules = campaignData.segment_rules || [];
            const rule = rules.find(r => sNum >= r.from && (r.to === null || r.to === '' || sNum <= r.to));
            const roll = () => Math.random() * 100;
            const hasRule = (field) => rule && rule[field] !== undefined;
            return {
              zerowidth_subject: hasRule('zw_subject') ? !!rule.zw_subject : roll() < (campaignData.zw_subject_pct ?? 100),
              zerowidth_fromname: hasRule('zw_fromname') ? !!rule.zw_fromname : roll() < (campaignData.zw_from_pct ?? 0),
              zerowidth_content: hasRule('zw_content') ? !!rule.zw_content : roll() < (campaignData.zw_content_pct ?? 0),
              garbage_code: hasRule('garbage') ? !!rule.garbage : roll() < (campaignData.garbage_pct ?? 0),
              use_multipart: hasRule('multipart') ? !!rule.multipart : roll() < (campaignData.multipart_pct ?? 0),
              zw_textpart: true,
              multipart_dice_enabled: !!campaignData.multipart_dice_enabled,
              tld_rotation: !!campaignData.tld_rotation,
              client_profile: rule?.client_profile || null,
              disable_client_headers: rule ? !!rule.disable_client_headers : false
            };
          })()
        }
      };
    }));

    setProviderSegments(updatedSegments);
    return updatedSegments;
  };
  
  // 🎯 Segmented-Parallel: AI-Generation + Slot-Creation
  const handleSegmentedParallelNext = async (segmentsOverride = null) => {
    // 1. Validierung
    const activeSegments = segmentsOverride || providerSegments;
    if (activeSegments.length === 0) {
      toast.error('Bitte zuerst Provider-Segmente erstellen');
      return;
    }

    const skipAiContentGeneration = htmlUploadActive;
    if (htmlUploadActive) {
      setAutoAiGenerating(true);
      setAutoAiProgress({
        current: 0,
        total: activeSegments.length,
        status: 'Bereite HTML-Templates vor...'
      });
    }
    const segmentsAlreadyPreparedForHtml = !htmlGeneratorActive && activeSegments.every(segment => (
      Array.isArray(segment.ai_variants) && segment.ai_variants.length > 0
      && Array.isArray(segment.config?.html_templates) && segment.config.html_templates.length > 0
    ));
    const segmentsForProcessing = htmlUploadActive && !htmlGeneratorActive
      ? (segmentsAlreadyPreparedForHtml
        ? activeSegments
        : await prepareSegmentsForHtmlUpload(segmentsOverride))
      : activeSegments;

    if (!segmentsForProcessing) {
      setAutoAiGenerating(false);
      return;
    }

    if (!segmentsOverride) {
      const recipientsForValidation = segmentationRecipientsSnapshot.length > 0
        ? segmentationRecipientsSnapshot
        : campaignData.central_recipients;
      if (recipientsForValidation && recipientsForValidation.length > 0) {
        const distributionCheck = validateSegmentRecipientDistribution(
          segmentsForProcessing,
          recipientsForValidation
        );
        if (!distributionCheck.ok) {
          console.error('❌ Recipient distribution mismatch:', distributionCheck);
          const missingCount = distributionCheck.missing.length;
          const extraCount = distributionCheck.extra.length;
          setAutoAiGenerating(false);
          toast.error(`❌ Empfänger-Verteilung fehlerhaft: ${missingCount} fehlend, ${extraCount} zu viel`);
          return;
        }
      }
    }

    for (const segment of segmentsForProcessing) {
      if (!segment.ai_variants || segment.ai_variants.length === 0) {
        setAutoAiGenerating(false);
        toast.error(`Segment "${segment.provider_name}" hat keine AI-Varianten! Bitte konfigurieren.`);
        return;
      }
    }
    
    // 2. Campaign erstellen (draft)
    try {
      const campaignPayload = {
        name: campaignData.name,
        description: campaignData.description,
        execution_mode: campaignData.execution_mode || 'segmented_parallel',  // Nutze aktuellen Mode (auch smtp_segmented_parallel!)
        central_recipients: campaignData.central_recipients,
        global_attachments: campaignData.global_attachments || [],  // 📎 Zentrale Anhänge!
        is_customer_order: campaignData.is_customer_order || false,  // 🔌 Kundenauftrag-Flag
        // 🌐 Webmail-spezifische Felder
        send_method: campaignData.send_method || 'smtp',
        selected_webmail_accounts: campaignData.selected_webmail_accounts || [],
        webmail_parallel_threads: campaignData.webmail_parallel_threads || 2,
        webmail_text_type: campaignData.webmail_text_type || 'html',
        rr_parallel_segments: campaignData.rr_parallel_segments
          ?? getDefaultParallelSegments(campaignData.execution_mode, campaignData.send_method),
        random_runtime_enabled: campaignData.random_runtime_enabled !== false,
        random_messages_per_connection: campaignData.random_messages_per_connection === true,
        multipart_dice_enabled: !!campaignData.multipart_dice_enabled,
        opener_html_templates: openerHtmlTemplates.length > 0 ? openerHtmlTemplates : null,
        status: 'draft'
      };
      
      const campaignResponse = await axios.post('/api/multi-campaigns', campaignPayload);
      const campaignId = campaignResponse.data.data.id;
      
      // 3. Pro Segment: Erstellen + AI generieren + Slots erstellen
      setAutoAiGenerating(true);
      setAutoAiProgress({ current: 0, total: segmentsForProcessing.length, status: 'Starte...' });
      
      // 🚀 PARALLEL in Batches: 50 Segmente gleichzeitig, dann nächste 50
      const SEGMENT_BATCH_SIZE = 50;
      const segmentBatches = Math.ceil(segmentsForProcessing.length / SEGMENT_BATCH_SIZE);
      
      for (let batchIdx = 0; batchIdx < segmentBatches; batchIdx++) {
        const batchStart = batchIdx * SEGMENT_BATCH_SIZE;
        const batchEnd = Math.min(batchStart + SEGMENT_BATCH_SIZE, segmentsForProcessing.length);
        const batchSegments = segmentsForProcessing.slice(batchStart, batchEnd);
        
        console.log(`🚀 Segment-Batch ${batchIdx + 1}/${segmentBatches}: Verarbeite Segmente ${batchStart + 1}-${batchEnd}`);
        
        // Alle Segmente im Batch PARALLEL verarbeiten
        const segmentPromises = batchSegments.map(async (segment, batchSegIdx) => {
          const segIdx = batchStart + batchSegIdx;
          
          try {
        setAutoAiProgress({
          current: segIdx,
          total: segmentsForProcessing.length,
              status: `Batch ${batchIdx + 1}: Segment ${segIdx + 1}/${segmentsForProcessing.length}`
        });
        
        // 3a. Segment in DB erstellen
        // ✅ Füge globale List-Unsubscribe Settings hinzu
        // Segment-Rules: Finde passende Rule fuer dieses Segment
        const segNum = segment.segment_number || (segIdx + 1);
        const rules = campaignData.segment_rules || [];
        const matchedRule = rules.find(r => segNum >= r.from && (r.to === null || r.to === '' || segNum <= r.to));
        
        const roll = () => Math.random() * 100;
        const hasRule = (field) => matchedRule && matchedRule[field] !== undefined;
        const segmentConfig = {
          ...segment.config,
          zerowidth_subject: hasRule('zw_subject') ? !!matchedRule.zw_subject : roll() < (campaignData.zw_subject_pct ?? 100),
          zerowidth_fromname: hasRule('zw_fromname') ? !!matchedRule.zw_fromname : roll() < (campaignData.zw_from_pct ?? 0),
          zerowidth_content: hasRule('zw_content') ? !!matchedRule.zw_content : roll() < (campaignData.zw_content_pct ?? 0),
          garbage_code: hasRule('garbage') ? !!matchedRule.garbage : roll() < (campaignData.garbage_pct ?? 0),
          use_multipart: hasRule('multipart') ? !!matchedRule.multipart : roll() < (campaignData.multipart_pct ?? 0),
          zw_textpart: true,
          multipart_dice_enabled: !!campaignData.multipart_dice_enabled,
          tld_rotation: !!campaignData.tld_rotation,
          client_profile: matchedRule?.client_profile || null,
          disable_client_headers: matchedRule ? !!matchedRule.disable_client_headers : false,
          thread_opener: campaignData.thread_opener_enabled ? {
            enabled: true,
            opener_html: campaignData.thread_opener_html || null,
            delay_after_opener_ms: (campaignData.thread_opener_delay || 5) * 1000,
            opener_recipient: campaignData.thread_opener_recipient || null,
          } : null,
          list_unsubscribe: {
            enabled: campaignData.list_unsubscribe_enabled || false,
            url: campaignData.list_unsubscribe_url || '{{Domain}}/unsubscribe',
            method: campaignData.list_unsubscribe_method || 'oneclick'
          },
          reply_to: campaignData.reply_to || null
        };
        const htmlRedirectConfigs = htmlUploadActive ? buildSegmentRedirectConfigsFromHtmlLinks() : null;
        if (htmlRedirectConfigs?.aws_redirects) {
          segmentConfig.aws_redirects = htmlRedirectConfigs.aws_redirects;
        }
        if (htmlRedirectConfigs?.aws_hybrid_redirects) {
          segmentConfig.aws_hybrid_redirects = htmlRedirectConfigs.aws_hybrid_redirects;
        }
        if (htmlRedirectConfigs?.searchapp_redirects) {
          segmentConfig.searchapp_redirects = htmlRedirectConfigs.searchapp_redirects;
        }
        if (htmlRedirectConfigs?.share_google_redirects) {
          segmentConfig.share_google_redirects = htmlRedirectConfigs.share_google_redirects;
        }
        if (htmlRedirectConfigs?.brandlogo_redirects) {
          segmentConfig.brandlogo_redirects = htmlRedirectConfigs.brandlogo_redirects;
        }
        if (htmlRedirectConfigs?.story_wrap_redirects) {
          segmentConfig.story_wrap_redirects = htmlRedirectConfigs.story_wrap_redirects;
        }
        
        console.log(`🔍 Sending segment ${segIdx + 1} config to backend:`, {
          door_opener: segmentConfig.door_opener,
          fingerprint: segmentConfig.fingerprint,
          has_encoding_dist: !!segmentConfig.fingerprint?.encoding_distribution
        });
        
        // Testmail als erster Empfänger einfügen (jedes X. Segment)
        let segRecipients = segment.recipients;
        const testCfg = getTestMailConfig();
        if (testCfg && (segIdx + 1) % testCfg.segmentInterval === 0) {
          segRecipients = [...testCfg.emails, ...segment.recipients];
        }
        
        const segmentResponse = await axios.post('/api/provider-segments/create', {
          campaign_id: campaignId,
          segment_number: segment.segment_number,
          segment_data: {
            provider_name: segment.provider_name,
            provider_type: segment.provider_type,
            recipients: segRecipients,
            config: segmentConfig
          }
        });
        
        const segmentId = segmentResponse.data.data.segment_id;
        
        // 3b. AI-Variants in DB erstellen
        for (const variant of segment.ai_variants) {
          console.log(`📊 Saving AI-Variant: ${variant.variant_group}, Ranges: ${variant.slot_range_start}-${variant.slot_range_end}`);
          await axios.post(`/api/provider-segments/${segmentId}/ai-variants`, variant);
        }
        
        // 3c. AI-Content generieren
        if (!skipAiContentGeneration) {
          setAutoAiProgress(prev => ({
            ...prev,
            status: `AI-Generation: ${segment.provider_name}...`
          }));
          await axios.post(`/api/provider-segments/${segmentId}/generate-ai`);
          
          // 3d. Warte auf AI-Completion (Polling)
          await pollSegmentAICompletion(segmentId, segment.provider_name, segIdx, segmentsForProcessing.length);
        }
        
        // 3d-AWS. 1 kombinierter Request pro Segment (Logo+CTA+Unsub im selben Bucket)
        const perTypeLinkConfig = htmlLinkConfig?.link_config || {};
        const perTypeLinkCounts = htmlLinkConfig?.link_counts || {};
        const awsLinkTypes = ['logo', 'cta', 'unsubscribe'].filter(t => {
          const p = perTypeLinkConfig[t]?.provider;
          return p === 'aws' || p === 'aws+searchapp';
        });
        
        if (awsLinkTypes.length > 0) {
          setAutoAiProgress(prev => ({
            ...prev,
            status: `AWS-Redirects: ${segment.provider_name}...`
          }));
          
          try {
            const allLinks = { logo: [], cta: [], unsubscribe: [] };
            
            // Gruppiere Typen nach Target-Mode (slot vs custom getrennt!)
            const slotTypes = [];
            const customTypes = [];
            let slotDomain = null;
            let customTargetUrl = null;
            let region = MULTI_CAMPAIGN_DEFAULT_AWS_REGIONS[0];
            
            for (const type of awsLinkTypes) {
              const typeCfg = perTypeLinkConfig[type];
              region = resolveMultiCampaignAwsRegion(typeCfg, segIdx);
              
              if (typeCfg.target === 'custom') {
                const targets = Array.isArray(typeCfg.custom_targets) ? typeCfg.custom_targets.filter(Boolean) : [];
                if (targets.length > 0) {
                  customTargetUrl = targets[segIdx % targets.length];
                  customTypes.push(type);
                }
              } else {
                const domains = Array.isArray(typeCfg.slot?.domains) ? typeCfg.slot.domains.filter(Boolean) : [];
                if (domains.length > 0) {
                  slotDomain = domains[segIdx % domains.length];
                  slotTypes.push(type);
                }
              }
            }
            
            // Request 1: Slot-Typen (gleiche Domain, gleicher Bucket)
            if (slotTypes.length > 0 && slotDomain) {
              const counts = { logo: 0, cta: 0, unsubscribe: 0 };
              slotTypes.forEach(t => { counts[t] = Math.max(0, Number(perTypeLinkCounts[t]) || 0); });
              
              try {
                const response = await axios.post('/api/aws-redirect/request', {
                  campaign_id: campaignResponse.data.data.campaign_id,
                  segment_id: segmentId,
                  region,
                  slot_domain: slotDomain,
                  counts
                });
                if (response.data.success && response.data.data.links) {
                  allLinks.logo.push(...(response.data.data.links.logo || []));
                  allLinks.cta.push(...(response.data.data.links.cta || []));
                  allLinks.unsubscribe.push(...(response.data.data.links.unsubscribe || []));
                }
              } catch (reqErr) {
                console.error(`❌ AWS Slot-Links für Segment ${segIdx + 1} failed:`, reqErr.message);
              }
            }
            
            // Request 2: Custom-Typen (eigener Bucket mit target_url)
            if (customTypes.length > 0 && customTargetUrl) {
              const counts = { logo: 0, cta: 0, unsubscribe: 0 };
              customTypes.forEach(t => { counts[t] = Math.max(0, Number(perTypeLinkCounts[t]) || 0); });
              
              try {
                const response = await axios.post('/api/aws-redirect/request', {
                  campaign_id: campaignResponse.data.data.campaign_id,
                  segment_id: segmentId,
                  region,
                  target_url: customTargetUrl,
                  counts
                });
                if (response.data.success && response.data.data.links) {
                  allLinks.logo.push(...(response.data.data.links.logo || []));
                  allLinks.cta.push(...(response.data.data.links.cta || []));
                  allLinks.unsubscribe.push(...(response.data.data.links.unsubscribe || []));
                }
              } catch (reqErr) {
                console.error(`❌ AWS Custom-Links für Segment ${segIdx + 1} failed:`, reqErr.message);
              }
            }
            
            // Speichere Links im Segment-Config
            if (!segmentConfig.aws_redirects) segmentConfig.aws_redirects = {};
            segmentConfig.aws_redirects.enabled = true;
            segmentConfig.aws_redirects.links = allLinks;
            
            // Update Segment in DB mit Links
            await axios.put(`/api/provider-segments/${segmentId}`, {
              segment_config: segmentConfig
            });
            
            const totalLinks = allLinks.logo.length + allLinks.cta.length + allLinks.unsubscribe.length;
            if (totalLinks > 0) {
              toast.success(`✅ ${totalLinks} AWS-Links für ${segment.provider_name}`);
            }
            
          } catch (awsError) {
            console.error('AWS-Redirect generation failed:', awsError);
            toast.error(`⚠️ AWS-Redirects für ${segment.provider_name} fehlgeschlagen`);
          }
        }

        // 3d-WRAP. stories.app.goo.gl oder search.app.goo.gl via POST /api/searchapp/request
        const wrapLinkTypes = ['logo', 'cta', 'unsubscribe'].filter(t => {
          const provider = perTypeLinkConfig[t]?.provider;
          return provider === 'story-wrap' || provider === 'search-app-wrap';
        });
        const wrapProvider = wrapLinkTypes
          .map(t => perTypeLinkConfig[t]?.provider)
          .find(p => p === 'story-wrap' || p === 'search-app-wrap')
          || 'story-wrap';

        if (wrapLinkTypes.length > 0) {
          setAutoAiProgress(prev => ({
            ...prev,
            status: `${wrapProvider === 'search-app-wrap' ? 'Search-App-Wrap' : 'Story-Wrap'} Links: ${segment.provider_name}...`
          }));

          try {
            const allLinks = {
              'story-wrap': [],
              story_cta: [],
              logo: [],
              cta: [],
              unsubscribe: [],
            };

            const urlGroups = {};
            for (const type of wrapLinkTypes) {
              const typeCfg = perTypeLinkConfig[type];
              const count = Math.max(0, Number(perTypeLinkCounts[type]) || 0);
              if (count === 0) continue;

              let slotDomain = null;
              let customTargetUrl = null;
              if (typeCfg?.target === 'custom') {
                const targets = Array.isArray(typeCfg.custom_targets) ? typeCfg.custom_targets.filter(Boolean) : [];
                if (targets.length > 0) {
                  const raw = targets[segIdx % targets.length];
                  customTargetUrl = raw.startsWith('http') ? raw : `https://${raw}`;
                }
              } else {
                const domains = Array.isArray(typeCfg?.slot?.domains) ? typeCfg.slot.domains.filter(Boolean) : [];
                if (domains.length > 0) slotDomain = domains[segIdx % domains.length];
              }
              if (!slotDomain && !customTargetUrl) continue;

              const groupKey = customTargetUrl ? `custom:${customTargetUrl}` : `slot:${slotDomain}`;
              if (!urlGroups[groupKey]) {
                urlGroups[groupKey] = {
                  slotDomain,
                  customTargetUrl,
                  counts: { logo: 0, cta: 0, unsubscribe: 0 },
                };
              }
              urlGroups[groupKey].counts[type] += count;
            }

            for (const grp of Object.values(urlGroups)) {
              const response = await axios.post('/api/searchapp-redirect/request', {
                campaign_id: campaignResponse.data.data.campaign_id,
                segment_id: segmentId,
                provider: wrapProvider,
                counts: grp.counts,
                ...(grp.slotDomain ? { slot_domain: grp.slotDomain } : {}),
                ...(grp.customTargetUrl ? { target_url: grp.customTargetUrl } : {}),
              });

              if (response.data.success && response.data.data?.links) {
                const rawLinks = response.data.data.links;
                if (wrapProvider === 'search-app-wrap') {
                  allLinks['story-wrap'].push(...(rawLinks['search-app-wrap'] || []));
                  allLinks.story_cta.push(...(rawLinks.search_app_wrap_cta || []));
                } else {
                  allLinks['story-wrap'].push(...(rawLinks['story-wrap'] || rawLinks.story_wrap || []));
                  allLinks.story_cta.push(...(rawLinks.story_cta || []));
                }
                allLinks.logo.push(...(rawLinks.logo || []));
                allLinks.cta.push(...(rawLinks.cta || []));
                allLinks.unsubscribe.push(...(rawLinks.unsubscribe || []));
              }
            }

            const totalLinks = allLinks['story-wrap'].length + allLinks.story_cta.length + allLinks.unsubscribe.length;
            if (totalLinks > 0) {
              segmentConfig.story_wrap_redirects = {
                enabled: true,
                provider: wrapProvider,
                links: allLinks,
              };
              await axios.put(`/api/provider-segments/${segmentId}`, {
                segment_config: segmentConfig
              });
              toast.success(`✅ ${totalLinks} ${wrapProvider === 'search-app-wrap' ? 'Search-App-Wrap' : 'Story-Wrap'} Links für ${segment.provider_name}`);
            }
          } catch (storyWrapError) {
            console.error('Wrap link generation failed:', storyWrapError);
            toast.error(`⚠️ Wrap-Links für ${segment.provider_name} fehlgeschlagen`);
          }
        }
        
        // 3d-SEARCHAPP. 1 kombinierter Request pro Segment (wie AWS)
        const searchLinkTypes = ['logo', 'cta', 'unsubscribe'].filter(t => {
          const p = perTypeLinkConfig[t]?.provider;
          return p === 'searchapp'
            || p === 'searchapp-s3'
            || p === 'aws+searchapp'
            || p === 'share.google+searchapp'
            || p === 'hybrid-share.google'
            || p === 'hybrid-aws-share.google';
        });
        
        if (searchLinkTypes.length > 0) {
          setAutoAiProgress(prev => ({
            ...prev,
            status: `Search.app Links: ${segment.provider_name}...`
          }));
          
          try {
            const allLinks = { logo: [], cta: [], unsubscribe: [] };
            
            const slotTypes = [];
            const customTypes = [];
            let slotDomain = null;
            let customTargetUrl = null;
            let linkStyle = 'SHORT';
            let isHybrid = false;
            let region = MULTI_CAMPAIGN_DEFAULT_AWS_REGIONS[0];
            const hasHybridShareGoogle = searchLinkTypes.some(t => perTypeLinkConfig[t]?.provider === 'hybrid-share.google');
            
            for (const type of searchLinkTypes) {
              const typeCfg = perTypeLinkConfig[type];
              linkStyle = typeCfg.link_style || linkStyle;
              if (typeCfg.provider === 'searchapp-s3') isHybrid = true;
              region = resolveMultiCampaignAwsRegion(typeCfg, segIdx);
              
              if (typeCfg.target === 'custom') {
                const targets = Array.isArray(typeCfg.custom_targets) ? typeCfg.custom_targets.filter(Boolean) : [];
                if (targets.length > 0) {
                  customTargetUrl = targets[segIdx % targets.length];
                  customTypes.push(type);
                }
              } else {
                const domains = Array.isArray(typeCfg.slot?.domains) ? typeCfg.slot.domains.filter(Boolean) : [];
                if (domains.length > 0) {
                  slotDomain = domains[segIdx % domains.length];
                  slotTypes.push(type);
                }
              }
            }
            if (hasHybridShareGoogle) linkStyle = 'UNGUESSABLE';
            
            const apiEndpoint = isHybrid
              ? '/api/searchapp-s3-redirect/request'
              : '/api/searchapp-redirect/request';
            
            const searchRetry = async (payload, label) => {
              for (let att = 0; att < 3; att++) {
                try {
                  const response = await axios.post(apiEndpoint, payload);
                  if (response.data.success && response.data.data?.links) return response.data.data.links;
                } catch (e) {
                  console.error(`❌ ${label} attempt ${att + 1} failed:`, e.message);
                  if (att < 2) await new Promise(r => setTimeout(r, 1000 * (att + 1)));
                }
              }
              return null;
            };

            if (slotTypes.length > 0 && slotDomain) {
              const counts = { logo: 0, cta: 0, unsubscribe: 0 };
              slotTypes.forEach(t => { counts[t] = Math.max(0, Number(perTypeLinkCounts[t]) || 0); });
              const rl = await searchRetry({
                campaign_id: campaignResponse.data.data.campaign_id, segment_id: segmentId,
                smtp_email: segment.provider_name, link_style: linkStyle, slot_domain: slotDomain,
                ...(isHybrid ? { region } : {}), counts
              }, `Search.app Slot seg ${segIdx + 1}`);
              if (rl) { allLinks.logo.push(...(rl.logo || [])); allLinks.cta.push(...(rl.cta || [])); allLinks.unsubscribe.push(...(rl.unsubscribe || [])); }
            }
            
            if (customTypes.length > 0 && customTargetUrl) {
              const counts = { logo: 0, cta: 0, unsubscribe: 0 };
              customTypes.forEach(t => { counts[t] = Math.max(0, Number(perTypeLinkCounts[t]) || 0); });
              const rl = await searchRetry({
                campaign_id: campaignResponse.data.data.campaign_id, segment_id: segmentId,
                smtp_email: segment.provider_name, link_style: linkStyle, target_url: customTargetUrl,
                ...(isHybrid ? { region } : {}), counts
              }, `Search.app Custom seg ${segIdx + 1}`);
              if (rl) { allLinks.logo.push(...(rl.logo || [])); allLinks.cta.push(...(rl.cta || [])); allLinks.unsubscribe.push(...(rl.unsubscribe || [])); }
            }

            // Retry individually for any requested types that came back empty
            for (const type of searchLinkTypes) {
              if (allLinks[type].length > 0) continue;
              const count = Math.max(0, Number(perTypeLinkCounts[type]) || 0);
              if (count === 0) continue;
              const typeCfg = perTypeLinkConfig[type];
              const useDomain = typeCfg.target === 'custom' ? customTargetUrl : slotDomain;
              if (!useDomain) continue;
              const domKey = typeCfg.target === 'custom' ? 'target_url' : 'slot_domain';
              console.log(`🔄 Retry empty ${type} for segment ${segIdx + 1}`);
              const rl = await searchRetry({
                campaign_id: campaignResponse.data.data.campaign_id, segment_id: segmentId,
                smtp_email: segment.provider_name, link_style: linkStyle, [domKey]: useDomain,
                ...(isHybrid ? { region } : {}), counts: { logo: 0, cta: 0, unsubscribe: 0, [type]: count }
              }, `Search.app retry ${type} seg ${segIdx + 1}`);
              if (rl && rl[type]?.length) allLinks[type].push(...rl[type]);
            }
            
            // Speichere Links im Segment-Config
            if (!segmentConfig.searchapp_redirects) segmentConfig.searchapp_redirects = {};
            segmentConfig.searchapp_redirects.enabled = true;
            segmentConfig.searchapp_redirects.links = allLinks;
            
            // Update Segment in DB
            await axios.put(`/api/provider-segments/${segmentId}`, {
              segment_config: segmentConfig
            });
            
            const totalLinks = allLinks.logo.length + allLinks.cta.length + allLinks.unsubscribe.length;
            if (totalLinks > 0) {
              toast.success(`✅ ${totalLinks} Search.app Links für ${segment.provider_name}`);
            }
            
          } catch (searchappError) {
            console.error('Search.app generation failed:', searchappError);
            toast.error(`⚠️ Search.app Links für ${segment.provider_name} fehlgeschlagen`);
          }
        }
        
        // 3d-SHAREGOOGLE. share.google Links pro Segment
        const shareGoogleLinkTypes = ['logo', 'cta', 'unsubscribe'].filter(t => {
          const p = perTypeLinkConfig[t]?.provider;
          return p === 'share.google'
            || p === 'share.google+searchapp';
        });
        
        if (shareGoogleLinkTypes.length > 0) {
          setAutoAiProgress(prev => ({
            ...prev,
            status: `Share.Google Links: ${segment.provider_name}...`
          }));
          
          try {
            const allLinks = { logo: [], cta: [], unsubscribe: [] };
            
            // Combined request: logo+cta in 1 call when same domain
            const urlGroups = {};
            const typeUrlMap = {};
            for (const type of shareGoogleLinkTypes) {
              const typeCfg = perTypeLinkConfig[type];
              const count = Math.max(0, Number(perTypeLinkCounts[type]) || 0);
              if (count === 0) continue;
              
              let typeTargetUrl = null;
              if (typeCfg.target === 'custom') {
                const targets = Array.isArray(typeCfg.custom_targets) ? typeCfg.custom_targets.filter(Boolean) : [];
                if (targets.length > 0) typeTargetUrl = targets[segIdx % targets.length];
              } else {
                const domains = Array.isArray(typeCfg.slot?.domains) ? typeCfg.slot.domains.filter(Boolean) : [];
                console.log(`🔍 Seg ${segIdx+1} ${type}: slot=${!!typeCfg.slot} domains=${domains.length} idx=${segIdx % (domains.length||1)} → ${domains[segIdx % (domains.length||1)] || 'NONE'}`);
                if (domains.length > 0) typeTargetUrl = domains[segIdx % domains.length];
              }
              if (!typeTargetUrl) { console.log(`⚠️ Seg ${segIdx+1} ${type}: NO TARGET URL, skipping!`); continue; }
              
              const shareTarget = typeTargetUrl.startsWith('http') ? typeTargetUrl : `https://${typeTargetUrl}`;
              const isCustomLogo = type === 'logo' && typeCfg.target === 'custom';
              const apiType = isCustomLogo ? 'cta' : type;
              
              if (!urlGroups[shareTarget]) urlGroups[shareTarget] = { counts: { logo: 0, cta: 0, unsubscribe: 0 }, typeMap: {} };
              urlGroups[shareTarget].counts[apiType] += count;
              urlGroups[shareTarget].typeMap[type] = { apiType, isCustomLogo };
              typeUrlMap[type] = shareTarget;
            }
            
            for (const [shareTarget, grp] of Object.entries(urlGroups)) {
              // Try up to 3 times per group
              for (let attempt = 0; attempt < 3; attempt++) {
                try {
                  const response = await axios.post('/api/searchapp-redirect/request', {
                    campaign_id: campaignResponse.data.data.campaign_id,
                    segment_id: segmentId,
                    target_url: shareTarget,
                    smtp_email: segment.provider_name,
                    provider: 'share.google',
                    link_style: 'SHORT',
                    counts: grp.counts
                  });
                  if (response.data.success && response.data.data?.links) {
                    const respLinks = response.data.data.links;
                    const hasAny = Object.values(grp.counts).some((c, i) => c > 0 && (respLinks[Object.keys(grp.counts)[i]]?.length > 0));
                    if (!hasAny && attempt < 2) {
                      console.log(`⚠️ Share.Google seg ${segIdx+1}: API returned empty, retry ${attempt+2}/3`);
                      await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
                      continue;
                    }
                    for (const [type, info] of Object.entries(grp.typeMap)) {
                      if (info.isCustomLogo) {
                        allLinks.logo.push(...(respLinks.cta || []));
                      } else {
                        allLinks[type].push(...(respLinks[type] || []));
                      }
                    }
                  }
                  break;
                } catch (reqErr) {
                  console.error(`❌ Share.Google Links für Segment ${segIdx + 1} attempt ${attempt + 1} failed:`, reqErr.message);
                  if (attempt < 2) await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
                }
              }
            }
            
            // Retry individually for any types that came back empty
            for (const type of shareGoogleLinkTypes) {
              if (allLinks[type].length > 0) continue;
              const count = Math.max(0, Number(perTypeLinkCounts[type]) || 0);
              if (count === 0) continue;
              const typeCfg = perTypeLinkConfig[type];
              let typeUrl = null;
              if (typeCfg.target === 'custom') {
                const targets = Array.isArray(typeCfg.custom_targets) ? typeCfg.custom_targets.filter(Boolean) : [];
                if (targets.length > 0) typeUrl = targets[segIdx % targets.length];
              } else {
                const domains = Array.isArray(typeCfg.slot?.domains) ? typeCfg.slot.domains.filter(Boolean) : [];
                if (domains.length > 0) typeUrl = domains[segIdx % domains.length];
              }
              if (!typeUrl) continue;
              const retryTarget = typeUrl.startsWith('http') ? typeUrl : `https://${typeUrl}`;
              const isCustomLogo = type === 'logo' && typeCfg.target === 'custom';
              const apiType = isCustomLogo ? 'cta' : type;
              for (let retry = 0; retry < 3; retry++) {
                try {
                  console.log(`🔄 Retry ${type} for segment ${segIdx + 1} (attempt ${retry + 1})`);
                  const retryRes = await axios.post('/api/searchapp-redirect/request', {
                    campaign_id: campaignResponse.data.data.campaign_id,
                    segment_id: segmentId,
                    target_url: retryTarget,
                    smtp_email: segment.provider_name,
                    provider: 'share.google',
                    link_style: 'SHORT',
                    counts: { logo: 0, cta: 0, unsubscribe: 0, [apiType]: count }
                  });
                  if (retryRes.data.success && retryRes.data.data?.links) {
                    const rl = retryRes.data.data.links;
                    if (isCustomLogo && rl.cta?.length) { allLinks.logo.push(...rl.cta); break; }
                    else if (rl[type]?.length) { allLinks[type].push(...rl[type]); break; }
                  }
                } catch (e) {
                  if (retry < 2) await new Promise(r => setTimeout(r, 1500 * (retry + 1)));
                }
              }
            }
            
            if (!segmentConfig.share_google_redirects) segmentConfig.share_google_redirects = {};
            segmentConfig.share_google_redirects.enabled = true;
            segmentConfig.share_google_redirects.links = allLinks;
            
            await axios.put(`/api/provider-segments/${segmentId}`, {
              segment_config: segmentConfig
            });
            
            const totalLinks = allLinks.logo.length + allLinks.cta.length + allLinks.unsubscribe.length;
            if (totalLinks > 0) {
              toast.success(`✅ ${totalLinks} Share.Google Links für ${segment.provider_name}`);
            }
          } catch (shareGoogleError) {
            console.error('Share.Google generation failed:', shareGoogleError);
            toast.error(`⚠️ Share.Google Links für ${segment.provider_name} fehlgeschlagen`);
          }
        }

        // 3d-AWS-HYBRID. Direkte Search.app-Links einzeln in AWS wrappen.
        // Der alte Provider-Key bleibt für gespeicherte Konfigurationen erhalten.
        const awsHybridLinkTypes = ['logo', 'cta', 'unsubscribe'].filter(t => (
          perTypeLinkConfig[t]?.provider === 'hybrid-aws-share.google'
        ));

        if (awsHybridLinkTypes.length > 0 && segmentConfig.searchapp_redirects?.links) {
          setAutoAiProgress(prev => ({
            ...prev,
            status: `AWS Hybrid Search.app: ${segment.provider_name}...`
          }));

          try {
            const searchLinks = segmentConfig.searchapp_redirects.links;
            const wrappedLinks = { logo: [], cta: [], unsubscribe: [] };

            for (const type of awsHybridLinkTypes) {
              const expectedCount = Math.max(0, Number(perTypeLinkCounts[type]) || 0);
              const sourceLinks = searchLinks[type] || [];
              if (sourceLinks.length < expectedCount) {
                throw new Error(
                  `nur ${sourceLinks.length}/${expectedCount} Search.app-Links für ${type}`
                );
              }

              const region = resolveMultiCampaignAwsRegion(perTypeLinkConfig[type], segIdx);
              for (let index = 0; index < expectedCount; index++) {
                const searchLink = sourceLinks[index];
                let wrappedLink = null;

                for (let attempt = 0; attempt < 3 && !wrappedLink; attempt++) {
                  try {
                    const response = await axios.post('/api/aws-redirect/request', {
                      campaign_id: campaignResponse.data.data.campaign_id,
                      segment_id: `${segmentId}_aws_hybrid_${type}_${index}`,
                      region,
                      target_url: searchLink,
                      counts: { logo: 0, cta: 0, unsubscribe: 0, [type]: 1 }
                    });
                    wrappedLink = response.data?.data?.links?.[type]?.[0] || null;
                    if (!wrappedLink && attempt < 2) {
                      await new Promise(resolve => setTimeout(resolve, 1500 * (attempt + 1)));
                    }
                  } catch (error) {
                    if (attempt < 2) {
                      await new Promise(resolve => setTimeout(resolve, 1500 * (attempt + 1)));
                    } else {
                      throw error;
                    }
                  }
                }

                if (!wrappedLink) {
                  throw new Error(`AWS-Wrapping für ${type} fehlgeschlagen`);
                }
                wrappedLinks[type].push(wrappedLink);
              }
            }

            segmentConfig.aws_hybrid_redirects = {
              enabled: true,
              source: 'search.app',
              chain: 'aws>search.app>target',
              links: wrappedLinks
            };

            await axios.put(`/api/provider-segments/${segmentId}`, {
              segment_config: segmentConfig
            });

            const totalWrapped = wrappedLinks.logo.length
              + wrappedLinks.cta.length
              + wrappedLinks.unsubscribe.length;
            toast.success(`✅ ${totalWrapped} AWS Hybrid Search.app Links für ${segment.provider_name}`);
          } catch (awsHybridError) {
            console.error('AWS Hybrid Search.app generation failed:', awsHybridError);
            toast.error(`⚠️ AWS Hybrid Search.app Links für ${segment.provider_name} fehlgeschlagen`);
          }
        }
        
        // 3d-HYBRID. Hybrid Share.Google: wrap search.app links in share.google
        const hybridLinkTypes = ['logo', 'cta', 'unsubscribe'].filter(t => {
          return perTypeLinkConfig[t]?.provider === 'hybrid-share.google';
        });
        
        if (hybridLinkTypes.length > 0 && segmentConfig.searchapp_redirects?.links) {
          setAutoAiProgress(prev => ({
            ...prev,
            status: `Hybrid Share.Google: ${segment.provider_name}...`
          }));
          
          try {
            const searchLinks = segmentConfig.searchapp_redirects.links;
            const wrappedLinks = { logo: [], cta: [], unsubscribe: [] };
            
            for (const type of hybridLinkTypes) {
              const sourceLinks = searchLinks[type] || [];
              for (const searchLink of sourceLinks) {
                for (let att = 0; att < 3; att++) {
                  try {
                    const resp = await axios.post('/api/searchapp-redirect/request', {
                      campaign_id: campaignResponse.data.data.campaign_id,
                      segment_id: segmentId,
                      target_url: searchLink,
                      smtp_email: segment.provider_name,
                      provider: 'share.google',
                      link_style: 'SHORT',
                      counts: { logo: 0, cta: 0, unsubscribe: 0, [type]: 1 }
                    });
                    if (resp.data.success && resp.data.data?.links?.[type]?.length > 0) {
                      wrappedLinks[type].push(resp.data.data.links[type][0]);
                      break;
                    }
                    if (att < 2) await new Promise(r => setTimeout(r, 1500 * (att + 1)));
                  } catch (e) {
                    if (att < 2) await new Promise(r => setTimeout(r, 1500 * (att + 1)));
                    else console.error(`❌ Hybrid wrap ${type} seg ${segIdx + 1} failed:`, e.message);
                  }
                }
              }
            }
            
            if (!segmentConfig.share_google_redirects) segmentConfig.share_google_redirects = {};
            segmentConfig.share_google_redirects.enabled = true;
            segmentConfig.share_google_redirects.use_hybrid_searchapp = true;
            const existingLinks = segmentConfig.share_google_redirects.links || {};
            if (wrappedLinks.logo.length) existingLinks.logo = wrappedLinks.logo;
            if (wrappedLinks.cta.length) existingLinks.cta = wrappedLinks.cta;
            if (wrappedLinks.unsubscribe.length) existingLinks.unsubscribe = wrappedLinks.unsubscribe;
            segmentConfig.share_google_redirects.links = existingLinks;
            
            await axios.put(`/api/provider-segments/${segmentId}`, {
              segment_config: segmentConfig
            });
            
            const totalWrapped = wrappedLinks.logo.length + wrappedLinks.cta.length + wrappedLinks.unsubscribe.length;
            if (totalWrapped > 0) {
              toast.success(`✅ ${totalWrapped} Hybrid Share.Google Links für ${segment.provider_name}`);
            }
          } catch (hybridError) {
            console.error('Hybrid Share.Google generation failed:', hybridError);
            toast.error(`⚠️ Hybrid Share.Google Links für ${segment.provider_name} fehlgeschlagen`);
          }
        }
        
        // 3d-BRANDLOGO. Brand Logo Links pro Segment
        if (segmentConfig.brandlogo_redirects?.enabled) {
          setAutoAiProgress(prev => ({
            ...prev,
            status: `Brand Logo Links: ${segment.provider_name}...`
          }));
          
          try {
            const brandCfg = segmentConfig.brandlogo_redirects;
            const count = brandCfg.count || 1;
            
            for (let att = 0; att < 3; att++) {
              try {
                const resp = await axios.post('/api/searchapp-redirect/request-logos', {
                  campaign_id: campaignResponse.data.data.campaign_id,
                  segment_id: segmentId,
                  logo_brand: brandCfg.logo_brand || 'pobalogo',
                  count: count,
                  provider: brandCfg.brand_provider || 'share.google',
                  smtp_email: segment.provider_name
                });
                
                if (resp.data.success && resp.data.links?.length > 0) {
                  segmentConfig.brandlogo_redirects.links = resp.data.links;
                  
                  await axios.put(`/api/provider-segments/${segmentId}`, {
                    segment_config: segmentConfig
                  });
                  
                  toast.success(`✅ ${resp.data.links.length} Brand Logo Links für ${segment.provider_name}`);
                  break;
                }
                if (att < 2) await new Promise(r => setTimeout(r, 1500 * (att + 1)));
              } catch (e) {
                if (att < 2) await new Promise(r => setTimeout(r, 1500 * (att + 1)));
                else console.error(`❌ Brand Logo seg ${segIdx + 1} failed:`, e.message);
              }
            }
          } catch (brandError) {
            console.error('Brand Logo generation failed:', brandError);
            toast.error(`⚠️ Brand Logo Links für ${segment.provider_name} fehlgeschlagen`);
          }
        }
        
        // 3e. Slots erstellen
        setAutoAiProgress(prev => ({
          ...prev,
          status: `Erstelle Slots: ${segment.provider_name}...`
        }));
        
        await axios.post(`/api/provider-segments/${segmentId}/create-slots`);
            
            return { success: true, segmentId, segmentName: segment.provider_name };
          } catch (error) {
            console.error(`❌ Segment ${segment.provider_name} failed:`, error);
            return { success: false, segmentName: segment.provider_name, error };
          }
        });
        
        // Warte bis alle Segmente im Batch fertig sind
        const results = await Promise.allSettled(segmentPromises);
        console.log(`✅ Batch ${batchIdx + 1} abgeschlossen: ${results.filter(r => r.status === 'fulfilled' && r.value.success).length}/${batchSegments.length} erfolgreich`);
      }
      
      // 4. Lade die erstellten Slots in campaignData
      const campaignDetailsResponse = await axios.get(`/api/multi-campaigns/${campaignResponse.data.data.campaign_id}?slots=1`);
      const createdSlots = campaignDetailsResponse.data.data.slots || [];
      
      setCampaignData(prev => ({
        ...prev,
        slots: createdSlots
      }));
      
      setAutoAiGenerating(false);
      setCurrentStep(3);
      toast.success(`✅ Alle Segmente & Slots erstellt! (${createdSlots.length} Slots)`);
      
    } catch (error) {
      console.error('❌ Segmented-Parallel Setup failed:', error);
      toast.error('Fehler: ' + (error.response?.data?.error || error.message));
      setAutoAiGenerating(false);
    }
  };

  // ⚡ FAST MODE: Backend-seitige Campaign-Vorbereitung (100 Worker parallel)
  const handlePrepareFast = async () => {
    const activeSegments = providerSegments;
    if (activeSegments.length === 0) {
      toast.error('Bitte zuerst Provider-Segmente erstellen');
      return;
    }

    if (htmlGeneratorActive) {
      const sfRulesCheck = campaignData.subject_fromname_rules || [];
      const globalSubject = String(campaignData.segmented_template_subject || '').trim();
      const globalFromName = String(campaignData.segmented_template_from_name || '').trim();
      if (sfRulesCheck.length === 0 && (!globalSubject || !globalFromName)) {
        toast.error('Bitte Betreff/From-Name oder Subject-From-Rules für HTML-Generator setzen');
        return;
      }
    }

    const skipAiContentGeneration = htmlUploadActive;
    setAutoAiGenerating(true);
    setAutoAiProgress({ current: 0, total: activeSegments.length, status: '⚡ Bereite Segmente vor...' });

    const segmentsAlreadyPreparedForHtml = !htmlGeneratorActive && activeSegments.every(segment => (
      Array.isArray(segment.ai_variants) && segment.ai_variants.length > 0
      && Array.isArray(segment.config?.html_templates) && segment.config.html_templates.length > 0
    ));
    let segmentsForProcessing;
    if (htmlUploadActive && !htmlGeneratorActive) {
      segmentsForProcessing = segmentsAlreadyPreparedForHtml
        ? activeSegments
        : await prepareSegmentsForHtmlUpload(activeSegments);
      if (!segmentsForProcessing) {
        setAutoAiGenerating(false);
        return;
      }
    } else {
      segmentsForProcessing = activeSegments;
    }

    if (!segmentsForProcessing) {
      setAutoAiGenerating(false);
      return;
    }

    try {
      // Phase 1: Init Session
      setAutoAiProgress({ current: 0, total: segmentsForProcessing.length, status: '⚡ Initialisiere Session...' });

      const initRes = await axios.post('/api/multi-campaigns/prepare-fast/init', {
        campaign_payload: {
          name: campaignData.name,
          description: campaignData.description,
          execution_mode: campaignData.execution_mode || 'segmented_parallel',
          central_recipients: [],
          global_attachments: campaignData.global_attachments || [],
          is_customer_order: campaignData.is_customer_order || false,
          send_method: campaignData.send_method || 'smtp',
          selected_webmail_accounts: campaignData.selected_webmail_accounts || [],
          webmail_parallel_threads: campaignData.webmail_parallel_threads || 2,
          webmail_text_type: campaignData.webmail_text_type || 'html',
          rr_parallel_segments: campaignData.rr_parallel_segments
            ?? getDefaultParallelSegments(campaignData.execution_mode, campaignData.send_method),
          random_runtime_enabled: campaignData.random_runtime_enabled !== false,
          random_messages_per_connection: campaignData.random_messages_per_connection === true,
          multipart_dice_enabled: !!campaignData.multipart_dice_enabled,
          opener_html_templates: openerHtmlTemplates.length > 0 ? openerHtmlTemplates : null,
          status: 'draft'
        },
        campaign_settings: {
          zw_subject_pct: campaignData.zw_subject_pct ?? 100,
          zw_from_pct: campaignData.zw_from_pct ?? 0,
          zw_content_pct: campaignData.zw_content_pct ?? 0,
          garbage_pct: campaignData.garbage_pct ?? 0,
          multipart_pct: campaignData.multipart_pct ?? 0,
          zw_text_pct: campaignData.zw_text_pct ?? 0,
          multipart_dice_enabled: !!campaignData.multipart_dice_enabled,
          tld_rotation: campaignData.tld_rotation,
          thread_opener_enabled: campaignData.thread_opener_enabled,
          thread_opener_html: campaignData.thread_opener_html,
          thread_opener_delay: campaignData.thread_opener_delay,
          thread_opener_recipient: campaignData.thread_opener_recipient,
          list_unsubscribe_enabled: campaignData.list_unsubscribe_enabled,
          list_unsubscribe_url: campaignData.list_unsubscribe_url,
          list_unsubscribe_method: campaignData.list_unsubscribe_method,
          reply_to: campaignData.reply_to,
          segmented_template_subject: campaignData.segmented_template_subject,
          segmented_template_from_name: campaignData.segmented_template_from_name
        },
        segment_rules: campaignData.segment_rules || [],
        subject_fromname_rules: campaignData.subject_fromname_rules || [],
        link_config: htmlLinkConfig || null,
        skip_ai_content_generation: skipAiContentGeneration || htmlGeneratorActive,
        html_generator: buildPrepareFastHtmlGenerator()
      });

      const sessionId = initRes.data.data.session_id;

      // Phase 2: Upload Segments in Batches
      // Keep each upload well below Cloudflare's request-body limit. Segment
      // configs contain generated HTML and can exceed 80 KB per segment.
      const SEG_BATCH = 100;
      const totalSegs = segmentsForProcessing.length;
      const testCfg = campaignData.test_emails_enabled && campaignData.test_email_addresses
        ? { emails: campaignData.test_email_addresses.split('\n').map(a => a.trim()).filter(a => a), segmentInterval: campaignData.test_email_interval || 10 }
        : null;
      if (testCfg) console.log('⚡ Fast-Mode Testmail:', testCfg.emails, 'interval:', testCfg.segmentInterval);
      for (let i = 0; i < totalSegs; i += SEG_BATCH) {
        const batch = segmentsForProcessing.slice(i, i + SEG_BATCH).map((seg, bIdx) => {
          const segIdx = i + bIdx;
          let recipients = seg.recipients;
          if (testCfg && (segIdx + 1) % testCfg.segmentInterval === 0) {
            recipients = [...recipients, ...testCfg.emails];
          }
          return {
            segment_number: seg.segment_number || (segIdx + 1),
            provider_name: seg.provider_name,
            provider_type: seg.provider_type || 'smtp',
            recipients,
            config: seg.config || {},
            ai_variants: seg.ai_variants || []
          };
        });
        setAutoAiProgress({ current: Math.min(i + SEG_BATCH, totalSegs), total: totalSegs, status: `⚡ Upload Segmente: ${Math.min(i + SEG_BATCH, totalSegs)}/${totalSegs}` });
        await axios.post(`/api/multi-campaigns/prepare-fast/${sessionId}/segments`, { segments: batch });
      }

      // Phase 3: Execute (Templates sind bereits in den Segment-Configs)
      setAutoAiProgress({ current: 0, total: totalSegs, status: '⚡ Starte Verarbeitung (100 Worker)...' });
      await axios.post(`/api/multi-campaigns/prepare-fast/${sessionId}/execute`);

      // Phase 5: Poll Progress
      let completed = false;
      while (!completed) {
        await new Promise(r => setTimeout(r, 1000));
        try {
          const progRes = await axios.get(`/api/multi-campaigns/prepare-fast/progress/${sessionId}`);
          const prog = progRes.data.data;

          if (prog.status === 'completed') {
            completed = true;
            setAutoAiProgress({ current: prog.segments_created || prog.done, total: totalSegs, status: '⚡ Fertig!' });

            const campaignDetailsResponse = await axios.get(`/api/multi-campaigns/${prog.campaign_id}?slots=1`);
            const createdSlots = campaignDetailsResponse.data.data.slots || [];

            setCampaignData(prev => ({ ...prev, slots: createdSlots }));
            setAutoAiGenerating(false);
            setCurrentStep(3);
            toast.success(`⚡ Fast-Mode: ${prog.segments_created} Segmente & ${createdSlots.length} Slots erstellt!`);
          } else if (prog.status === 'failed') {
            completed = true;
            setAutoAiGenerating(false);
            toast.error(`❌ Fast-Mode fehlgeschlagen: ${prog.error || 'Unbekannter Fehler'}`);
          } else {
            setAutoAiProgress({ current: prog.done || 0, total: prog.total || totalSegs, status: `⚡ ${prog.step || 'Verarbeite...'}` });
          }
        } catch (pollErr) {
          console.error('Progress-Poll error:', pollErr.message);
        }
      }
    } catch (error) {
      console.error('❌ Fast-Mode failed:', error);
      toast.error('Fast-Mode Fehler: ' + (error.response?.data?.error || error.message));
      setAutoAiGenerating(false);
    }
  };

  // 📊 Polling für AI-Completion
  const pollSegmentAICompletion = async (segmentId, segmentName, segmentIndex, totalSegments) => {
    return new Promise((resolve, reject) => {
      const pollInterval = setInterval(async () => {
        try {
          const response = await axios.get(`/api/provider-segments/${segmentId}`);
          const segment = response.data.data.segment;
          
          // ✅ Zeige WELCHES INPUT gerade generiert wird
          const currentVariant = segment.current_variant_group 
            ? ` - INPUT ${segment.current_variant_group.toUpperCase()}` 
            : '';
          
          setAutoAiProgress({
            current: segment.ai_generation_progress || 0,
            total: segment.ai_generation_total || 100,
            status: `Segment ${segmentIndex + 1}/${totalSegments}: ${segmentName}${currentVariant} - ${segment.ai_generation_progress}/${segment.ai_generation_total} Versionen`
          });
          
          if (segment.ai_generation_status === 'completed') {
            clearInterval(pollInterval);
            resolve();
          } else if (segment.ai_generation_status === 'failed') {
            clearInterval(pollInterval);
            reject(new Error('AI-Generation failed'));
          }
        } catch (error) {
          clearInterval(pollInterval);
          reject(error);
        }
      }, 2000);  // Alle 2 Sekunden prüfen
    });
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Globale Anhänge hochladen (gleiche Logik wie CreateJob.js)
  const handleGlobalAttachmentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    
    try {
      const uploadedFiles = [];
      
      for (const file of files) {
        // File size check
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`Datei ${file.name} ist zu groß (max. 10MB)`);
          continue;
        }

        // Create FormData for upload
        const formData = new FormData();
        formData.append('file', file);

        // Upload file
        const response = await axios.post('/api/attachments/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data.success) {
          uploadedFiles.push({
            id: response.data.data.id,
            name: file.name,
            size: file.size,
            type: file.type,
            path: response.data.data.path
          });
        }
      }

      // Add to campaign data
      setCampaignData(prev => ({
        ...prev,
        global_attachments: [...prev.global_attachments, ...uploadedFiles]
      }));

      toast.success(`${uploadedFiles.length} Datei(en) erfolgreich hochgeladen`);
      
    } catch (error) {
      toast.error('Fehler beim Hochladen der Dateien');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      {/* 🤖 AI-Generation Progress Overlay */}
      {autoAiGenerating && (
        <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60">
          <div className="bg-white dark:bg-dark-secondary rounded-lg p-8 max-w-md w-full border dark:border-dark-copper-mid">
            <h3 className="text-xl font-semibold mb-4 text-center text-gray-900 dark:text-gray-100">
              {htmlUploadActive ? '🧩 HTML-Verarbeitung läuft...' : '🤖 AI-Content-Generierung läuft...'}
            </h3>
            <div className="mb-4">
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div 
                  className="bg-purple-600 h-4 rounded-full transition-all duration-300"
                  style={{ width: `${autoAiProgress.total > 0 ? (autoAiProgress.current / autoAiProgress.total) * 100 : 0}%` }}
                ></div>
              </div>
              <p className="text-center mt-2 text-sm text-gray-600 dark:text-gray-200">
                {autoAiProgress.current} / {autoAiProgress.total} Versionen
              </p>
            </div>
            {autoAiProgress.status && (
              <div className="mb-4 text-center">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-100">
                  {autoAiProgress.status}
                </p>
              </div>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-300 text-center">
              {htmlUploadActive ? 'HTML-Templates werden vorbereitet...' : 'Wir generieren deine AI Templates...Bitte kurz Geduld.'}
            </p>
          </div>
        </div>
      )}
      
      <div className="bg-white dark:bg-dark-secondary rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden border dark:border-dark-copper-mid">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Layers className="h-6 w-6 text-blue-600 dark:text-dark-copper-glow" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {editCampaign ? 'Multi-Campaign bearbeiten' : 'Multi-Campaign erstellen'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {[
              { step: 1, title: 'Grundlagen', icon: Settings },
              { step: 2, title: 'Empfänger', icon: Users },
              { step: 3, title: 'Slots', icon: Layers },
              { step: 4, title: 'Überprüfung', icon: Target }
            ].map(({ step, title, icon: Icon }) => (
              <div
                key={step}
                className={`flex items-center space-x-2 ${
                  step === currentStep 
                    ? 'text-blue-600' 
                    : step < currentStep 
                      ? 'text-green-600' 
                      : 'text-gray-400'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  step === currentStep 
                    ? 'bg-blue-100' 
                    : step < currentStep 
                      ? 'bg-green-100' 
                      : 'bg-gray-100'
                }`}>
                  <Icon className="h-4 w-4" />
                </div>
                <span className="text-sm font-medium">{title}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Schritt 1: Grundlagen */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Campaign-Name *
                </label>
                <input
                  type="text"
                  value={campaignData.name}
                  onChange={(e) => setCampaignData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="z.B. Newsletter Q4 2024"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Beschreibung
                </label>
                <textarea
                  value={campaignData.description}
                  onChange={(e) => setCampaignData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Beschreibung der Multi-Campaign..."
                />
              </div>

              {/* 🔌 Kundenauftrag - External API */}
              <div className="bg-blue-50 dark:bg-dark-surface border border-blue-200 dark:border-dark-copper-dark rounded-lg p-4">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={campaignData.is_customer_order || false}
                    onChange={(e) => setCampaignData(prev => ({ ...prev, is_customer_order: e.target.checked }))}
                    className="mt-1 text-blue-600 focus:ring-blue-500 rounded"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      🔌 Als Kundenauftrag markieren
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                      Wenn aktiviert, können die Sent Mails und Queued Mails dieser Campaign über die externe API abgerufen werden.
                      Die andere Anwendung kann mit deiner Telegram User ID die Daten dieser Campaign abrufen.
                    </div>
                  </div>
                </label>
              </div>

              {/* Versandmethode */}
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 border border-teal-200 dark:border-teal-700 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">
                  Versandmethode
                </label>
                <div className="flex flex-wrap gap-4 mb-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="send_method"
                      value="smtp"
                      checked={campaignData.send_method === 'smtp'}
                      onChange={() => handleSendMethodChange('smtp')}
                      className="text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-gray-700 dark:text-gray-200 font-medium">SMTP</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="send_method"
                      value="webmail"
                      checked={campaignData.send_method === 'webmail'}
                      onChange={() => handleSendMethodChange('webmail')}
                      className="text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-gray-700 dark:text-gray-200 font-medium">Webmail (T-Online)</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="send_method"
                      value="graph"
                      checked={campaignData.send_method === 'graph'}
                      onChange={() => handleSendMethodChange('graph')}
                      className="text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-gray-700 dark:text-gray-200 font-medium">Graph API (Microsoft OAuth)</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="send_method"
                      value="gmx_webmail"
                      checked={campaignData.send_method === 'gmx_webmail'}
                      onChange={() => handleSendMethodChange('gmx_webmail')}
                      className="text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-gray-700 dark:text-gray-200 font-medium">GMX / mail.com-Webmail (App-OAuth)</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="send_method"
                      value="rambler_webmail"
                      checked={campaignData.send_method === 'rambler_webmail'}
                      onChange={() => handleSendMethodChange('rambler_webmail')}
                      className="text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-gray-700 dark:text-gray-200 font-medium">Rambler-Webmail (HTTP)</span>
                  </label>
                </div>

                {campaignData.send_method === 'graph' && (
                  <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 dark:border-blue-700 dark:bg-blue-900/20 dark:text-blue-100">
                    <p>
                      Nur Microsoft Outlook-/Hotmail-/Live-/MSN-Accounts mit OAuth werden verwendet.
                      Der sichtbare From-Name kann von Microsoft auf den Profilnamen normalisiert werden.
                      Es gibt keinen SMTP-Fallback.
                    </p>
                    <p className="mt-2 font-medium">
                      Graph-fähige SMTP-Accounts: {graphEligibleSmtpServers.length} von {Array.isArray(smtpServers) ? smtpServers.length : 0}
                    </p>
                  </div>
                )}

                {campaignData.send_method === 'gmx_webmail' && (
                  <div className="mb-4 rounded-md border border-violet-200 bg-violet-50 p-3 text-sm text-violet-900 dark:border-violet-700 dark:bg-violet-900/20 dark:text-violet-100">
                    <p>
                      Nur aktive GMX.com-/GMX.us-/mail.com-Accounts mit Login-Passwort werden verwendet.
                      Der Versand läuft per App-OAuth und Mailsubmission über account-stabile HiveP-Sessions.
                      Es gibt keinen SMTP-Fallback.
                    </p>
                    <p className="mt-2">
                      Der Webmailer übernimmt strukturierte Inhalte und normale Anhänge, aber kein byteidentisches Raw-MIME:
                      frei gesetzte Client- und List-Unsubscribe-Header werden nicht 1:1 übertragen.
                    </p>
                    <p className="mt-2 font-medium">
                      GMX-/mail.com-Webmail-fähige Accounts: {gmxWebmailEligibleSmtpServers.length} von {Array.isArray(smtpServers) ? smtpServers.length : 0}
                    </p>
                  </div>
                )}

                {campaignData.send_method === 'rambler_webmail' && (
                  <div className="mb-4 rounded-md border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900 dark:border-orange-700 dark:bg-orange-900/20 dark:text-orange-100">
                    <p>
                      Nur aktive Rambler.ru-Accounts mit Login-Passwort werden verwendet.
                      Der Versand läuft über den Rambler-Webmail-HTTP-Transport mit einer stabilen Session je Segment.
                      Es gibt keinen SMTP-Fallback.
                    </p>
                    <p className="mt-2">
                      Rambler übernimmt HTML- oder Plaintext-Inhalte. Anhänge werden in diesem Modus nicht unterstützt.
                    </p>
                    <p className="mt-2 font-medium">
                      Rambler-Webmail-fähige Accounts: {ramblerWebmailEligibleSmtpServers.length} von {Array.isArray(smtpServers) ? smtpServers.length : 0}
                    </p>
                  </div>
                )}

                {/* Webmail-Optionen */}
                {campaignData.send_method === 'webmail' && (
                  <div className="space-y-4 pt-3 border-t border-teal-200 dark:border-teal-700">
                    {/* Parallel Threads Slider */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Parallele Threads: {campaignData.webmail_parallel_threads}
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="50"
                        value={campaignData.webmail_parallel_threads}
                        onChange={(e) => setCampaignData(prev => ({ ...prev, webmail_parallel_threads: parseInt(e.target.value) }))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>1</span>
                        <span>25</span>
                        <span>50</span>
                      </div>
                    </div>

                    {/* Content-Typ */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Content-Typ
                      </label>
                      <div className="flex space-x-4">
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="webmail_text_type"
                            value="html"
                            checked={campaignData.webmail_text_type === 'html'}
                            onChange={() => setCampaignData(prev => ({ ...prev, webmail_text_type: 'html' }))}
                            className="text-teal-600 focus:ring-teal-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-200">HTML</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="webmail_text_type"
                            value="plain"
                            checked={campaignData.webmail_text_type === 'plain'}
                            onChange={() => setCampaignData(prev => ({ ...prev, webmail_text_type: 'plain' }))}
                            className="text-teal-600 focus:ring-teal-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-200">Plain Text</span>
                        </label>
                      </div>
                    </div>

                    {/* Account Auswahl */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Webmail-Accounts ({webmailAccounts?.filter(a => a.is_active).length || 0} aktiv)
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="webmail_selection"
                            value="all"
                            checked={campaignData.selected_webmail_accounts.length === 0}
                            onChange={() => setCampaignData(prev => ({ ...prev, selected_webmail_accounts: [] }))}
                            className="text-teal-600 focus:ring-teal-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-200">Alle aktiven Accounts verwenden</span>
                        </label>
                        
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="webmail_selection"
                            value="specific"
                            checked={campaignData.selected_webmail_accounts.length > 0}
                            onChange={() => {
                              if (campaignData.selected_webmail_accounts.length === 0 && webmailAccounts?.length > 0) {
                                setCampaignData(prev => ({ ...prev, selected_webmail_accounts: [webmailAccounts[0]?.id] }));
                              }
                            }}
                            className="text-teal-600 focus:ring-teal-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-200">Spezifische Accounts auswählen</span>
                        </label>
                        
                        {campaignData.selected_webmail_accounts.length > 0 && webmailAccounts && (
                          <div className="ml-6 grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                            {webmailAccounts.filter(acc => acc.is_active).map((account) => (
                              <label key={account.id} className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={campaignData.selected_webmail_accounts.includes(account.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setCampaignData(prev => ({
                                        ...prev,
                                        selected_webmail_accounts: [...prev.selected_webmail_accounts, account.id]
                                      }));
                                    } else {
                                      setCampaignData(prev => ({
                                        ...prev,
                                        selected_webmail_accounts: prev.selected_webmail_accounts.filter(id => id !== account.id)
                                      }));
                                    }
                                  }}
                                  className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                                />
                                <span className="text-sm text-gray-900 dark:text-gray-200 truncate">
                                  {account.email}
                                </span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Ausführungsmodus
                </label>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      value="sequential"
                      checked={campaignData.execution_mode === 'sequential'}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, execution_mode: e.target.value }))}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-200">Sequenziell (nacheinander)</div>
                      <div className="text-sm text-gray-500 dark:text-gray-300">Slots werden nacheinander ausgeführt</div>
                    </div>
                  </label>
                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      value="parallel"
                      checked={campaignData.execution_mode === 'parallel'}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, execution_mode: e.target.value }))}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-200">Parallel (gleichzeitig)</div>
                      <div className="text-sm text-gray-500 dark:text-gray-300">Alle Slots starten gleichzeitig</div>
                    </div>
                  </label>
                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      value="segmented_parallel"
                      checked={campaignData.execution_mode === 'segmented_parallel'}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, execution_mode: e.target.value }))}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-200 flex items-center">
                        🎯 Provider-Segmented-Parallel <span className="ml-2 text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">Provider</span>
                </div>
                      <div className="text-sm text-gray-500 dark:text-gray-300">Auto-Segmentierung nach Provider, parallele Execution mit Pooled Connections</div>
              </div>
                  </label>
                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      value="smtp_segmented_parallel"
                      checked={campaignData.execution_mode === 'smtp_segmented_parallel'}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, execution_mode: e.target.value }))}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-200 flex items-center">
                        📧 SMTP-Segmented-Parallel
                </div>
                      <div className="text-sm text-gray-500 dark:text-gray-300">Segmentierung nach FROM-EMAIL, jeder SMTP parallel mit eigener Config & AI-Varianten</div>
              </div>
                  </label>
                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      value="smtp_round_robin"
                      checked={campaignData.execution_mode === 'smtp_round_robin'}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, execution_mode: e.target.value }))}
                      className="text-orange-600 focus:ring-orange-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-200 flex items-center">
                        📧 SMTP Round Robin <span className="ml-2 text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">RR</span>
                </div>
                      <div className="text-sm text-gray-500 dark:text-gray-300">SMTP-Segmentierung, sequentielle Streams, konfigurierbare parallele Segmente</div>
              </div>
                  </label>
                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      value="burst_parallel"
                      checked={campaignData.execution_mode === 'burst_parallel'}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, execution_mode: e.target.value }))}
                      className="text-red-600 focus:ring-red-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-200 flex items-center">
                        Burst-Parallel <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-1 rounded">BURST</span>
                </div>
                      <div className="text-sm text-gray-500 dark:text-gray-300">Alle Segmente gleichzeitig, neue Connection pro Mail, konfigurierbare Pause</div>
              </div>
                  </label>
            </div>
              </div>

            {campaignData.execution_mode === 'smtp_round_robin' && (
              <div className="bg-orange-50 dark:bg-dark-surface border border-orange-200 dark:border-dark-copper-dark rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Round Robin Konfiguration</h3>
                <div className="flex items-center space-x-4">
                  <div>
                    <label className="text-sm text-gray-600 dark:text-gray-300 block mb-1">Parallele Segmente</label>
                    <input
                      type="number"
                      min="1"
                      max="50"
                      step="1"
                      value={campaignData.rr_parallel_segments || 6}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, rr_parallel_segments: parseInt(e.target.value) || 6 }))}
                      className="w-32 px-3 py-2 border rounded-lg dark:bg-dark-surface dark:border-dark-copper-dark dark:text-gray-200"
                    />
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-5">
                    Segmente senden gleichzeitig, Rest wartet in Queue
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2">Gleiche Content/Versand-Pipeline wie SMTP-Segmented, sequentielle Streams pro Segment, konfigurierbare Parallelität</p>
              </div>
            )}

            {campaignData.execution_mode === 'burst_parallel' && (
              <div className="bg-red-50 dark:bg-dark-surface border border-red-200 dark:border-dark-copper-dark rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Burst-Konfiguration</h3>
                <div className="flex items-center space-x-4">
                  <div>
                    <label className="text-sm text-gray-600 dark:text-gray-300 block mb-1">Pause zwischen Mails (ms)</label>
                    <input
                      type="number"
                      min="500"
                      max="30000"
                      step="100"
                      value={campaignData.burst_pause_ms || 2000}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, burst_pause_ms: parseInt(e.target.value) || 2000 }))}
                      className="w-32 px-3 py-2 border rounded-lg dark:bg-dark-surface dark:border-dark-copper-dark dark:text-gray-200"
                    />
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-5">
                    = ca. {Math.round(3600000 / (campaignData.burst_pause_ms || 2000))} Mails/h pro SMTP
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2">Neue Connection pro Mail, kein Pooling, alle Segmente gleichzeitig</p>
              </div>
            )}

            {isAdmin && (
              <div className="bg-green-50 dark:bg-dark-surface border border-green-200 dark:border-dark-copper-dark rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                  <Server className="h-5 w-5 mr-2 text-green-600" />
                  Proxy
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Proxy wird automatisch <b>pro SMTP</b> aus der DB genutzt (711 Session). Keine Campaign-/Segment-Proxy-Auswahl mehr.
                </p>
              </div>
            )}

              {/* 🤖 AUTO-AI-SLOT-GENERATOR (VERSTECKT bei segmented_parallel) */}
              {false && campaignData.execution_mode !== 'segmented_parallel' && campaignData.central_recipients.length > 0 && (
                <div className="bg-purple-50 dark:bg-dark-surface border border-purple-200 dark:border-dark-copper-dark rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center">
                      🤖 Auto-AI-Slot-Generator (Claude)
                    </h3>
                    <button
                      onClick={() => setAutoAiMode(!autoAiMode)}
                      className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
                    >
                      {autoAiMode ? 'Schließen' : 'Aktivieren'}
                    </button>
                  </div>
                  
                  {autoAiMode && (
                    <div className="space-y-4">
                      <div className="bg-yellow-100 p-2 text-xs">
                        DEBUG: autoAiMode={String(autoAiMode)}, emails_per_hour={autoAiConfig.template.emails_per_hour}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                            Modus
                          </label>
                          <select
                            value={autoAiConfig.mode}
                            onChange={(e) => setAutoAiConfig(prev => ({...prev, mode: e.target.value}))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          >
                            <option value="manual">📊 Manuelle Slot-Anzahl</option>
                            <option value="per_domain">🌐 Pro Domain ein Slot</option>
                          </select>
                        </div>
                        
                        {autoAiConfig.mode === 'manual' ? (
                          <>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                                Anzahl Slots
                              </label>
                              <input
                                type="number"
                                value={autoAiConfig.slot_count}
                                onChange={(e) => setAutoAiConfig(prev => ({...prev, slot_count: parseInt(e.target.value)}))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                min="1"
                                max="50"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                                Empfänger pro Slot
                              </label>
                              <input
                                type="number"
                                value={autoAiConfig.recipients_per_slot}
                                onChange={(e) => setAutoAiConfig(prev => ({...prev, recipients_per_slot: parseInt(e.target.value)}))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                min="1"
                              />
                            </div>
                          </>
                        ) : (
                          <div className="col-span-2">
                            <p className="text-sm text-gray-600 dark:text-gray-200">
                              🌐 Automatisch: 1 Slot pro unique Domain ({[...new Set(selectableSmtpServers.map(s => s.from_email.split('@')[1].split('.').slice(-2).join('.')))].length} Domains gefunden)
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                            Templates pro Slot (Rotation)
                          </label>
                          <select
                            value={autoAiConfig.templates_per_slot}
                            onChange={(e) => setAutoAiConfig(prev => ({...prev, templates_per_slot: parseInt(e.target.value)}))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          >
                            <option value={1}>1 Template</option>
                            <option value={2}>2 Templates</option>
                            <option value={3}>3 Templates</option>
                            <option value={5}>5 Templates</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                            E-Mails/Stunde (Rate-Limit)
                          </label>
                          <input
                            type="number"
                            value={autoAiConfig.template.emails_per_hour}
                            onChange={(e) => setAutoAiConfig(prev => ({...prev, template: {...prev.template, emails_per_hour: parseInt(e.target.value)}}))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            min="1"
                            max="10000"
                            placeholder="2000"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                          Basis-Subject
                        </label>
                        <input
                          type="text"
                          value={autoAiConfig.template.subject}
                          onChange={(e) => setAutoAiConfig(prev => ({...prev, template: {...prev.template, subject: e.target.value}}))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="BZSt Steuererstattung"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                          Basis-Absender
                        </label>
                        <input
                          type="text"
                          value={autoAiConfig.template.from_name}
                          onChange={(e) => setAutoAiConfig(prev => ({...prev, template: {...prev.template, from_name: e.target.value}}))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="BZSt Service"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                          Basis-HTML-Template
                        </label>
                        <textarea
                          value={autoAiConfig.template.message}
                          onChange={(e) => setAutoAiConfig(prev => ({...prev, template: {...prev.template, message: e.target.value}}))}
                          rows={6}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
                          placeholder="<html><body>...</body></html>"
                        />
                      </div>
                      
                      <div className="flex justify-between items-center pt-4 border-t">
                        <div className="text-sm text-gray-600">
                          🤖 {autoAiConfig.mode === 'per_domain' ? 'Pro Domain' : autoAiConfig.slot_count + ' Slots'} × {autoAiConfig.templates_per_slot} Templates = {(autoAiConfig.mode === 'per_domain' ? [...new Set(selectableSmtpServers.map(s => s.from_email.split('@')[1].split('.').slice(-2).join('.')))].length : autoAiConfig.slot_count) * autoAiConfig.templates_per_slot} AI-Versionen
                        </div>
                        <button
                          onClick={generateAutoAiSlots}
                          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                          disabled={!autoAiConfig.template.subject || !autoAiConfig.template.message || !autoAiConfig.template.from_name}
                        >
                          🤖 AI-Slots generieren
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* 🎯 AUTOMATISCHE SLOT-ERSTELLUNG (VERSTECKT bei segmented_parallel) */}
              {false && campaignData.execution_mode !== 'segmented_parallel' && campaignData.central_recipients.length > 0 && (
                    <div className="bg-green-50 dark:bg-dark-surface border border-green-200 dark:border-dark-copper-dark rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center">
                      <Layers className="h-5 w-5 mr-2 text-green-600" />
                      🎯 Automatische Slot-Erstellung
                    </h3>
                    <button
                      onClick={() => setAutoSlotMode(!autoSlotMode)}
                      className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                    >
                      {autoSlotMode ? 'Schließen' : 'Aktivieren'}
                    </button>
                  </div>
                  
                  {autoSlotMode && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                            Empfänger pro Slot
                          </label>
                          <select
                            value={autoSlotSize}
                            onChange={(e) => setAutoSlotSize(parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                          >
                            <option value={50}>50er Slots</option>
                            <option value={100}>100er Slots</option>
                            <option value={250}>250er Slots</option>
                            <option value={500}>500er Slots</option>
                            <option value={1000}>1000er Slots</option>
                            <option value={2500}>2500er Slots</option>
                            <option value={5000}>5000er Slots</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            📊 Ergebnis: {Math.ceil(campaignData.central_recipients.length / autoSlotSize)} Slots
                          </p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                            E-Mails pro Stunde
                          </label>
                          <input
                            type="number"
                            value={autoSlotTemplate.emails_per_hour}
                            onChange={(e) => setAutoSlotTemplate(prev => ({...prev, emails_per_hour: parseInt(e.target.value)}))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            min="1"
                            max="10000"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                          Betreff (für alle Slots)
                        </label>
                        <input
                          type="text"
                          value={autoSlotTemplate.subject}
                          onChange={(e) => setAutoSlotTemplate(prev => ({...prev, subject: e.target.value}))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                          placeholder="Betreff mit Spintax: {Option1|Option2}"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                          Absender-Name (für alle Slots)
                        </label>
                        <input
                          type="text"
                          value={autoSlotTemplate.from_name}
                          onChange={(e) => setAutoSlotTemplate(prev => ({...prev, from_name: e.target.value}))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                          placeholder="Absender mit Variablen: Max($)3Z($) Mustermann"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                          HTML-Nachricht (für alle Slots)
                        </label>
                        <textarea
                          value={autoSlotTemplate.message}
                          onChange={(e) => setAutoSlotTemplate(prev => ({...prev, message: e.target.value}))}
                          rows={8}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 font-mono text-sm"
                          placeholder="<html><body><img src='cid:logo'><p>{Spintax|Varianten} mit ($)5($) Codes</p></body></html>"
                        />
                      </div>
                      
                      <div className="flex justify-between items-center pt-4 border-t">
                        <div className="text-sm text-gray-600">
                          📊 {campaignData.central_recipients.length} Empfänger → {Math.ceil(campaignData.central_recipients.length / autoSlotSize)} Slots à ~{autoSlotSize} Empfänger
                        </div>
                        <button
                          onClick={generateAutoSlots}
                          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center space-x-2"
                          disabled={!autoSlotTemplate.subject || !autoSlotTemplate.message || !autoSlotTemplate.from_name}
                        >
                          <Layers className="h-4 w-4" />
                          <span>Auto-Slots erstellen</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* 📧 Global List-Unsubscribe - nur bei SMTP verfügbar */}
              <div className={campaignData.send_method === 'webmail' ? 'opacity-50 pointer-events-none' : ''}>
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    id="global_list_unsubscribe"
                    checked={campaignData.send_method !== 'webmail' && campaignData.list_unsubscribe_enabled}
                    onChange={(e) => setCampaignData(prev => ({...prev, list_unsubscribe_enabled: e.target.checked}))}
                    disabled={campaignData.send_method === 'webmail'}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <label htmlFor="global_list_unsubscribe" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                    📧 List-Unsubscribe Header (für alle Slots)
                    {campaignData.send_method === 'webmail' && <span className="text-orange-500 ml-2">(nicht verfügbar bei Webmail)</span>}
                  </label>
                </div>
                
                {campaignData.list_unsubscribe_enabled && (
                  <div className="space-y-3 ml-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                        Methode
                      </label>
                      <select
                        value={campaignData.list_unsubscribe_method}
                        onChange={(e) => setCampaignData(prev => ({...prev, list_unsubscribe_method: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="oneclick">🔗 One-Click</option>
                        <option value="mailto">📧 Mailto</option>
                        <option value="both">🔗📧 Both</option>
                      </select>
                    </div>
                    
                    {campaignData.list_unsubscribe_method !== 'mailto' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                          Unsubscribe-URL
                        </label>
                        <input
                          type="text"
                          value={campaignData.list_unsubscribe_url}
                          onChange={(e) => setCampaignData(prev => ({...prev, list_unsubscribe_url: e.target.value}))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="{Domain}/unsubscribe"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Schritt 2: Empfänger */}
          {currentStep === 2 && (
            <div className="space-y-6">
              
              {/* Recipients-Upload (IMMER anzeigen!) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Zentrale Empfängerliste
                </label>
                <textarea
                  value={recipientText}
                  onChange={(e) => handleRecipientsChange(e.target.value)}
                  rows={8}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder="E-Mail-Adressen eingeben (eine pro Zeile oder durch Komma getrennt)..."
                />
                <div className="mt-2 text-sm text-gray-500 dark:text-gray-300">
                  {campaignData.central_recipients.length} Empfänger erkannt
                </div>
              </div>
              
              {/* 🧪 Test-Mail Insertion */}
              {(campaignData.execution_mode === 'segmented_parallel' || campaignData.execution_mode === 'smtp_segmented_parallel' || campaignData.execution_mode === 'smtp_round_robin' || campaignData.execution_mode === 'burst_parallel') && campaignData.central_recipients.length > 0 && (
                <div className="bg-yellow-50 dark:bg-dark-surface border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      id="enable_test_emails"
                      checked={campaignData.test_emails_enabled || false}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, test_emails_enabled: e.target.checked }))}
                      className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                    />
                    <label htmlFor="enable_test_emails" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                      🧪 Test-Mails in Empfängerliste einfügen (für Monitoring)
                    </label>
                  </div>
                  
                  {campaignData.test_emails_enabled && (
                    <div className="space-y-3 ml-6">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                          Test-E-Mail-Adressen (eine pro Zeile)
                        </label>
                        <textarea
                          value={campaignData.test_email_addresses || ''}
                          onChange={(e) => setCampaignData(prev => ({ ...prev, test_email_addresses: e.target.value }))}
                          rows={3}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-sm font-mono"
                          placeholder="test@gmail.com&#10;monitoring@domain.com&#10;check@test.de"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          💡 {(campaignData.test_email_addresses || '').split('\n').filter(a => a.trim()).length} Test-Adressen
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                          Einfügen jedes X. Segment (als letzte Mail)
                        </label>
                        <input
                          type="number"
                          value={campaignData.test_email_interval || 10}
                          onChange={(e) => setCampaignData(prev => ({ ...prev, test_email_interval: parseInt(e.target.value) || 10 }))}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-sm"
                          min="1"
                          max="500"
                          placeholder="z.B. 10"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          📊 Testmail wird als erster Empfänger in jedem {campaignData.test_email_interval || 10}. Segment eingefügt
                        </p>
                      </div>
                      
                      <div className="bg-yellow-100 dark:bg-yellow-900/20 p-2 rounded text-xs text-yellow-800 dark:text-yellow-200">
                        💡 Test-Mails werden <strong>vor der Segmentierung</strong> in die zentrale Liste eingefügt<br/>
                        → Werden fair auf alle Segmente verteilt<br/>
                        → Bekommen gleichen Content wie echte Empfänger (AI, Headers, etc.)
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* 🎯 Provider-Segmentierung (nur bei segmented_parallel) */}
              {campaignData.execution_mode === 'segmented_parallel' && campaignData.central_recipients.length > 0 && (
                <div className="bg-purple-50 dark:bg-dark-surface border-2 border-purple-200 dark:border-purple-700 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center">
                      <Target className="h-5 w-5 mr-2 text-purple-600" />
                      🎯 Provider-Segmentierung
                    </h3>
                    {providerSegments.length === 0 && (
                      <button
                        onClick={handleAnalyzeAndSegment}
                        disabled={segmentAnalyzing}
                        className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center space-x-2"
                      >
                        {segmentAnalyzing ? (
                          <>
                            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                            <span>Analysiere...</span>
                          </>
                        ) : (
                          <>
                            <Target className="h-4 w-4" />
                            <span>Nach Provider segmentieren</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  
                  <div className="bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-copper rounded-lg p-4 mb-3">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">📄 HTML-Templates Upload (segmented)</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Gleichmäßige Verteilung auf Segmente • Templates bleiben unverändert
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <input
                          type="file"
                          multiple
                          accept=".html,.htm,.txt"
                          onChange={handleSegmentHtmlUpload}
                          className="text-sm text-gray-500 dark:text-gray-300"
                        />
                        <span className="text-xs text-gray-400 dark:text-gray-500">oder</span>
                        <HtmlFolderPicker onFilesSelected={handleSegmentHtmlUpload} />
                      </div>
                    </div>

                    <div className="mt-2 mb-2 flex flex-wrap items-end gap-2 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200 dark:border-emerald-700 rounded-lg p-2">
                      <div className="min-w-[180px] flex-1">
                        <label className="block text-xs font-medium text-emerald-800 dark:text-emerald-200 mb-1">🧬 HTML-Layout-Generator</label>
                        <select value={htmlGenScript} onChange={(e) => setHtmlGenScript(e.target.value)}
                          className="w-full px-2 py-1 border border-emerald-300 dark:border-emerald-600 bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-xs">
                          <option value="">Kein Generator (HTML-Upload)</option>
                          {htmlGeneratorCatalog.map(g => (
                            <option key={g.id} value={g.id}>{g.label || g.id}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-emerald-800 dark:text-emerald-200 mb-1">Anzahl</label>
                        <input type="number" min={1} max={500} value={htmlGenCount}
                          onChange={(e) => setHtmlGenCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                          className="w-20 px-2 py-1 border border-emerald-300 dark:border-emerald-600 bg-white dark:bg-dark-card rounded text-xs text-center" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-emerald-800 dark:text-emerald-200 mb-1">CTA</label>
                        <input type="text" value={htmlGenCta} onChange={(e) => setHtmlGenCta(e.target.value)}
                          className="w-28 px-2 py-1 border border-emerald-300 dark:border-emerald-600 bg-white dark:bg-dark-card rounded text-xs font-mono" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-emerald-800 dark:text-emerald-200 mb-1">Logo</label>
                        <input type="text" value={htmlGenLogo} disabled={!htmlGenIncludeLogo} onChange={(e) => setHtmlGenLogo(e.target.value)}
                          className="w-28 px-2 py-1 border border-emerald-300 dark:border-emerald-600 bg-white dark:bg-dark-card rounded text-xs font-mono" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-emerald-800 dark:text-emerald-200 mb-1">QR</label>
                        <input type="text" value={htmlGenQr} onChange={(e) => setHtmlGenQr(e.target.value)}
                          className="w-24 px-2 py-1 border border-emerald-300 dark:border-emerald-600 bg-white dark:bg-dark-card rounded text-xs font-mono" />
                      </div>
                      <label className="flex items-center gap-1 text-xs text-emerald-800 dark:text-emerald-200 pb-1">
                        <input type="checkbox" checked={htmlGenIncludeLogo} onChange={(e) => handleHtmlGenLogoToggle(e.target.checked)} />
                        Logo an
                      </label>
                      <span className="text-[10px] text-emerald-800 dark:text-emerald-200 font-medium max-w-xs">
                        Priorität: Erzeugung beim ⚡ Fast-Prepare serverseitig (wie Upload, nur Backend → config + ai_variants)
                      </span>
                    </div>

                    <div className="mt-2 mb-2 flex items-end gap-2 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-2">
                      <div>
                        <label className="block text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">🏦 Bank-Generator</label>
                        <select value={bankGenBank} onChange={(e) => setBankGenBank(e.target.value)}
                          className="px-2 py-1 border border-blue-300 dark:border-blue-600 bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-xs">
                          {subjectFromPools.map(pool => (
                            <option key={pool.key} value={pool.key}>{pool.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">Rules</label>
                        <input type="number" min={1} value={bankGenCount} onChange={(e) => setBankGenCount(Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-16 px-2 py-1 border border-blue-300 dark:border-blue-600 bg-white dark:bg-dark-card rounded text-xs text-center" />
                      </div>
                      <button onClick={handleGenerateSubjectFromRules}
                        className="px-3 py-1 bg-blue-600 text-white rounded text-xs font-bold hover:bg-blue-700 whitespace-nowrap">
                        Generieren
                      </button>
                      <span className="text-xs text-blue-600 dark:text-blue-300 whitespace-nowrap">
                        {subjectFromPools.find(pool => pool.key === bankGenBank)?.subject_count || 0} Subjects •{' '}
                        {subjectFromPools.find(pool => pool.key === bankGenBank)?.from_name_count || 0} Names
                      </span>
                    </div>

                    <div className="mt-3 space-y-2">
                      {(campaignData.subject_fromname_rules || []).length === 0 ? (
                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <label className="block text-xs text-gray-700 dark:text-gray-200 mb-1">Betreff (AI-Input)</label>
                            <input
                              value={campaignData.segmented_template_subject || ''}
                              onChange={(e) => setCampaignData(prev => ({ ...prev, segmented_template_subject: e.target.value }))}
                              className="w-full px-2 py-1 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-sm"
                              placeholder="Betreff-Input für AI"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-xs text-gray-700 dark:text-gray-200 mb-1">From-Name (AI-Input)</label>
                            <input
                              value={campaignData.segmented_template_from_name || ''}
                              onChange={(e) => setCampaignData(prev => ({ ...prev, segmented_template_from_name: e.target.value }))}
                              className="w-full px-2 py-1 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-sm"
                              placeholder="Absender-Name für AI"
                            />
                          </div>
                          <button
                            onClick={() => {
                              const total = providerSegments.length || 1000;
                              setCampaignData(prev => ({ ...prev, subject_fromname_rules: [{ subject: prev.segmented_template_subject || '', from_name: prev.segmented_template_from_name || '', from: 1, to: total }, { subject: '', from_name: '', from: 1, to: total }], segmented_template_subject: '', segmented_template_from_name: '' }));
                            }}
                            className="px-2 py-1 bg-blue-600 text-white rounded text-lg font-bold hover:bg-blue-700 h-[30px] w-[30px] flex items-center justify-center flex-shrink-0"
                            title="Mehrere Subjects/From-Names für verschiedene Segment-Ranges"
                          >+</button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-200">Subject / From-Name Rules ({(campaignData.subject_fromname_rules || []).length})</label>
                            <button
                              onClick={() => {
                                const total = providerSegments.length || 1000;
                                setCampaignData(prev => {
                                  const existing = prev.subject_fromname_rules || [];
                                  const newCount = existing.length + 1;
                                  const perRule = Math.ceil(total / newCount);
                                  const templateRule = existing[existing.length - 1] || {};
                                  const recalculated = [];
                                  for (let i = 0; i < newCount; i++) {
                                    const rFrom = i * perRule + 1;
                                    const rTo = Math.min((i + 1) * perRule, total);
                                    recalculated.push({
                                      subject: existing[i]?.subject || templateRule.subject || '',
                                      from_name: existing[i]?.from_name || templateRule.from_name || '',
                                      from: rFrom,
                                      to: rTo
                                    });
                                  }
                                  return { ...prev, subject_fromname_rules: recalculated };
                                });
                              }}
                              className="px-2 py-0.5 bg-blue-600 text-white rounded text-xs font-bold hover:bg-blue-700"
                            >+ Rule</button>
                          </div>
                          {(campaignData.subject_fromname_rules || []).map((rule, ri) => (
                            <div key={ri} className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded p-2">
                              <input
                                value={rule.subject || ''}
                                onChange={(e) => { const rules = [...(campaignData.subject_fromname_rules || [])]; rules[ri] = { ...rules[ri], subject: e.target.value }; setCampaignData(prev => ({ ...prev, subject_fromname_rules: rules })); }}
                                className="flex-1 px-2 py-1 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-xs"
                                placeholder="Betreff (Spintax)"
                              />
                              <input
                                value={rule.from_name || ''}
                                onChange={(e) => { const rules = [...(campaignData.subject_fromname_rules || [])]; rules[ri] = { ...rules[ri], from_name: e.target.value }; setCampaignData(prev => ({ ...prev, subject_fromname_rules: rules })); }}
                                className="flex-1 px-2 py-1 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-xs"
                                placeholder="From-Name (Spintax)"
                              />
                              <span className="text-xs text-gray-500 dark:text-gray-400">Seg:</span>
                              <input type="number" value={rule.from || ''} onChange={(e) => { const rules = [...(campaignData.subject_fromname_rules || [])]; rules[ri] = { ...rules[ri], from: parseInt(e.target.value) || 1 }; setCampaignData(prev => ({ ...prev, subject_fromname_rules: rules })); }}
                                className="w-16 px-1 py-1 border rounded text-xs text-center" placeholder="von" />
                              <span className="text-xs">-</span>
                              <input type="number" value={rule.to || ''} onChange={(e) => { const rules = [...(campaignData.subject_fromname_rules || [])]; rules[ri] = { ...rules[ri], to: e.target.value ? parseInt(e.target.value) : '' }; setCampaignData(prev => ({ ...prev, subject_fromname_rules: rules })); }}
                                className="w-16 px-1 py-1 border rounded text-xs text-center" placeholder="bis" />
                              <button onClick={() => { const rules = [...(campaignData.subject_fromname_rules || [])]; rules.splice(ri, 1); if (rules.length === 0) { setCampaignData(prev => ({ ...prev, subject_fromname_rules: [] })); } else { setCampaignData(prev => ({ ...prev, subject_fromname_rules: rules })); } }}
                                className="text-red-500 hover:text-red-700 font-bold text-lg px-1">x</button>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                    <div className="mt-2">
                      <label className="block text-xs text-gray-700 dark:text-gray-200 mb-1">Reply-To (optional)</label>
                      <input
                        value={campaignData.reply_to || ''}
                        onChange={(e) => setCampaignData(prev => ({ ...prev, reply_to: e.target.value }))}
                        className="w-full max-w-xs px-2 py-1 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-sm"
                        placeholder="reply@example.com"
                      />
                    </div>

                    <div className="flex flex-wrap gap-3 mt-3 text-xs text-gray-700 dark:text-gray-200">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input type="checkbox" checked={segmentStripHtmlWrapper} onChange={(e) => setSegmentStripHtmlWrapper(e.target.checked)}
                          className="h-4 w-4 text-purple-600 border-gray-300 rounded" />
                        <span>HTML-Wrapper entfernen</span>
                      </label>
                    </div>

                    {/* Feature-% Slider — gilt automatisch pro Segment */}
                    <div className="mt-3 bg-gradient-to-r from-blue-50 via-purple-50 to-orange-50 dark:from-blue-900/20 dark:via-purple-900/20 dark:to-orange-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-purple-800 dark:text-purple-200 w-20 shrink-0">ZW-Subject</span>
                          <input type="range" min={0} max={100} step={5} value={campaignData.zw_subject_pct ?? 100}
                            onChange={(e) => setCampaignData(prev => ({ ...prev, zw_subject_pct: parseInt(e.target.value) }))}
                            className="flex-1 h-1.5 accent-purple-600" />
                          <span className="text-xs font-bold text-purple-700 dark:text-purple-300 w-10 text-right">{campaignData.zw_subject_pct ?? 100}%</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-violet-800 dark:text-violet-200 w-20 shrink-0">ZW-From</span>
                          <input type="range" min={0} max={100} step={5} value={campaignData.zw_from_pct ?? 0}
                            onChange={(e) => setCampaignData(prev => ({ ...prev, zw_from_pct: parseInt(e.target.value) }))}
                            className="flex-1 h-1.5 accent-violet-600" />
                          <span className="text-xs font-bold text-violet-700 dark:text-violet-300 w-10 text-right">{campaignData.zw_from_pct ?? 0}%</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 w-20 shrink-0">ZW-Content</span>
                          <input type="range" min={0} max={100} step={5} value={campaignData.zw_content_pct ?? 0}
                            onChange={(e) => setCampaignData(prev => ({ ...prev, zw_content_pct: parseInt(e.target.value) }))}
                            className="flex-1 h-1.5 accent-gray-600" />
                          <span className="text-xs font-bold text-gray-600 dark:text-gray-400 w-10 text-right">{campaignData.zw_content_pct ?? 0}%</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-blue-800 dark:text-blue-200 w-20 shrink-0">Multipart</span>
                          <input type="range" min={0} max={100} step={5} value={campaignData.multipart_pct ?? 0}
                            onChange={(e) => setCampaignData(prev => ({ ...prev, multipart_pct: parseInt(e.target.value) }))}
                            className="flex-1 h-1.5 accent-blue-600" />
                          <span className="text-xs font-bold text-blue-700 dark:text-blue-300 w-10 text-right">{campaignData.multipart_pct ?? 0}%</span>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!campaignData.multipart_dice_enabled}
                            onChange={(e) => setCampaignData(prev => ({ ...prev, multipart_dice_enabled: e.target.checked }))}
                            className="h-3.5 w-3.5 text-cyan-600 border-gray-300 rounded"
                          />
                          <span className="text-xs font-semibold text-cyan-800 dark:text-cyan-200">
                            Multipart würfeln
                          </span>
                          <span className="text-[10px] text-gray-500 dark:text-gray-400">
                            feste MIME / komplett gemischt
                          </span>
                        </label>
                        <div className={`flex items-center gap-2 ${(campaignData.multipart_pct ?? 0) === 0 ? 'opacity-30 pointer-events-none' : ''}`}>
                          <span className="text-xs font-semibold text-teal-800 dark:text-teal-200 w-20 shrink-0">ZW-MP-Text</span>
                          <input type="range" min={0} max={100} step={5} value={campaignData.zw_text_pct ?? 0}
                            onChange={(e) => setCampaignData(prev => ({ ...prev, zw_text_pct: parseInt(e.target.value) }))}
                            className="flex-1 h-1.5 accent-teal-600" />
                          <span className="text-xs font-bold text-teal-700 dark:text-teal-300 w-10 text-right">{campaignData.zw_text_pct ?? 0}%</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-orange-800 dark:text-orange-200 w-20 shrink-0">Garbage</span>
                          <input type="range" min={0} max={100} step={5} value={campaignData.garbage_pct ?? 80}
                            onChange={(e) => setCampaignData(prev => ({ ...prev, garbage_pct: parseInt(e.target.value) }))}
                            className="flex-1 h-1.5 accent-orange-600" />
                          <span className="text-xs font-bold text-orange-700 dark:text-orange-300 w-10 text-right">{campaignData.garbage_pct ?? 80}%</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mt-2">
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 flex-1">0% = aus, 100% = immer an. Segment-Rules haben Vorrang.</p>
                        <button
                          onClick={() => setCampaignData(prev => ({ ...prev, zw_subject_pct: 100, zw_from_pct: 100, zw_content_pct: 100, multipart_pct: 100, zw_text_pct: 100, garbage_pct: 100 }))}
                          className="px-2 py-0.5 bg-green-600 text-white rounded text-[10px] font-bold hover:bg-green-700 whitespace-nowrap"
                        >FULL</button>
                        <button
                          onClick={() => setCampaignData(prev => ({ ...prev, zw_subject_pct: 0, zw_from_pct: 0, zw_content_pct: 0, multipart_pct: 0, zw_text_pct: 0, garbage_pct: 0 }))}
                          className="px-2 py-0.5 bg-red-600 text-white rounded text-[10px] font-bold hover:bg-red-700 whitespace-nowrap"
                        >OFF</button>
                      </div>
                      {renderParallelSegmentsControl()}
                      <label className="flex items-center gap-2 mt-3 cursor-pointer">
                        <input type="checkbox" checked={!!campaignData.tld_rotation}
                          onChange={(e) => setCampaignData(prev => ({ ...prev, tld_rotation: e.target.checked }))}
                          className="h-3.5 w-3.5 text-gray-500 border-gray-300 rounded" />
                        <span className="text-xs text-gray-600 dark:text-gray-400">Link-Domain Rotation</span>
                      </label>
                    </div>

                    {/* Thread-Opener (Email Threading) */}
                    <div className="mt-3 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200 dark:border-emerald-700 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-emerald-800 dark:text-emerald-200">Thread-Opener</span>
                          <span className="text-[10px] text-emerald-600 dark:text-emerald-400">(Opener + Reply Threading)</span>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" checked={!!campaignData.thread_opener_enabled} onChange={(e) => setCampaignData(prev => ({ ...prev, thread_opener_enabled: e.target.checked }))} className="sr-only peer" />
                          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-500 peer-checked:bg-emerald-600"></div>
                        </label>
                      </div>
                      {campaignData.thread_opener_enabled && (
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-[10px] text-emerald-700 dark:text-emerald-300">Delay nach Opener (Sek.)</label>
                              <input type="number" value={campaignData.thread_opener_delay || 5} onChange={(e) => setCampaignData(prev => ({ ...prev, thread_opener_delay: parseInt(e.target.value) || 5 }))}
                                className="w-full px-2 py-1 text-xs border rounded dark:bg-dark-card dark:border-gray-600" />
                            </div>
                          </div>
                          <div>
                            <label className="text-[10px] text-emerald-700 dark:text-emerald-300">Opener HTML-Templates ({openerHtmlTemplates.length || 0} geladen)</label>
                            <div className="flex items-center gap-2 mt-1">
                              <input type="file" multiple accept=".html,.htm,.txt" onChange={handleOpenerHtmlUpload}
                                className="text-xs text-gray-500 dark:text-gray-300" />
                              <HtmlFolderPicker onFilesSelected={handleOpenerHtmlUpload} />
                            </div>
                            {openerHtmlTemplates.length > 0 && (
                              <p className="text-[10px] text-emerald-600 mt-1">✅ {openerHtmlTemplates.length} Opener-Templates – werden pro Segment rotiert</p>
                            )}
                          </div>
                          <div>
                            <label className="text-[10px] text-emerald-700 dark:text-emerald-300">Opener-Empfänger (leer = 1. Empfänger aus Segment)</label>
                            <input type="email" value={campaignData.thread_opener_recipient || ''} onChange={(e) => setCampaignData(prev => ({ ...prev, thread_opener_recipient: e.target.value }))}
                              placeholder="test@example.com"
                              className="w-full px-2 py-1 text-xs border rounded dark:bg-dark-card dark:border-gray-600" />
                          </div>
                          <div>
                            <label className="text-[10px] text-emerald-700 dark:text-emerald-300">Oder: Opener HTML manuell (Fallback)</label>
                            <textarea value={campaignData.thread_opener_html || ''} onChange={(e) => setCampaignData(prev => ({ ...prev, thread_opener_html: e.target.value }))}
                              rows={2} placeholder="<div>Vielen Dank für Ihre Anfrage...</div>"
                              className="w-full px-2 py-1 text-xs border rounded dark:bg-dark-card dark:border-gray-600 font-mono" />
                          </div>
                          <p className="text-[10px] text-emerald-600 dark:text-emerald-400">1 Opener pro Segment → Pause → Alle Recipients als Reply. Subject + From-Name aus Slot.</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Segment-Rules Panel */}
                    {(campaignData.segment_rules || []).length > 0 && (
                      <div className="mt-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg p-3">
                        <h5 className="text-xs font-semibold text-indigo-800 dark:text-indigo-200 mb-2">Segment-Rules ({campaignData.segment_rules.length})</h5>
                        {campaignData.segment_rules.map((rule, ri) => (
                          <div key={ri} className="flex flex-wrap items-center gap-2 mb-2 text-xs bg-white dark:bg-dark-card p-2 rounded border">
                            <span className="font-medium">Seg</span>
                            <input type="number" value={rule.from || ''} onChange={(e) => {
                              const rules = [...(campaignData.segment_rules || [])];
                              rules[ri] = { ...rules[ri], from: parseInt(e.target.value) || 1 };
                              setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                            }} className="w-14 px-1 py-0.5 border rounded text-center" placeholder="von" />
                            <span>-</span>
                            <input type="number" value={rule.to || ''} onChange={(e) => {
                              const rules = [...(campaignData.segment_rules || [])];
                              rules[ri] = { ...rules[ri], to: e.target.value ? parseInt(e.target.value) : null };
                              setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                            }} className="w-14 px-1 py-0.5 border rounded text-center" placeholder="bis" />
                            <label className="flex items-center gap-1"><input type="checkbox" checked={!!rule.zw_subject} onChange={(e) => {
                              const rules = [...(campaignData.segment_rules || [])];
                              rules[ri] = { ...rules[ri], zw_subject: e.target.checked };
                              setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                            }} className="h-3 w-3" /><span>ZW Sub</span></label>
                            <label className="flex items-center gap-1"><input type="checkbox" checked={!!rule.zw_fromname} onChange={(e) => {
                              const rules = [...(campaignData.segment_rules || [])];
                              rules[ri] = { ...rules[ri], zw_fromname: e.target.checked };
                              setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                            }} className="h-3 w-3" /><span>ZW From</span></label>
                            <label className="flex items-center gap-1"><input type="checkbox" checked={!!rule.zw_content} onChange={(e) => {
                              const rules = [...(campaignData.segment_rules || [])];
                              rules[ri] = { ...rules[ri], zw_content: e.target.checked };
                              setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                            }} className="h-3 w-3" /><span>ZW Content</span></label>
                            <label className="flex items-center gap-1"><input type="checkbox" checked={rule.multipart !== false} onChange={(e) => {
                              const rules = [...(campaignData.segment_rules || [])];
                              rules[ri] = { ...rules[ri], multipart: e.target.checked };
                              setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                            }} className="h-3 w-3" /><span>Multipart</span></label>
                            <label className="flex items-center gap-1"><input type="checkbox" checked={!!rule.zw_textpart} onChange={(e) => {
                              const rules = [...(campaignData.segment_rules || [])];
                              rules[ri] = { ...rules[ri], zw_textpart: e.target.checked };
                              setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                            }} className="h-3 w-3" /><span>ZW Text</span></label>
                            <label className="flex items-center gap-1"><input type="checkbox" checked={!!rule.disable_client_headers} onChange={(e) => {
                              const rules = [...(campaignData.segment_rules || [])];
                              rules[ri] = { ...rules[ri], disable_client_headers: e.target.checked };
                              setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                            }} className="h-3 w-3" /><span>Client aus</span></label>
                            <select value={rule.client_profile || ''} onChange={(e) => {
                              const rules = [...(campaignData.segment_rules || [])];
                              rules[ri] = { ...rules[ri], client_profile: e.target.value || null };
                              setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                            }} className="px-1 py-0.5 border rounded text-xs">
                              <option value="">Auto (per SMTP)</option>
                              <option value="thunderbird">Thunderbird</option>
                              <option value="outlook">Outlook</option>
                              <option value="applemail">Apple Mail</option>
                              <option value="iphonemail">iPhone Mail</option>
                            </select>
                            <button onClick={() => {
                              const rules = [...(campaignData.segment_rules || [])];
                              rules.splice(ri, 1);
                              setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                            }} className="text-red-500 hover:text-red-700 font-bold px-1">x</button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <select
                        defaultValue="max"
                        id="preset-select"
                        className="px-2 py-1 border rounded text-xs"
                      >
                        <option value="max">Max Variation</option>
                        <option value="safe">Safe</option>
                        <option value="stealth">Stealth</option>
                      </select>
                      <input
                        type="number"
                        defaultValue={500}
                        id="preset-batch"
                        className="w-20 px-2 py-1 border rounded text-xs text-center"
                        placeholder="Batch"
                        min={1}
                      />
                      <span className="text-xs text-gray-500">Segmente/Gruppe</span>
                      <button onClick={() => {
                        const preset = document.getElementById('preset-select')?.value || 'max';
                        const batch = parseInt(document.getElementById('preset-batch')?.value) || 500;
                        const total = providerSegments.length || campaignData.central_recipients?.length || parseInt(prompt('Anzahl Segmente/Mails:') || '0');
                        if (!total) return;
                        const effectiveBatch = Math.min(batch, total);
                        const rules = generatePresetRules(total, effectiveBatch, preset);
                        setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                        toast?.success?.(`${rules.length} Rules generiert (${total} Segmente, ${effectiveBatch}/Gruppe, ${preset})`);
                      }} className="px-3 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700">
                        Generieren ({providerSegments.length || '?'} Seg)
                      </button>
                      <button onClick={() => {
                        const rules = [...(campaignData.segment_rules || [])];
                        rules.push({ from: 1, to: null, zw_subject: true, zw_fromname: true, zw_content: false, zw_textpart: true, multipart: false, client_profile: null, disable_client_headers: false });
                        setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                      }} className="px-2 py-1 border border-indigo-300 text-indigo-600 rounded text-xs hover:bg-indigo-50">
                        + Manuell
                      </button>
                    </div>

                    {segmentedHtmlTemplates.length > 0 && providerSegments.length === 0 && (
                      <div className="mt-2 text-xs text-purple-700 dark:text-purple-200">
                        ✅ Templates gespeichert • werden beim Segmentieren verteilt
                      </div>
                    )}

                    {htmlUploadActive && providerSegments.length > 0 && (
                      <div className="mt-2 text-xs text-purple-700 dark:text-purple-200">
                        ✅ {segmentedHtmlTemplates.length} Templates geladen • AI-Content bleibt original (nur Betreff/From wird randomisiert)
                      </div>
                    )}
                  </div>

                  {renderHtmlLinkPanel()}

                  {/* Global Rate-Limit Panel (nicht bei Burst) */}
                  {providerSegments.length > 0 && campaignData.execution_mode !== 'burst_parallel' && (
                    <div className="bg-orange-50 dark:bg-dark-surface border border-orange-200 dark:border-dark-copper rounded-lg p-4 mt-3">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Versand-Geschwindigkeit (alle Segmente)</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">Sending Pattern</label>
                          <select
                            value={globalRateConfig.pattern}
                            onChange={(e) => setGlobalRateConfig(prev => ({ ...prev, pattern: e.target.value }))}
                            className="w-full px-2 py-1.5 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-xs"
                          >
                            <optgroup label="Standard">
                              <option value="constant">Constant (Gleiche Rate)</option>
                              <option value="pyramid">Pyramid Warmup (Safe)</option>
                              <option value="inverse_pyramid">Inverse Pyramid (Fast)</option>
                            </optgroup>
                            <optgroup label="Natural">
                              <option value="wave">Wave (2 Peaks)</option>
                              <option value="morning_rush">Morning Rush (B2B)</option>
                              <option value="evening_blast">Evening Blast (B2C)</option>
                            </optgroup>
                            <optgroup label="Aggressive">
                              <option value="flash_blast">Flash Blast (Launch)</option>
                            </optgroup>
                            <optgroup label="Stealth">
                              <option value="ghost_protocol">Ghost Protocol (7d Warmup)</option>
                              <option value="stealth_drip">Stealth Drip (8h Spread)</option>
                              <option value="chaos_theory">Chaos Theory (Random)</option>
                            </optgroup>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">Basis-Rate (E-Mails/h)</label>
                          <input
                            type="number"
                            value={globalRateConfig.baseRate}
                            onChange={(e) => setGlobalRateConfig(prev => ({ ...prev, baseRate: parseInt(e.target.value) || 3000 }))}
                            className="w-full px-2 py-1.5 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-xs"
                            min="50" max="20000"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">Streams</label>
                          <input
                            type="number"
                            value={globalRateConfig.streams}
                            onChange={(e) => setGlobalRateConfig(prev => ({ ...prev, streams: parseInt(e.target.value) || 1 }))}
                            className="w-full px-2 py-1.5 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-xs"
                            min="1" max="6"
                          />
                        </div>
                        <div className="flex items-end">
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={globalRateConfig.jitter.enabled}
                              onChange={(e) => setGlobalRateConfig(prev => ({ ...prev, jitter: { ...prev.jitter, enabled: e.target.checked } }))}
                              className="h-3.5 w-3.5 text-orange-600 rounded"
                            />
                            <span className="text-xs text-gray-700 dark:text-gray-200">Jitter {globalRateConfig.jitter.enabled ? `(${Math.abs(globalRateConfig.jitter.min_ms)}-${globalRateConfig.jitter.max_ms}ms)` : ''}</span>
                          </div>
                        </div>
                      </div>
                      {globalRateConfig.jitter.enabled && (
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          <div>
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Frueher (ms)</label>
                            <input type="number" value={Math.abs(globalRateConfig.jitter.min_ms)}
                              onChange={(e) => setGlobalRateConfig(prev => ({ ...prev, jitter: { ...prev.jitter, min_ms: -Math.abs(parseInt(e.target.value) || 500) } }))}
                              className="w-full px-2 py-1 border border-gray-300 dark:border-dark-copper rounded text-xs bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100"
                              min="0" max="5000" />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Spaeter (ms)</label>
                            <input type="number" value={globalRateConfig.jitter.max_ms}
                              onChange={(e) => setGlobalRateConfig(prev => ({ ...prev, jitter: { ...prev.jitter, max_ms: Math.abs(parseInt(e.target.value) || 500) } }))}
                              className="w-full px-2 py-1 border border-gray-300 dark:border-dark-copper rounded text-xs bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100"
                              min="0" max="5000" />
                          </div>
                        </div>
                      )}
                      {globalRateConfig.pattern !== 'constant' && (
                        <div className="mt-2 text-xs text-orange-700 dark:text-orange-300">
                          Seg 1: ~{calculateRateForPosition(globalRateConfig.pattern, 0, globalRateConfig.baseRate)}/h
                          {' | '}Seg {Math.ceil(providerSegments.length / 2)}: ~{calculateRateForPosition(globalRateConfig.pattern, 0.5, globalRateConfig.baseRate)}/h
                          {' | '}Seg {providerSegments.length}: ~{calculateRateForPosition(globalRateConfig.pattern, 1, globalRateConfig.baseRate)}/h
                        </div>
                      )}
                      <button
                        onClick={applyGlobalRateToSegments}
                        className="mt-3 w-full px-3 py-1.5 bg-orange-600 text-white rounded text-xs font-medium hover:bg-orange-700"
                      >
                        Auf alle {providerSegments.length} Segmente anwenden
                      </button>
                    </div>
                  )}

                  {providerSegments.length > 0 ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                        ✅ {providerSegments.length} Provider-Segmente erstellt ({campaignData.central_recipients.length} Empfänger)
                        </div>
                        {providerSegments.length > 1 && providerSegments[0].ai_variants?.length > 0 && (
                          <button
                            onClick={() => setSmartReplicateModalOpen(true)}
                            disabled={htmlUploadActive}
                            className={`px-3 py-1 rounded-md text-sm flex items-center space-x-2 ${
                              htmlUploadActive
                                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                                : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700'
                            }`}
                          >
                            <Shuffle className="h-4 w-4" />
                            <span>⚡ Segment 1 auf alle kopieren</span>
                          </button>
                        )}
                      </div>
                      
                      {providerSegments.map((segment, index) => (
                        <div
                          key={index}
                          className="bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-copper rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3">
                                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                  {segment.provider_name}
                                </div>
                                <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                                  {segment.provider_type}
                                </span>
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                📧 {segment.recipient_count} Empfänger ({Math.round((segment.recipient_count / campaignData.central_recipients.length) * 100)}%)
                                {segment.ai_variants && segment.ai_variants.length > 0 && (
                                  <span className="ml-3">🤖 {segment.ai_variants.length} AI-Variants</span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                setCurrentSegment(segment);
                                setSegmentConfigModalOpen(true);
                              }}
                              className="px-3 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 text-sm"
                            >
                              ⚙️ Konfigurieren
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      Klicke auf "Nach Provider segmentieren" um die zentrale Liste automatisch aufzuteilen
                    </div>
                  )}
                </div>
              )}
              
              {/* 📧 SMTP/Webmail-Segmentierung (bei smtp_segmented_parallel, smtp_round_robin, burst) */}
              {(campaignData.execution_mode === 'smtp_segmented_parallel' || campaignData.execution_mode === 'smtp_round_robin' || campaignData.execution_mode === 'burst_parallel') && campaignData.central_recipients.length > 0 && (
                <div className={`${campaignData.send_method === 'webmail' ? 'bg-teal-50 dark:bg-dark-surface border-2 border-teal-200 dark:border-teal-700' : 'bg-green-50 dark:bg-dark-surface border-2 border-green-200 dark:border-green-700'} rounded-lg p-6`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center">
                      <Server className={`h-5 w-5 mr-2 ${campaignData.send_method === 'webmail' ? 'text-teal-600' : 'text-green-600'}`} />
                      {campaignData.send_method === 'webmail'
                        ? '🌐 Webmail-Segmentierung (by Account)'
                        : '📧 SMTP-Segmentierung (by FROM-EMAIL)'}
                    </h3>
                    {providerSegments.length === 0 && (
                      <button
                        onClick={handleSmtpAnalyzeAndSegment}
                        disabled={segmentAnalyzing || (campaignData.send_method === 'webmail' ? (webmailAccounts || []).filter(a => a.is_active).length === 0 : selectableSmtpServers.length === 0)}
                        className={`px-4 py-2 ${campaignData.send_method === 'webmail' ? 'bg-teal-600 hover:bg-teal-700' : 'bg-green-600 hover:bg-green-700'} text-white rounded-md disabled:opacity-50 flex items-center space-x-2`}
                      >
                        {segmentAnalyzing ? (
                          <>
                            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                            <span>Analysiere...</span>
                          </>
                        ) : (
                          <>
                            <Server className="h-4 w-4" />
                            <span>
                              {campaignData.send_method === 'webmail'
                                ? 'Nach Webmail-Accounts segmentieren'
                                : 'Nach SMTPs segmentieren'}
                            </span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  
                  <div className="bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-copper rounded-lg p-4 mb-3">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">📄 HTML-Templates Upload (segmented)</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Gleichmäßige Verteilung auf Segmente • Templates bleiben unverändert
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <input
                          type="file"
                          multiple
                          accept=".html,.htm,.txt"
                          onChange={handleSegmentHtmlUpload}
                          className="text-sm text-gray-500 dark:text-gray-300"
                        />
                        <span className="text-xs text-gray-400 dark:text-gray-500">oder</span>
                        <HtmlFolderPicker onFilesSelected={handleSegmentHtmlUpload} />
                      </div>
                    </div>

                    <div className="mt-2 mb-2 flex flex-wrap items-end gap-2 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200 dark:border-emerald-700 rounded-lg p-2">
                      <div className="min-w-[180px] flex-1">
                        <label className="block text-xs font-medium text-emerald-800 dark:text-emerald-200 mb-1">🧬 HTML-Layout-Generator</label>
                        <select value={htmlGenScript} onChange={(e) => setHtmlGenScript(e.target.value)}
                          className="w-full px-2 py-1 border border-emerald-300 dark:border-emerald-600 bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-xs">
                          <option value="">Kein Generator (HTML-Upload)</option>
                          {htmlGeneratorCatalog.map(g => (
                            <option key={g.id} value={g.id}>{g.label || g.id}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-emerald-800 dark:text-emerald-200 mb-1">Anzahl</label>
                        <input type="number" min={1} max={500} value={htmlGenCount}
                          onChange={(e) => setHtmlGenCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                          className="w-20 px-2 py-1 border border-emerald-300 dark:border-emerald-600 bg-white dark:bg-dark-card rounded text-xs text-center" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-emerald-800 dark:text-emerald-200 mb-1">CTA</label>
                        <input type="text" value={htmlGenCta} onChange={(e) => setHtmlGenCta(e.target.value)}
                          className="w-28 px-2 py-1 border border-emerald-300 dark:border-emerald-600 bg-white dark:bg-dark-card rounded text-xs font-mono" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-emerald-800 dark:text-emerald-200 mb-1">Logo</label>
                        <input type="text" value={htmlGenLogo} disabled={!htmlGenIncludeLogo} onChange={(e) => setHtmlGenLogo(e.target.value)}
                          className="w-28 px-2 py-1 border border-emerald-300 dark:border-emerald-600 bg-white dark:bg-dark-card rounded text-xs font-mono" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-emerald-800 dark:text-emerald-200 mb-1">QR</label>
                        <input type="text" value={htmlGenQr} onChange={(e) => setHtmlGenQr(e.target.value)}
                          className="w-24 px-2 py-1 border border-emerald-300 dark:border-emerald-600 bg-white dark:bg-dark-card rounded text-xs font-mono" />
                      </div>
                      <label className="flex items-center gap-1 text-xs text-emerald-800 dark:text-emerald-200 pb-1">
                        <input type="checkbox" checked={htmlGenIncludeLogo} onChange={(e) => handleHtmlGenLogoToggle(e.target.checked)} />
                        Logo an
                      </label>
                      <span className="text-[10px] text-emerald-800 dark:text-emerald-200 font-medium max-w-xs">
                        Priorität: Erzeugung beim ⚡ Fast-Prepare serverseitig (wie Upload, nur Backend → config + ai_variants)
                      </span>
                    </div>

                    <div className="mt-2 mb-2 flex items-end gap-2 bg-gradient-to-r from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20 border border-green-200 dark:border-green-700 rounded-lg p-2">
                      <div>
                        <label className="block text-xs font-medium text-green-800 dark:text-green-200 mb-1">🏦 Bank-Generator</label>
                        <select value={bankGenBank} onChange={(e) => setBankGenBank(e.target.value)}
                          className="px-2 py-1 border border-green-300 dark:border-green-600 bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-xs">
                          {subjectFromPools.map(pool => (
                            <option key={pool.key} value={pool.key}>{pool.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-green-800 dark:text-green-200 mb-1">Rules</label>
                        <input type="number" min={1} value={bankGenCount} onChange={(e) => setBankGenCount(Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-16 px-2 py-1 border border-green-300 dark:border-green-600 bg-white dark:bg-dark-card rounded text-xs text-center" />
                      </div>
                      <button onClick={handleGenerateSubjectFromRules}
                        className="px-3 py-1 bg-green-600 text-white rounded text-xs font-bold hover:bg-green-700 whitespace-nowrap">
                        Generieren
                      </button>
                      <span className="text-xs text-green-600 dark:text-green-300 whitespace-nowrap">
                        {subjectFromPools.find(pool => pool.key === bankGenBank)?.subject_count || 0} Subjects •{' '}
                        {subjectFromPools.find(pool => pool.key === bankGenBank)?.from_name_count || 0} Names
                      </span>
                    </div>

                    <div className="mt-3 space-y-2">
                      {(campaignData.subject_fromname_rules || []).length === 0 ? (
                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <label className="block text-xs text-gray-700 dark:text-gray-200 mb-1">Betreff (AI-Input)</label>
                            <input
                              value={campaignData.segmented_template_subject || ''}
                              onChange={(e) => setCampaignData(prev => ({ ...prev, segmented_template_subject: e.target.value }))}
                              className="w-full px-2 py-1 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-sm"
                              placeholder="Betreff-Input für AI"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-xs text-gray-700 dark:text-gray-200 mb-1">From-Name (AI-Input)</label>
                            <input
                              value={campaignData.segmented_template_from_name || ''}
                              onChange={(e) => setCampaignData(prev => ({ ...prev, segmented_template_from_name: e.target.value }))}
                              className="w-full px-2 py-1 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-sm"
                              placeholder="Absender-Name für AI"
                            />
                          </div>
                          <button
                            onClick={() => {
                              const total = providerSegments.length || 1000;
                              setCampaignData(prev => ({ ...prev, subject_fromname_rules: [{ subject: prev.segmented_template_subject || '', from_name: prev.segmented_template_from_name || '', from: 1, to: total }, { subject: '', from_name: '', from: 1, to: total }], segmented_template_subject: '', segmented_template_from_name: '' }));
                            }}
                            className="px-2 py-1 bg-green-600 text-white rounded text-lg font-bold hover:bg-green-700 h-[30px] w-[30px] flex items-center justify-center flex-shrink-0"
                            title="Mehrere Subjects/From-Names für verschiedene Segment-Ranges"
                          >+</button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-200">Subject / From-Name Rules ({(campaignData.subject_fromname_rules || []).length})</label>
                            <button
                              onClick={() => {
                                const total = providerSegments.length || 1000;
                                setCampaignData(prev => {
                                  const existing = prev.subject_fromname_rules || [];
                                  const newCount = existing.length + 1;
                                  const perRule = Math.ceil(total / newCount);
                                  const templateRule = existing[existing.length - 1] || {};
                                  const recalculated = [];
                                  for (let i = 0; i < newCount; i++) {
                                    const rFrom = i * perRule + 1;
                                    const rTo = Math.min((i + 1) * perRule, total);
                                    recalculated.push({
                                      subject: existing[i]?.subject || templateRule.subject || '',
                                      from_name: existing[i]?.from_name || templateRule.from_name || '',
                                      from: rFrom,
                                      to: rTo
                                    });
                                  }
                                  return { ...prev, subject_fromname_rules: recalculated };
                                });
                              }}
                              className="px-2 py-0.5 bg-green-600 text-white rounded text-xs font-bold hover:bg-green-700"
                            >+ Rule</button>
                          </div>
                          {(campaignData.subject_fromname_rules || []).map((rule, ri) => (
                            <div key={ri} className="flex items-center gap-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded p-2">
                              <input
                                value={rule.subject || ''}
                                onChange={(e) => { const rules = [...(campaignData.subject_fromname_rules || [])]; rules[ri] = { ...rules[ri], subject: e.target.value }; setCampaignData(prev => ({ ...prev, subject_fromname_rules: rules })); }}
                                className="flex-1 px-2 py-1 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-xs"
                                placeholder="Betreff (Spintax)"
                              />
                              <input
                                value={rule.from_name || ''}
                                onChange={(e) => { const rules = [...(campaignData.subject_fromname_rules || [])]; rules[ri] = { ...rules[ri], from_name: e.target.value }; setCampaignData(prev => ({ ...prev, subject_fromname_rules: rules })); }}
                                className="flex-1 px-2 py-1 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-xs"
                                placeholder="From-Name (Spintax)"
                              />
                              <span className="text-xs text-gray-500 dark:text-gray-400">Seg:</span>
                              <input type="number" value={rule.from || ''} onChange={(e) => { const rules = [...(campaignData.subject_fromname_rules || [])]; rules[ri] = { ...rules[ri], from: parseInt(e.target.value) || 1 }; setCampaignData(prev => ({ ...prev, subject_fromname_rules: rules })); }}
                                className="w-16 px-1 py-1 border rounded text-xs text-center" placeholder="von" />
                              <span className="text-xs">-</span>
                              <input type="number" value={rule.to || ''} onChange={(e) => { const rules = [...(campaignData.subject_fromname_rules || [])]; rules[ri] = { ...rules[ri], to: e.target.value ? parseInt(e.target.value) : '' }; setCampaignData(prev => ({ ...prev, subject_fromname_rules: rules })); }}
                                className="w-16 px-1 py-1 border rounded text-xs text-center" placeholder="bis" />
                              <button onClick={() => { const rules = [...(campaignData.subject_fromname_rules || [])]; rules.splice(ri, 1); setCampaignData(prev => ({ ...prev, subject_fromname_rules: rules })); }}
                                className="text-red-500 hover:text-red-700 font-bold text-lg px-1">x</button>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                    <div className="mt-2">
                      <label className="block text-xs text-gray-700 dark:text-gray-200 mb-1">Reply-To (optional)</label>
                      <input
                        value={campaignData.reply_to || ''}
                        onChange={(e) => setCampaignData(prev => ({ ...prev, reply_to: e.target.value }))}
                        className="w-full max-w-xs px-2 py-1 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-sm"
                        placeholder="reply@example.com"
                      />
                    </div>

                    <div className="flex flex-wrap gap-3 mt-3 text-xs text-gray-700 dark:text-gray-200">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input type="checkbox" checked={segmentStripHtmlWrapper} onChange={(e) => setSegmentStripHtmlWrapper(e.target.checked)}
                          className={`h-4 w-4 ${campaignData.send_method === 'webmail' ? 'text-teal-600' : 'text-green-600'} border-gray-300 rounded`} />
                        <span>HTML-Wrapper entfernen</span>
                      </label>
                    </div>

                    {/* Feature-% Slider (Webmail) — gilt automatisch pro Segment */}
                    <div className="mt-3 bg-gradient-to-r from-blue-50 via-purple-50 to-orange-50 dark:from-blue-900/20 dark:via-purple-900/20 dark:to-orange-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-purple-800 dark:text-purple-200 w-20 shrink-0">ZW-Subject</span>
                          <input type="range" min={0} max={100} step={5} value={campaignData.zw_subject_pct ?? 100}
                            onChange={(e) => setCampaignData(prev => ({ ...prev, zw_subject_pct: parseInt(e.target.value) }))}
                            className="flex-1 h-1.5 accent-purple-600" />
                          <span className="text-xs font-bold text-purple-700 dark:text-purple-300 w-10 text-right">{campaignData.zw_subject_pct ?? 100}%</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-violet-800 dark:text-violet-200 w-20 shrink-0">ZW-From</span>
                          <input type="range" min={0} max={100} step={5} value={campaignData.zw_from_pct ?? 0}
                            onChange={(e) => setCampaignData(prev => ({ ...prev, zw_from_pct: parseInt(e.target.value) }))}
                            className="flex-1 h-1.5 accent-violet-600" />
                          <span className="text-xs font-bold text-violet-700 dark:text-violet-300 w-10 text-right">{campaignData.zw_from_pct ?? 0}%</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 w-20 shrink-0">ZW-Content</span>
                          <input type="range" min={0} max={100} step={5} value={campaignData.zw_content_pct ?? 0}
                            onChange={(e) => setCampaignData(prev => ({ ...prev, zw_content_pct: parseInt(e.target.value) }))}
                            className="flex-1 h-1.5 accent-gray-600" />
                          <span className="text-xs font-bold text-gray-600 dark:text-gray-400 w-10 text-right">{campaignData.zw_content_pct ?? 0}%</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-blue-800 dark:text-blue-200 w-20 shrink-0">Multipart</span>
                          <input type="range" min={0} max={100} step={5} value={campaignData.multipart_pct ?? 0}
                            onChange={(e) => setCampaignData(prev => ({ ...prev, multipart_pct: parseInt(e.target.value) }))}
                            className="flex-1 h-1.5 accent-blue-600" />
                          <span className="text-xs font-bold text-blue-700 dark:text-blue-300 w-10 text-right">{campaignData.multipart_pct ?? 0}%</span>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!campaignData.multipart_dice_enabled}
                            onChange={(e) => setCampaignData(prev => ({ ...prev, multipart_dice_enabled: e.target.checked }))}
                            className="h-3.5 w-3.5 text-cyan-600 border-gray-300 rounded"
                          />
                          <span className="text-xs font-semibold text-cyan-800 dark:text-cyan-200">
                            Multipart würfeln
                          </span>
                          <span className="text-[10px] text-gray-500 dark:text-gray-400">
                            feste MIME / komplett gemischt
                          </span>
                        </label>
                        <div className={`flex items-center gap-2 ${(campaignData.multipart_pct ?? 0) === 0 ? 'opacity-30 pointer-events-none' : ''}`}>
                          <span className="text-xs font-semibold text-teal-800 dark:text-teal-200 w-20 shrink-0">ZW-MP-Text</span>
                          <input type="range" min={0} max={100} step={5} value={campaignData.zw_text_pct ?? 0}
                            onChange={(e) => setCampaignData(prev => ({ ...prev, zw_text_pct: parseInt(e.target.value) }))}
                            className="flex-1 h-1.5 accent-teal-600" />
                          <span className="text-xs font-bold text-teal-700 dark:text-teal-300 w-10 text-right">{campaignData.zw_text_pct ?? 0}%</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-orange-800 dark:text-orange-200 w-20 shrink-0">Garbage</span>
                          <input type="range" min={0} max={100} step={5} value={campaignData.garbage_pct ?? 80}
                            onChange={(e) => setCampaignData(prev => ({ ...prev, garbage_pct: parseInt(e.target.value) }))}
                            className="flex-1 h-1.5 accent-orange-600" />
                          <span className="text-xs font-bold text-orange-700 dark:text-orange-300 w-10 text-right">{campaignData.garbage_pct ?? 80}%</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mt-2">
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 flex-1">0% = aus, 100% = immer an. Segment-Rules haben Vorrang.</p>
                        <button
                          onClick={() => setCampaignData(prev => ({ ...prev, zw_subject_pct: 100, zw_from_pct: 100, zw_content_pct: 100, multipart_pct: 100, zw_text_pct: 100, garbage_pct: 100 }))}
                          className="px-2 py-0.5 bg-green-600 text-white rounded text-[10px] font-bold hover:bg-green-700 whitespace-nowrap"
                        >FULL</button>
                        <button
                          onClick={() => setCampaignData(prev => ({ ...prev, zw_subject_pct: 0, zw_from_pct: 0, zw_content_pct: 0, multipart_pct: 0, zw_text_pct: 0, garbage_pct: 0 }))}
                          className="px-2 py-0.5 bg-red-600 text-white rounded text-[10px] font-bold hover:bg-red-700 whitespace-nowrap"
                        >OFF</button>
                      </div>
                      {renderParallelSegmentsControl()}
                      <label className="flex items-center gap-2 mt-3 cursor-pointer">
                        <input type="checkbox" checked={!!campaignData.tld_rotation}
                          onChange={(e) => setCampaignData(prev => ({ ...prev, tld_rotation: e.target.checked }))}
                          className="h-3.5 w-3.5 text-gray-500 border-gray-300 rounded" />
                        <span className="text-xs text-gray-600 dark:text-gray-400">Link-Domain Rotation</span>
                      </label>
                    </div>

                    {/* Thread-Opener (Email Threading) - SMTP-Segmented */}
                    <div className="mt-3 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200 dark:border-emerald-700 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-emerald-800 dark:text-emerald-200">Thread-Opener</span>
                          <span className="text-[10px] text-emerald-600 dark:text-emerald-400">(Opener + Reply Threading)</span>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" checked={!!campaignData.thread_opener_enabled} onChange={(e) => setCampaignData(prev => ({ ...prev, thread_opener_enabled: e.target.checked }))} className="sr-only peer" />
                          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-500 peer-checked:bg-emerald-600"></div>
                        </label>
                      </div>
                      {campaignData.thread_opener_enabled && (
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-[10px] text-emerald-700 dark:text-emerald-300">Delay nach Opener (Sek.)</label>
                              <input type="number" value={campaignData.thread_opener_delay || 5} onChange={(e) => setCampaignData(prev => ({ ...prev, thread_opener_delay: parseInt(e.target.value) || 5 }))}
                                className="w-full px-2 py-1 text-xs border rounded dark:bg-dark-card dark:border-gray-600" />
                            </div>
                          </div>
                          <div>
                            <label className="text-[10px] text-emerald-700 dark:text-emerald-300">Opener HTML-Templates ({openerHtmlTemplates.length || 0} geladen)</label>
                            <div className="flex items-center gap-2 mt-1">
                              <input type="file" multiple accept=".html,.htm,.txt" onChange={handleOpenerHtmlUpload}
                                className="text-xs text-gray-500 dark:text-gray-300" />
                              <HtmlFolderPicker onFilesSelected={handleOpenerHtmlUpload} />
                            </div>
                            {openerHtmlTemplates.length > 0 && (
                              <p className="text-[10px] text-emerald-600 mt-1">✅ {openerHtmlTemplates.length} Opener-Templates – werden pro Segment rotiert</p>
                            )}
                          </div>
                          <div>
                            <label className="text-[10px] text-emerald-700 dark:text-emerald-300">Opener-Empfänger (leer = 1. Empfänger aus Segment)</label>
                            <input type="email" value={campaignData.thread_opener_recipient || ''} onChange={(e) => setCampaignData(prev => ({ ...prev, thread_opener_recipient: e.target.value }))}
                              placeholder="test@example.com"
                              className="w-full px-2 py-1 text-xs border rounded dark:bg-dark-card dark:border-gray-600" />
                          </div>
                          <div>
                            <label className="text-[10px] text-emerald-700 dark:text-emerald-300">Oder: Opener HTML manuell (Fallback)</label>
                            <textarea value={campaignData.thread_opener_html || ''} onChange={(e) => setCampaignData(prev => ({ ...prev, thread_opener_html: e.target.value }))}
                              rows={2} placeholder="<div>Vielen Dank für Ihre Anfrage...</div>"
                              className="w-full px-2 py-1 text-xs border rounded dark:bg-dark-card dark:border-gray-600 font-mono" />
                          </div>
                          <p className="text-[10px] text-emerald-600 dark:text-emerald-400">1 Opener pro Segment → Pause → Alle Recipients als Reply. Subject + From-Name aus Slot.</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Segment-Rules Panel (SMTP-Segmented) */}
                    {(campaignData.segment_rules || []).length > 0 && (
                      <div className="mt-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg p-3">
                        <h5 className="text-xs font-semibold text-indigo-800 dark:text-indigo-200 mb-2">Segment-Rules ({campaignData.segment_rules.length})</h5>
                        {campaignData.segment_rules.map((rule, ri) => (
                          <div key={ri} className="flex flex-wrap items-center gap-2 mb-2 text-xs bg-white dark:bg-dark-card p-2 rounded border">
                            <span className="font-medium">Seg</span>
                            <input type="number" value={rule.from || ''} onChange={(e) => {
                              const rules = [...(campaignData.segment_rules || [])];
                              rules[ri] = { ...rules[ri], from: parseInt(e.target.value) || 1 };
                              setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                            }} className="w-14 px-1 py-0.5 border rounded text-center" placeholder="von" />
                            <span>-</span>
                            <input type="number" value={rule.to || ''} onChange={(e) => {
                              const rules = [...(campaignData.segment_rules || [])];
                              rules[ri] = { ...rules[ri], to: e.target.value ? parseInt(e.target.value) : null };
                              setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                            }} className="w-14 px-1 py-0.5 border rounded text-center" placeholder="bis" />
                            <label className="flex items-center gap-1"><input type="checkbox" checked={!!rule.zw_subject} onChange={(e) => {
                              const rules = [...(campaignData.segment_rules || [])];
                              rules[ri] = { ...rules[ri], zw_subject: e.target.checked };
                              setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                            }} className="h-3 w-3" /><span>ZW Sub</span></label>
                            <label className="flex items-center gap-1"><input type="checkbox" checked={!!rule.zw_fromname} onChange={(e) => {
                              const rules = [...(campaignData.segment_rules || [])];
                              rules[ri] = { ...rules[ri], zw_fromname: e.target.checked };
                              setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                            }} className="h-3 w-3" /><span>ZW From</span></label>
                            <label className="flex items-center gap-1"><input type="checkbox" checked={!!rule.zw_content} onChange={(e) => {
                              const rules = [...(campaignData.segment_rules || [])];
                              rules[ri] = { ...rules[ri], zw_content: e.target.checked };
                              setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                            }} className="h-3 w-3" /><span>ZW Content</span></label>
                            <label className="flex items-center gap-1"><input type="checkbox" checked={rule.multipart !== false} onChange={(e) => {
                              const rules = [...(campaignData.segment_rules || [])];
                              rules[ri] = { ...rules[ri], multipart: e.target.checked };
                              setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                            }} className="h-3 w-3" /><span>Multipart</span></label>
                            <label className="flex items-center gap-1"><input type="checkbox" checked={!!rule.zw_textpart} onChange={(e) => {
                              const rules = [...(campaignData.segment_rules || [])];
                              rules[ri] = { ...rules[ri], zw_textpart: e.target.checked };
                              setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                            }} className="h-3 w-3" /><span>ZW Text</span></label>
                            <label className="flex items-center gap-1"><input type="checkbox" checked={!!rule.disable_client_headers} onChange={(e) => {
                              const rules = [...(campaignData.segment_rules || [])];
                              rules[ri] = { ...rules[ri], disable_client_headers: e.target.checked };
                              setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                            }} className="h-3 w-3" /><span>Client aus</span></label>
                            <select value={rule.client_profile || ''} onChange={(e) => {
                              const rules = [...(campaignData.segment_rules || [])];
                              rules[ri] = { ...rules[ri], client_profile: e.target.value || null };
                              setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                            }} className="px-1 py-0.5 border rounded text-xs">
                              <option value="">Auto (per SMTP)</option>
                              <option value="thunderbird">Thunderbird</option>
                              <option value="outlook">Outlook</option>
                              <option value="applemail">Apple Mail</option>
                              <option value="iphonemail">iPhone Mail</option>
                            </select>
                            <button onClick={() => {
                              const rules = [...(campaignData.segment_rules || [])];
                              rules.splice(ri, 1);
                              setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                            }} className="text-red-500 hover:text-red-700 font-bold px-1">x</button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <select
                        defaultValue="max"
                        id="preset-select"
                        className="px-2 py-1 border rounded text-xs"
                      >
                        <option value="max">Max Variation</option>
                        <option value="safe">Safe</option>
                        <option value="stealth">Stealth</option>
                      </select>
                      <input
                        type="number"
                        defaultValue={500}
                        id="preset-batch"
                        className="w-20 px-2 py-1 border rounded text-xs text-center"
                        placeholder="Batch"
                        min={1}
                      />
                      <span className="text-xs text-gray-500">Segmente/Gruppe</span>
                      <button onClick={() => {
                        const preset = document.getElementById('preset-select')?.value || 'max';
                        const batch = parseInt(document.getElementById('preset-batch')?.value) || 500;
                        const total = providerSegments.length || campaignData.central_recipients?.length || parseInt(prompt('Anzahl Segmente/Mails:') || '0');
                        if (!total) return;
                        const effectiveBatch = Math.min(batch, total);
                        const rules = generatePresetRules(total, effectiveBatch, preset);
                        setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                        toast?.success?.(`${rules.length} Rules generiert (${total} Segmente, ${effectiveBatch}/Gruppe, ${preset})`);
                      }} className="px-3 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700">
                        Generieren ({providerSegments.length || '?'} Seg)
                      </button>
                      <button onClick={() => {
                        const rules = [...(campaignData.segment_rules || [])];
                        rules.push({ from: 1, to: null, zw_subject: true, zw_fromname: true, zw_content: false, zw_textpart: true, multipart: false, client_profile: null, disable_client_headers: false });
                        setCampaignData(prev => ({ ...prev, segment_rules: rules }));
                      }} className="px-2 py-1 border border-indigo-300 text-indigo-600 rounded text-xs hover:bg-indigo-50">
                        + Manuell
                      </button>
                    </div>

                    {segmentedHtmlTemplates.length > 0 && providerSegments.length === 0 && (
                      <div className={`mt-2 text-xs ${campaignData.send_method === 'webmail' ? 'text-teal-700 dark:text-teal-200' : 'text-green-700 dark:text-green-200'}`}>
                        ✅ Templates gespeichert • werden beim Segmentieren verteilt
                      </div>
                    )}

                    {htmlUploadActive && providerSegments.length > 0 && (
                      <div className={`mt-2 text-xs ${campaignData.send_method === 'webmail' ? 'text-teal-700 dark:text-teal-200' : 'text-green-700 dark:text-green-200'}`}>
                        ✅ {segmentedHtmlTemplates.length} Templates geladen • AI-Content bleibt original (nur Betreff/From wird randomisiert)
                      </div>
                    )}
                  </div>

                  {renderHtmlLinkPanel()}

                  {/* Global Rate-Limit Panel (SMTP-Segmented, nicht bei Burst) */}
                  {providerSegments.length > 0 && campaignData.execution_mode !== 'burst_parallel' && (
                    <div className="bg-orange-50 dark:bg-dark-surface border border-orange-200 dark:border-dark-copper rounded-lg p-4 mt-3">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Versand-Geschwindigkeit (alle Segmente)</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">Sending Pattern</label>
                          <select
                            value={globalRateConfig.pattern}
                            onChange={(e) => setGlobalRateConfig(prev => ({ ...prev, pattern: e.target.value }))}
                            className="w-full px-2 py-1.5 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-xs"
                          >
                            <optgroup label="Standard">
                              <option value="constant">Constant (Gleiche Rate)</option>
                              <option value="pyramid">Pyramid Warmup (Safe)</option>
                              <option value="inverse_pyramid">Inverse Pyramid (Fast)</option>
                            </optgroup>
                            <optgroup label="Natural">
                              <option value="wave">Wave (2 Peaks)</option>
                              <option value="morning_rush">Morning Rush (B2B)</option>
                              <option value="evening_blast">Evening Blast (B2C)</option>
                            </optgroup>
                            <optgroup label="Aggressive">
                              <option value="flash_blast">Flash Blast (Launch)</option>
                            </optgroup>
                            <optgroup label="Stealth">
                              <option value="ghost_protocol">Ghost Protocol (7d Warmup)</option>
                              <option value="stealth_drip">Stealth Drip (8h Spread)</option>
                              <option value="chaos_theory">Chaos Theory (Random)</option>
                            </optgroup>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">Basis-Rate (E-Mails/h)</label>
                          <input
                            type="number"
                            value={globalRateConfig.baseRate}
                            onChange={(e) => setGlobalRateConfig(prev => ({ ...prev, baseRate: parseInt(e.target.value) || 3000 }))}
                            className="w-full px-2 py-1.5 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-xs"
                            min="50" max="20000"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">Streams</label>
                          <input
                            type="number"
                            value={globalRateConfig.streams}
                            onChange={(e) => setGlobalRateConfig(prev => ({ ...prev, streams: parseInt(e.target.value) || 1 }))}
                            className="w-full px-2 py-1.5 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-xs"
                            min="1" max="6"
                          />
                        </div>
                        <div className="flex items-end">
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={globalRateConfig.jitter.enabled}
                              onChange={(e) => setGlobalRateConfig(prev => ({ ...prev, jitter: { ...prev.jitter, enabled: e.target.checked } }))}
                              className="h-3.5 w-3.5 text-orange-600 rounded"
                            />
                            <span className="text-xs text-gray-700 dark:text-gray-200">Jitter {globalRateConfig.jitter.enabled ? `(${Math.abs(globalRateConfig.jitter.min_ms)}-${globalRateConfig.jitter.max_ms}ms)` : ''}</span>
                          </div>
                        </div>
                      </div>
                      {globalRateConfig.jitter.enabled && (
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          <div>
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Frueher (ms)</label>
                            <input type="number" value={Math.abs(globalRateConfig.jitter.min_ms)}
                              onChange={(e) => setGlobalRateConfig(prev => ({ ...prev, jitter: { ...prev.jitter, min_ms: -Math.abs(parseInt(e.target.value) || 500) } }))}
                              className="w-full px-2 py-1 border border-gray-300 dark:border-dark-copper rounded text-xs bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100"
                              min="0" max="5000" />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Spaeter (ms)</label>
                            <input type="number" value={globalRateConfig.jitter.max_ms}
                              onChange={(e) => setGlobalRateConfig(prev => ({ ...prev, jitter: { ...prev.jitter, max_ms: Math.abs(parseInt(e.target.value) || 500) } }))}
                              className="w-full px-2 py-1 border border-gray-300 dark:border-dark-copper rounded text-xs bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100"
                              min="0" max="5000" />
                          </div>
                        </div>
                      )}
                      {globalRateConfig.pattern !== 'constant' && (
                        <div className="mt-2 text-xs text-orange-700 dark:text-orange-300">
                          Seg 1: ~{calculateRateForPosition(globalRateConfig.pattern, 0, globalRateConfig.baseRate)}/h
                          {' | '}Seg {Math.ceil(providerSegments.length / 2)}: ~{calculateRateForPosition(globalRateConfig.pattern, 0.5, globalRateConfig.baseRate)}/h
                          {' | '}Seg {providerSegments.length}: ~{calculateRateForPosition(globalRateConfig.pattern, 1, globalRateConfig.baseRate)}/h
                        </div>
                      )}
                      <button
                        onClick={applyGlobalRateToSegments}
                        className="mt-3 w-full px-3 py-1.5 bg-orange-600 text-white rounded text-xs font-medium hover:bg-orange-700"
                      >
                        Auf alle {providerSegments.length} Segmente anwenden
                      </button>
                    </div>
                  )}

                  {providerSegments.length > 0 ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                          ✅ {providerSegments.length} {getSendMethodLabel(campaignData.send_method)}-Segmente erstellt ({campaignData.central_recipients.length} Empfänger)
                        </div>
                        {providerSegments.length > 1 && providerSegments[0].ai_variants?.length > 0 && (
                          <button
                            onClick={() => setSmartReplicateModalOpen(true)}
                            disabled={htmlUploadActive}
                            className={`px-3 py-1 rounded-md text-sm flex items-center space-x-2 ${
                              htmlUploadActive
                                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                                : 'bg-gradient-to-r from-green-600 to-teal-600 text-white hover:from-green-700 hover:to-teal-700'
                            }`}
                          >
                            <Shuffle className="h-4 w-4" />
                            <span>⚡ Segment 1 auf alle kopieren</span>
                          </button>
                        )}
                      </div>
                      
                      {providerSegments.map((segment, index) => (
                        <div
                          key={index}
                          className="bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-copper rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3">
                                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                  {segment.provider_name}
                                </div>
                                <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">
                                  {getSendMethodLabel(campaignData.send_method)}
                                </span>
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                📧 {segment.recipient_count} Empfänger ({Math.round((segment.recipient_count / campaignData.central_recipients.length) * 100)}%)
                                {segment.ai_variants && segment.ai_variants.length > 0 && (
                                  <span className="ml-3">🤖 {segment.ai_variants.length} AI-Variants</span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                setCurrentSegment(segment);
                                setSegmentConfigModalOpen(true);
                              }}
                              className="px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 text-sm"
                            >
                              ⚙️ Konfigurieren
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      {selectableSmtpServers.length === 0 
                        ? (campaignData.send_method === 'graph'
                          ? '⚠️ Keine Graph-fähigen SMTP-Accounts verfügbar.'
                          : (campaignData.send_method === 'gmx_webmail'
                            ? '⚠️ Keine geeigneten GMX.com-/GMX.us-/mail.com-Accounts verfügbar.'
                            : campaignData.send_method === 'rambler_webmail'
                              ? '⚠️ Keine geeigneten Rambler.ru-Accounts verfügbar.'
                            : '⚠️ Keine SMTP-Server verfügbar. Bitte zuerst SMTP-Server hinzufügen.'))
                        : 'Klicke auf "Nach SMTPs segmentieren" um die zentrale Liste nach FROM-EMAILs aufzuteilen'
                      }
                    </div>
                  )}
                </div>
              )}

              {/* Globale Anhänge */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Globale Anhänge (für alle Slots)
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <div className="text-center">
                    <div className="text-sm text-gray-500 dark:text-gray-300 mb-2">
                      Anhänge, die in allen Slots verwendet werden sollen
                    </div>
                    <input
                      type="file"
                      multiple
                      onChange={handleGlobalAttachmentUpload}
                      className="text-sm text-gray-500 dark:text-gray-300"
                      disabled={uploading}
                    />
                    {uploading && (
                      <div className="text-xs text-gray-500 dark:text-gray-300 mt-2">
                        Upload läuft...
                      </div>
                    )}
                  </div>
                  {campaignData.global_attachments.length > 0 && (
                    <div className="mt-3">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Ausgewählte Dateien:</div>
                      <div className="space-y-1">
                        {campaignData.global_attachments.map((file, index) => (
                          <div key={index} className="flex items-center justify-between text-sm">
                            <span>{file.name} ({Math.round(file.size/1024)}KB)</span>
                            <button
                              onClick={() => {
                                setCampaignData(prev => ({
                                  ...prev,
                                  global_attachments: prev.global_attachments.filter((_, i) => i !== index)
                                }));
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Domain-Analyse (VERSTECKT bei segmented_parallel und smtp_segmented_parallel) */}
              {campaignData.execution_mode !== 'segmented_parallel' && campaignData.execution_mode !== 'smtp_segmented_parallel' && campaignData.execution_mode !== 'smtp_round_robin' && campaignData.execution_mode !== 'burst_parallel' && domainAnalysis && (
                <div className="bg-blue-50 dark:bg-dark-surface p-4 rounded-lg border border-blue-200 dark:border-dark-copper">
                  <h3 className="font-medium text-blue-900 dark:text-gray-100 mb-3 flex items-center">
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Domain-Analyse
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(domainAnalysis.categories).map(([category, data]) => (
                      <div key={category} className="text-center">
                        <div className="text-lg font-semibold text-blue-600">
                          {data.count}
                        </div>
                        <div className="text-sm text-gray-600">
                          {category} ({data.percentage}%)
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {domainAnalysis.topDomains.length > 0 && (
                    <div className="mt-4">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Top Domains:</div>
                      <div className="flex flex-wrap gap-2">
                        {domainAnalysis.topDomains.slice(0, 8).map((domain) => (
                          <span
                            key={domain.domain}
                            className="px-2 py-1 bg-white rounded text-xs"
                          >
                            {domain.domain} ({domain.count})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {campaignData.central_recipients.length >= 50 && (
                    <div className="mt-4 flex items-center space-x-3">
                      <button
                        onClick={() => setAutoSlotMode(!autoSlotMode)}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center space-x-2"
                      >
                        <Layers className="h-4 w-4" />
                        <span>🎯 Auto-Slots</span>
                      </button>
                      
                      <button
                        onClick={() => setAutoAiMode(!autoAiMode)}
                        className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center space-x-2"
                      >
                        <span>🤖</span>
                        <span>Auto-AI-Slots</span>
                      </button>
                    </div>
                  )}
                  
                  {/* 🤖 AUTO-AI-SLOT-KONFIGURATION PANEL */}
                  {autoAiMode && (
                    <div className="mt-4 bg-purple-50 dark:bg-dark-surface border border-purple-200 dark:border-dark-copper-dark rounded-lg p-4">
                      <h4 className="text-md font-medium text-gray-900 mb-4">
                        🤖 Auto-AI-Slot-Konfiguration (Claude)
                      </h4>
                      
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                              Modus
                            </label>
                            <select
                              value={autoAiConfig.mode}
                              onChange={(e) => setAutoAiConfig(prev => ({...prev, mode: e.target.value}))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                              <option value="manual">📊 Manuelle Anzahl</option>
                              <option value="per_domain">🌐 Pro Domain (Auto)</option>
                            </select>
                          </div>
                          
                        {autoAiConfig.mode === 'manual' ? (
                          <>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                                  Slot-Berechnung
                                </label>
                                <select
                                  value={autoAiConfig.slot_calculation_mode || 'manual'}
                                  onChange={(e) => {
                                    const mode = e.target.value;
                                    const totalEmails = campaignData.central_recipients?.length || 0;
                                    let calculatedSlots = autoAiConfig.slot_count;
                                    
                                    if (mode === 'max_optimal') {
                                      // Maximal mögliche Slots (min 50 E-Mails/Slot)
                                      calculatedSlots = Math.max(1, Math.floor(totalEmails / 50));
                                    } else if (mode === 'balanced') {
                                      // Gleichmäßig: ~100 E-Mails/Slot
                                      calculatedSlots = Math.max(1, Math.floor(totalEmails / 100));
                                    } else if (mode === 'conservative') {
                                      // Konservativ: ~200 E-Mails/Slot
                                      calculatedSlots = Math.max(1, Math.floor(totalEmails / 200));
                                    } else if (mode === 'aggressive') {
                                      // Aggressiv: ~25 E-Mails/Slot (mehr Slots)
                                      calculatedSlots = Math.max(1, Math.floor(totalEmails / 25));
                                    }
                                    
                                    setAutoAiConfig(prev => ({
                                      ...prev, 
                                      slot_calculation_mode: mode,
                                      slot_count: mode === 'manual' ? prev.slot_count : calculatedSlots
                                    }));
                                    
                                    if (mode !== 'manual') {
                                      toast.success(`📊 ${calculatedSlots} Slots berechnet (${Math.round(totalEmails/calculatedSlots)} E-Mails/Slot)`);
                                    }
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-dark-surface dark:text-gray-200"
                                >
                                  <option value="manual">✏️ Manuell</option>
                                  <option value="aggressive">⚡ Aggressiv (~25 E-Mails/Slot)</option>
                                  <option value="max_optimal">🎯 Optimal (~50 E-Mails/Slot)</option>
                                  <option value="balanced">⚖️ Gleichmäßig (~100 E-Mails/Slot)</option>
                                  <option value="conservative">🛡️ Konservativ (~200 E-Mails/Slot)</option>
                                </select>
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                                  Anzahl Slots
                                  {autoAiConfig.slot_calculation_mode && autoAiConfig.slot_calculation_mode !== 'manual' && (
                                    <span className="ml-2 text-xs text-green-600 dark:text-green-400">✓ Auto-berechnet</span>
                                  )}
                                </label>
                                <input
                                  type="number"
                                  value={autoAiConfig.slot_count}
                                  onChange={(e) => setAutoAiConfig(prev => ({
                                    ...prev, 
                                    slot_count: parseInt(e.target.value),
                                    slot_calculation_mode: 'manual' // Zurück auf manuell wenn User ändert
                                  }))}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-dark-surface dark:text-gray-200"
                                  min="1"
                                  max="1000"
                                  disabled={autoAiConfig.slot_calculation_mode && autoAiConfig.slot_calculation_mode !== 'manual'}
                                />
                                {campaignData.central_recipients?.length > 0 && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    ≈ {Math.round((campaignData.central_recipients?.length || 0) / autoAiConfig.slot_count)} E-Mails pro Slot
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                                SMTP-Verteilung
                              </label>
                              <select
                                value={autoAiConfig.smtp_mode}
                                onChange={(e) => setAutoAiConfig(prev => ({...prev, smtp_mode: e.target.value}))}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-dark-surface dark:text-gray-200"
                              >
                                <option value="rotation">🔄 Alle Server rotieren (jeder Slot nutzt alle)</option>
                                <option value="sequential">📊 Sequential (je 1 Server pro Slot)</option>
                                <option value="mixed">🎲 Gemischt (zufällig)</option>
                                <option value="segmented">📊 Segmentiert (manuell zuweisen)</option>
                              </select>
                            </div>
                          </>
                        ) : (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                              Automatische Slots
                            </label>
                            <div className="px-3 py-2 bg-purple-100 border border-purple-300 rounded-md text-sm font-medium text-purple-900">
                              🌐 {[...new Set(selectableSmtpServers.map(s => s.from_email.split('@')[1].split('.').slice(-2).join('.')))].length} Domains = {[...new Set(selectableSmtpServers.map(s => s.from_email.split('@')[1].split('.').slice(-2).join('.')))].length} Slots
                            </div>
                          </div>
                        )}
                        </div>
                        
                        {/* 📊 SMTP-SEGMENTIERUNG UI (nur im manuellen Modus mit segmented) */}
                        {autoAiConfig.mode === 'manual' && autoAiConfig.smtp_mode === 'segmented' && (
                          <div className="bg-blue-50 dark:bg-dark-surface border border-blue-200 dark:border-dark-copper-dark rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                📊 SMTP-Server-Segmentierung
                              </h5>
                              <button
                                onClick={() => {
                                  const newSegmentation = [...autoAiConfig.smtp_segmentation];
                                  newSegmentation.push({
                                    server_id: selectableSmtpServers[0]?.id || null,
                                    start_slot: 1,
                                    end_slot: autoAiConfig.slot_count
                                  });
                                  setAutoAiConfig(prev => ({...prev, smtp_segmentation: newSegmentation}));
                                }}
                                className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700"
                              >
                                + Server hinzufügen
                              </button>
                            </div>
                            
                            <div className="space-y-2">
                              {autoAiConfig.smtp_segmentation.map((seg, index) => (
                                <div key={index} className="flex items-center space-x-2">
                                  <select
                                    value={seg.server_id || ''}
                                    onChange={(e) => {
                                      const newSeg = [...autoAiConfig.smtp_segmentation];
                                      newSeg[index].server_id = parseInt(e.target.value);
                                      setAutoAiConfig(prev => ({...prev, smtp_segmentation: newSeg}));
                                    }}
                                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                  >
                                    <option value="">Server wählen</option>
                                    {selectableSmtpServers.map(smtp => (
                                      <option key={smtp.id} value={smtp.id}>
                                        {smtp.from_email} ({smtp.host})
                                      </option>
                                    ))}
                                  </select>
                                  
                                  <span className="text-xs text-gray-600">Slot</span>
                                  <input
                                    type="number"
                                    value={seg.start_slot}
                                    onChange={(e) => {
                                      const newSeg = [...autoAiConfig.smtp_segmentation];
                                      newSeg[index].start_slot = parseInt(e.target.value) || 1;
                                      setAutoAiConfig(prev => ({...prev, smtp_segmentation: newSeg}));
                                    }}
                                    className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                                    min="1"
                                    max={autoAiConfig.slot_count}
                                  />
                                  
                                  <span className="text-xs text-gray-600">bis</span>
                                  <input
                                    type="number"
                                    value={seg.end_slot}
                                    onChange={(e) => {
                                      const newSeg = [...autoAiConfig.smtp_segmentation];
                                      newSeg[index].end_slot = parseInt(e.target.value) || autoAiConfig.slot_count;
                                      setAutoAiConfig(prev => ({...prev, smtp_segmentation: newSeg}));
                                    }}
                                    className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                                    min="1"
                                    max={autoAiConfig.slot_count}
                                  />
                                  
                                  <button
                                    onClick={() => {
                                      const newSeg = autoAiConfig.smtp_segmentation.filter((_, i) => i !== index);
                                      setAutoAiConfig(prev => ({...prev, smtp_segmentation: newSeg}));
                                    }}
                                    className="px-2 py-1 bg-red-500 text-white text-xs rounded-md hover:bg-red-600"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                            
                            {autoAiConfig.smtp_segmentation.length === 0 && (
                              <p className="text-xs text-gray-500 mt-2">
                                Klicke auf "+ Server hinzufügen" um SMTP-Server zu Slot-Bereichen zuzuweisen
                              </p>
                            )}
                          </div>
                        )}
                        
                        {/* 🤖 INTELLIGENT AUTO-SLOT (Professionelle Strategien) */}
                        {autoAiConfig.mode === 'manual' && autoAiConfig.detected_providers.length > 0 && selectableSmtpServers.length > 0 && (
                          <div className="bg-gradient-to-r from-cyan-50 to-teal-50 dark:bg-dark-surface border-2 border-cyan-400 dark:border-cyan-500 rounded-lg p-4 shadow-md mb-4">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <h5 className="text-base font-bold text-cyan-900 dark:text-cyan-300">
                                  🤖 Intelligente Auto-Slot-Strategien
                                </h5>
                                <p className="text-sm text-cyan-700 dark:text-cyan-400 font-medium mt-1">
                                  Professionelle Segmentierung per Klick • {selectableSmtpServers.length} SMTP-Server • {autoAiConfig.detected_providers.length} Provider
                                </p>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {/* Strategie 1: Round-Robin */}
                              <button
                                onClick={() => {
                                  const newProviderSeg = [];
                                  const newSmtpSeg = [];
                                  const providers = autoAiConfig.detected_providers.slice(0, Math.min(5, autoAiConfig.detected_providers.length));
                                  
                                  // 🎯 INTELLIGENT: Berechne optimale Slot-Anzahl basierend auf E-Mails
                                  const totalEmails = campaignData.central_recipients?.length || 0;
                                  const minEmailsPerSlot = 50; // Mindestens 50 E-Mails pro Slot
                                  const maxSlots = Math.floor(totalEmails / minEmailsPerSlot);
                                  const optimalSlotCount = Math.min(autoAiConfig.slot_count, maxSlots);
                                  
                                  if (optimalSlotCount < autoAiConfig.slot_count) {
                                    toast.warning(`⚠️ Slot-Anzahl auf ${optimalSlotCount} reduziert (mind. ${minEmailsPerSlot} E-Mails/Slot)`);
                                  }
                                  
                                  const slotsPerServer = Math.floor(optimalSlotCount / selectableSmtpServers.length);
                                  let currentSlot = 1;
                                  
                                  selectableSmtpServers.forEach((smtp, serverIdx) => {
                                    const serverStartSlot = currentSlot;
                                    const serverEndSlot = Math.min(currentSlot + slotsPerServer - 1, optimalSlotCount);
                                    
                                    // SMTP-Zuweisung
                                    newSmtpSeg.push({
                                      server_id: smtp.id,
                                      start_slot: serverStartSlot,
                                      end_slot: serverEndSlot
                                    });
                                    
                                    // Provider innerhalb dieses Server-Bereichs rotieren
                                    const slotsInServerRange = serverEndSlot - serverStartSlot + 1;
                                    const slotsPerProvider = Math.floor(slotsInServerRange / providers.length);
                                    
                                    providers.forEach((prov, provIdx) => {
                                      const rotatedIdx = (provIdx + serverIdx) % providers.length;
                                      const provider = providers[rotatedIdx];
                                      const startSlot = serverStartSlot + rotatedIdx * slotsPerProvider;
                                      const endSlot = Math.min(startSlot + slotsPerProvider - 1, serverEndSlot);
                                      
                                      if (startSlot <= serverEndSlot && startSlot <= optimalSlotCount) {
                                        newProviderSeg.push({
                                          provider: provider.provider,
                                          start_slot: startSlot,
                                          end_slot: Math.min(endSlot, optimalSlotCount)
                                        });
                                      }
                                    });
                                    
                                    currentSlot = serverEndSlot + 1;
                                  });
                                  
                                  // Update Slot-Count
                                  setAutoAiConfig(prev => ({
                                    ...prev, 
                                    slot_count: optimalSlotCount,
                                    smtp_mode: 'segmented',
                                    smtp_segmentation: newSmtpSeg,
                                    provider_segmentation_enabled: true,
                                    provider_segmentation: newProviderSeg
                                  }));
                                  toast.success(`🔄 Round-Robin: ${optimalSlotCount} Slots • ${selectableSmtpServers.length} Server × ${providers.length} Provider!`);
                                }}
                                className="p-3 bg-white dark:bg-dark-background rounded-lg border-2 border-cyan-300 dark:border-cyan-600 hover:border-cyan-500 hover:shadow-lg transition-all text-left"
                              >
                                <div className="font-bold text-cyan-900 dark:text-cyan-300 mb-1">🔄 Round-Robin</div>
                                <div className="text-xs text-cyan-700 dark:text-cyan-400">Jeder SMTP-Server bekommt alle Provider in rotierter Reihenfolge (auto-optimiert)</div>
                              </button>
                              
                              {/* Strategie 2: Provider-Isolation */}
                              <button
                                onClick={() => {
                                  const newProviderSeg = [];
                                  const newSmtpSeg = [];
                                  
                                  // 🎯 INTELLIGENT: Berechne optimale Slot-Anzahl
                                  const totalEmails = campaignData.central_recipients?.length || 0;
                                  const minEmailsPerSlot = 50;
                                  const maxSlots = Math.floor(totalEmails / minEmailsPerSlot);
                                  
                                  // Kombiniere Provider und SMTP-Server in 1:1 Zuordnung
                                  const maxCombinations = Math.min(autoAiConfig.detected_providers.length, selectableSmtpServers.length);
                                  const optimalSlotCount = Math.min(autoAiConfig.slot_count, maxSlots);
                                  const slotsPerCombination = Math.floor(optimalSlotCount / maxCombinations);
                                  
                                  if (optimalSlotCount < autoAiConfig.slot_count) {
                                    toast.warning(`⚠️ Slot-Anzahl auf ${optimalSlotCount} reduziert (mind. ${minEmailsPerSlot} E-Mails/Slot)`);
                                  }
                                  
                                  for (let i = 0; i < maxCombinations; i++) {
                                    const provider = autoAiConfig.detected_providers[i];
                                    const smtp = selectableSmtpServers[i];
                                    const startSlot = i * slotsPerCombination + 1;
                                    const endSlot = Math.min((i + 1) * slotsPerCombination, optimalSlotCount);
                                    
                                    // Jeder Provider bekommt exklusiv einen SMTP-Server
                                    newProviderSeg.push({
                                      provider: provider.provider,
                                      start_slot: startSlot,
                                      end_slot: endSlot
                                    });
                                    
                                    newSmtpSeg.push({
                                      server_id: smtp.id,
                                      start_slot: startSlot,
                                      end_slot: endSlot
                                    });
                                  }
                                  
                                  setAutoAiConfig(prev => ({
                                    ...prev, 
                                    slot_count: optimalSlotCount,
                                    smtp_mode: 'segmented',
                                    smtp_segmentation: newSmtpSeg,
                                    provider_segmentation_enabled: true,
                                    provider_segmentation: newProviderSeg
                                  }));
                                  toast.success(`🎯 Provider-Isolation: ${optimalSlotCount} Slots • ${maxCombinations} dedizierte Paare!`);
                                }}
                                className="p-3 bg-white dark:bg-dark-background rounded-lg border-2 border-cyan-300 dark:border-cyan-600 hover:border-cyan-500 hover:shadow-lg transition-all text-left"
                              >
                                <div className="font-bold text-cyan-900 dark:text-cyan-300 mb-1">🎯 Provider-Isolation</div>
                                <div className="text-xs text-cyan-700 dark:text-cyan-400">1:1 Zuordnung: Jeder Provider + eigener SMTP-Server (auto-optimiert)</div>
                              </button>
                              
                              {/* Strategie 3: Weighted Distribution */}
                              <button
                                onClick={() => {
                                  const newProviderSeg = [];
                                  const newSmtpSeg = [];
                                  
                                  // 🎯 INTELLIGENT: Auto-Optimierung
                                  const totalEmails = campaignData.central_recipients?.length || 0;
                                  const minEmailsPerSlot = 50;
                                  const maxSlots = Math.floor(totalEmails / minEmailsPerSlot);
                                  const optimalSlotCount = Math.min(autoAiConfig.slot_count, maxSlots);
                                  
                                  if (optimalSlotCount < autoAiConfig.slot_count) {
                                    toast.warning(`⚠️ Slot-Anzahl auf ${optimalSlotCount} reduziert (mind. ${minEmailsPerSlot} E-Mails/Slot)`);
                                  }
                                  
                                  const providerTotalEmails = autoAiConfig.detected_providers.reduce((sum, p) => sum + p.count, 0);
                                  let currentSlot = 1;
                                  
                                  // Verteile Slots proportional zur E-Mail-Anzahl
                                  autoAiConfig.detected_providers.forEach((prov, idx) => {
                                    const percentage = prov.count / providerTotalEmails;
                                    const slotsForProvider = Math.max(1, Math.round(optimalSlotCount * percentage));
                                    const endSlot = Math.min(currentSlot + slotsForProvider - 1, optimalSlotCount);
                                    
                                    if (currentSlot <= optimalSlotCount) {
                                      newProviderSeg.push({
                                        provider: prov.provider,
                                        start_slot: currentSlot,
                                        end_slot: endSlot
                                      });
                                      
                                      // SMTP-Server rotieren basierend auf Provider-Index
                                      const smtpIdx = idx % selectableSmtpServers.length;
                                      newSmtpSeg.push({
                                        server_id: selectableSmtpServers[smtpIdx].id,
                                        start_slot: currentSlot,
                                        end_slot: endSlot
                                      });
                                      
                                      currentSlot = endSlot + 1;
                                    }
                                  });
                                  
                                  setAutoAiConfig(prev => ({
                                    ...prev, 
                                    slot_count: optimalSlotCount,
                                    smtp_mode: 'segmented',
                                    smtp_segmentation: newSmtpSeg,
                                    provider_segmentation_enabled: true,
                                    provider_segmentation: newProviderSeg
                                  }));
                                  toast.success(`⚖️ Weighted: ${optimalSlotCount} Slots proportional verteilt!`);
                                }}
                                className="p-3 bg-white dark:bg-dark-background rounded-lg border-2 border-cyan-300 dark:border-cyan-600 hover:border-cyan-500 hover:shadow-lg transition-all text-left"
                              >
                                <div className="font-bold text-cyan-900 dark:text-cyan-300 mb-1">⚖️ Weighted Distribution</div>
                                <div className="text-xs text-cyan-700 dark:text-cyan-400">Proportional: Slots nach Empfängeranzahl (auto-optimiert)</div>
                              </button>
                              
                              {/* Strategie 4: Reverse Pyramid */}
                              <button
                                onClick={() => {
                                  const newProviderSeg = [];
                                  const newSmtpSeg = [];
                                  
                                  // 🎯 INTELLIGENT: Auto-Optimierung
                                  const totalEmails = campaignData.central_recipients?.length || 0;
                                  const minEmailsPerSlot = 50;
                                  const maxSlots = Math.floor(totalEmails / minEmailsPerSlot);
                                  const optimalSlotCount = Math.min(autoAiConfig.slot_count, maxSlots);
                                  
                                  if (optimalSlotCount < autoAiConfig.slot_count) {
                                    toast.warning(`⚠️ Slot-Anzahl auf ${optimalSlotCount} reduziert (mind. ${minEmailsPerSlot} E-Mails/Slot)`);
                                  }
                                  
                                  // Kleinste Provider zuerst (IP Warm-Up Strategie)
                                  const reversedProviders = [...autoAiConfig.detected_providers].reverse();
                                  const slotsPerProvider = Math.floor(optimalSlotCount / reversedProviders.length);
                                  
                                  reversedProviders.forEach((prov, idx) => {
                                    const startSlot = idx * slotsPerProvider + 1;
                                    const endSlot = Math.min((idx + 1) * slotsPerProvider, optimalSlotCount);
                                    
                                    if (startSlot <= optimalSlotCount) {
                                      newProviderSeg.push({
                                        provider: prov.provider,
                                        start_slot: startSlot,
                                        end_slot: endSlot
                                      });
                                      
                                      // SMTP-Server auch reverse (warme IPs für große Provider am Ende)
                                      const smtpIdx = (selectableSmtpServers.length - 1 - (idx % selectableSmtpServers.length));
                                      newSmtpSeg.push({
                                        server_id: selectableSmtpServers[smtpIdx].id,
                                        start_slot: startSlot,
                                        end_slot: endSlot
                                      });
                                    }
                                  });
                                  
                                  setAutoAiConfig(prev => ({
                                    ...prev, 
                                    slot_count: optimalSlotCount,
                                    smtp_mode: 'segmented',
                                    smtp_segmentation: newSmtpSeg,
                                    provider_segmentation_enabled: true,
                                    provider_segmentation: newProviderSeg
                                  }));
                                  toast.success(`🔺 Reverse Pyramid: ${optimalSlotCount} Slots • Warm-Up aktiviert!`);
                                }}
                                className="p-3 bg-white dark:bg-dark-background rounded-lg border-2 border-cyan-300 dark:border-cyan-600 hover:border-cyan-500 hover:shadow-lg transition-all text-left"
                              >
                                <div className="font-bold text-cyan-900 dark:text-cyan-300 mb-1">🔺 Reverse Pyramid</div>
                                <div className="text-xs text-cyan-700 dark:text-cyan-400">Warm-Up: Kleine Provider zuerst (auto-optimiert)</div>
                              </button>
                              
                              {/* Strategie 5: Interleaved Mix */}
                              <button
                                onClick={() => {
                                  const newProviderSeg = [];
                                  const newSmtpSeg = [];
                                  
                                  // 🎯 INTELLIGENT: Auto-Optimierung
                                  const totalEmails = campaignData.central_recipients?.length || 0;
                                  const minEmailsPerSlot = 50;
                                  const maxSlots = Math.floor(totalEmails / minEmailsPerSlot);
                                  const optimalSlotCount = Math.min(autoAiConfig.slot_count, maxSlots);
                                  
                                  if (optimalSlotCount < autoAiConfig.slot_count) {
                                    toast.warning(`⚠️ Slot-Anzahl auf ${optimalSlotCount} reduziert (mind. ${minEmailsPerSlot} E-Mails/Slot)`);
                                  }
                                  
                                  // Verwende ALLE Provider (inkl. "Rest"), nicht nur Top 4!
                                  const providers = autoAiConfig.detected_providers;
                                  let currentSlot = 1;
                                  
                                  // Provider und SMTP abwechselnd (Rate-Limit-Schutz)
                                  while (currentSlot <= optimalSlotCount) {
                                    for (const prov of providers) {
                                      if (currentSlot > optimalSlotCount) {
                                        break;
                                      }
                                      
                                      // Jeder Slot = 1 Provider
                                      newProviderSeg.push({
                                        provider: prov.provider,
                                        start_slot: currentSlot,
                                        end_slot: currentSlot
                                      });
                                      
                                      // SMTP-Server auch abwechselnd
                                      const smtpIdx = currentSlot % selectableSmtpServers.length;
                                      newSmtpSeg.push({
                                        server_id: selectableSmtpServers[smtpIdx].id,
                                        start_slot: currentSlot,
                                        end_slot: currentSlot
                                      });
                                      
                                      currentSlot++;
                                    }
                                  }
                                  
                                  setAutoAiConfig(prev => ({
                                    ...prev, 
                                    slot_count: optimalSlotCount,
                                    smtp_mode: 'segmented',
                                    smtp_segmentation: newSmtpSeg,
                                    provider_segmentation_enabled: true,
                                    provider_segmentation: newProviderSeg
                                  }));
                                  toast.success(`🔀 Interleaved: ${optimalSlotCount} Slots • Alle ${providers.length} Provider abwechselnd!`);
                                }}
                                className="p-3 bg-white dark:bg-dark-background rounded-lg border-2 border-cyan-300 dark:border-cyan-600 hover:border-cyan-500 hover:shadow-lg transition-all text-left"
                              >
                                <div className="font-bold text-cyan-900 dark:text-cyan-300 mb-1">🔀 Interleaved Mix</div>
                                <div className="text-xs text-cyan-700 dark:text-cyan-400">Abwechselnd: Slot-für-Slot Rotation (auto-optimiert)</div>
                              </button>
                              
                              {/* Strategie 6: Major-First */}
                              <button
                                onClick={() => {
                                  const newProviderSeg = [];
                                  const newSmtpSeg = [];
                                  
                                  // 🎯 INTELLIGENT: Auto-Optimierung
                                  const totalEmails = campaignData.central_recipients?.length || 0;
                                  const minEmailsPerSlot = 50;
                                  const maxSlots = Math.floor(totalEmails / minEmailsPerSlot);
                                  const optimalSlotCount = Math.min(autoAiConfig.slot_count, maxSlots);
                                  
                                  if (optimalSlotCount < autoAiConfig.slot_count) {
                                    toast.warning(`⚠️ Slot-Anzahl auf ${optimalSlotCount} reduziert (mind. ${minEmailsPerSlot} E-Mails/Slot)`);
                                  }
                                  
                                  // Große Provider priorisieren
                                  const majorProviders = ['Gmail', 'Outlook', 'GMX', 'Mail.com', 'Web.de', 'T-Online'];
                                  const foundMajor = autoAiConfig.detected_providers.filter(p => majorProviders.includes(p.provider));
                                  const foundRest = autoAiConfig.detected_providers.filter(p => !majorProviders.includes(p.provider));
                                  const allProviders = [...foundMajor, ...foundRest];
                                  
                                  const slotsPerProvider = Math.floor(optimalSlotCount / allProviders.length);
                                  
                                  allProviders.forEach((prov, idx) => {
                                    const startSlot = idx * slotsPerProvider + 1;
                                    const endSlot = Math.min((idx + 1) * slotsPerProvider, optimalSlotCount);
                                    
                                    if (startSlot <= optimalSlotCount) {
                                      newProviderSeg.push({
                                        provider: prov.provider,
                                        start_slot: startSlot,
                                        end_slot: endSlot
                                      });
                                      
                                      // SMTP: Beste Server für Major Provider, Rest für andere
                                      const isMajor = majorProviders.includes(prov.provider);
                                      const smtpIdx = isMajor 
                                        ? (idx % Math.ceil(selectableSmtpServers.length / 2))  // Erste Hälfte für Major
                                        : Math.ceil(selectableSmtpServers.length / 2) + (idx % Math.floor(selectableSmtpServers.length / 2)); // Zweite Hälfte für Rest
                                      
                                      newSmtpSeg.push({
                                        server_id: selectableSmtpServers[Math.min(smtpIdx, selectableSmtpServers.length - 1)].id,
                                        start_slot: startSlot,
                                        end_slot: endSlot
                                      });
                                    }
                                  });
                                  
                                  setAutoAiConfig(prev => ({
                                    ...prev, 
                                    slot_count: optimalSlotCount,
                                    smtp_mode: 'segmented',
                                    smtp_segmentation: newSmtpSeg,
                                    provider_segmentation_enabled: true,
                                    provider_segmentation: newProviderSeg
                                  }));
                                  toast.success(`🏆 Major-First: ${optimalSlotCount} Slots • ${foundMajor.length} große Provider priorisiert!`);
                                }}
                                className="p-3 bg-white dark:bg-dark-background rounded-lg border-2 border-cyan-300 dark:border-cyan-600 hover:border-cyan-500 hover:shadow-lg transition-all text-left"
                              >
                                <div className="font-bold text-cyan-900 dark:text-cyan-300 mb-1">🏆 Major-First</div>
                                <div className="text-xs text-cyan-700 dark:text-cyan-400">Priorisiert: Große Provider zuerst (auto-optimiert)</div>
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {/* 🌐 PROVIDER-SEGMENTIERUNG UI (im manuellen Modus) */}
                        {autoAiConfig.mode === 'manual' && autoAiConfig.detected_providers.length > 0 && (
                          <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:bg-dark-surface border-2 border-purple-300 dark:border-purple-500 rounded-lg p-4 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                              <div>
                                <h5 className="text-base font-bold text-purple-900 dark:text-purple-300">
                                  🌐 Provider-Segmentierung
                                </h5>
                                <p className="text-sm text-purple-700 dark:text-purple-400 mt-1 font-medium">
                                  {autoAiConfig.detected_providers.length} Provider erkannt • {campaignData.central_recipients?.length || 0} E-Mails gesamt
                                </p>
                              </div>
                              <label className="flex items-center space-x-2 cursor-pointer bg-white dark:bg-dark-background px-3 py-2 rounded-lg shadow-sm">
                                <input
                                  type="checkbox"
                                  checked={autoAiConfig.provider_segmentation_enabled}
                                  onChange={(e) => setAutoAiConfig(prev => ({...prev, provider_segmentation_enabled: e.target.checked}))}
                                  className="h-4 w-4 text-purple-600 border-gray-300 rounded"
                                />
                                <span className="text-sm font-bold text-purple-700 dark:text-purple-300">Aktiviert</span>
                              </label>
                            </div>
                            
                            {autoAiConfig.provider_segmentation_enabled && (
                              <>
                                {/* Provider-Übersicht */}
                                <div className="mb-4 p-3 bg-white dark:bg-dark-background rounded-lg border border-purple-200 dark:border-purple-600">
                                  <strong className="text-sm text-purple-900 dark:text-purple-300 block mb-2">📊 Verfügbare Provider:</strong>
                                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                    {autoAiConfig.detected_providers.map((prov, idx) => (
                                      <div key={idx} className="px-3 py-2 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900 dark:to-blue-900 rounded-lg border border-purple-300 dark:border-purple-600">
                                        <div className="text-sm font-bold text-purple-900 dark:text-purple-200">{prov.provider}</div>
                                        <div className="text-xs text-purple-700 dark:text-purple-400 font-semibold">{prov.count.toLocaleString()} E-Mails</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                
                                {/* Statistik: Belegte/Freie Slots */}
                                {(() => {
                                  const assignedSlots = new Set();
                                  const totalEmails = campaignData.central_recipients?.length || 0;
                                  const totalSlots = autoAiConfig.slot_count;
                                  
                                  // Sammle alle zugewiesenen Slots
                                  autoAiConfig.provider_segmentation.forEach(seg => {
                                    for (let i = seg.start_slot; i <= seg.end_slot; i++) {
                                      assignedSlots.add(i);
                                    }
                                  });
                                  
                                  // Berechne zugewiesene E-Mails
                                  // Wenn ALLE Slots belegt sind, sind ALLE E-Mails zugewiesen!
                                  const allSlotsCovered = assignedSlots.size === totalSlots;
                                  const totalAssignedEmails = allSlotsCovered ? totalEmails : Math.round((assignedSlots.size / totalSlots) * totalEmails);
                                  
                                  const freeSlots = totalSlots - assignedSlots.size;
                                  const unassignedEmails = totalEmails - totalAssignedEmails;
                                  const emailsPerSlot = totalEmails / totalSlots;
                                  
                                  return (
                                    <div className="mb-4 p-3 bg-gradient-to-r from-yellow-50 to-orange-50 dark:bg-dark-background rounded-lg border-2 border-orange-300 dark:border-orange-500">
                                      <div className="grid grid-cols-2 gap-4">
                                        <div>
                                          <div className="text-xs text-orange-700 dark:text-orange-400 font-bold">📊 Slots</div>
                                          <div className="text-lg font-bold text-orange-900 dark:text-orange-300">
                                            {assignedSlots.size} / {totalSlots} belegt
                                          </div>
                                          <div className="text-sm text-orange-800 dark:text-orange-400 font-semibold">
                                            {freeSlots > 0 ? `${freeSlots} Slots noch frei` : 'Alle Slots belegt'}
                                          </div>
                                        </div>
                                        <div>
                                          <div className="text-xs text-orange-700 dark:text-orange-400 font-bold">📧 E-Mails</div>
                                          <div className="text-lg font-bold text-orange-900 dark:text-orange-300">
                                            {totalAssignedEmails.toLocaleString()} / {totalEmails.toLocaleString()}
                                          </div>
                                          <div className="text-sm text-orange-800 dark:text-orange-400 font-semibold">
                                            {unassignedEmails > 0 ? `${unassignedEmails.toLocaleString()} noch offen` : 'Alle zugewiesen'}
                                          </div>
                                          <div className="text-xs text-orange-600 dark:text-orange-500 mt-1">
                                            ≈ {Math.round(emailsPerSlot)} E-Mails/Slot
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}
                                
                                <button
                                  onClick={() => {
                                    const newSegmentation = [...autoAiConfig.provider_segmentation];
                                    newSegmentation.push({
                                      provider: autoAiConfig.detected_providers[0]?.provider || '',
                                      start_slot: 1,
                                      end_slot: 10
                                    });
                                    setAutoAiConfig(prev => ({...prev, provider_segmentation: newSegmentation}));
                                  }}
                                  className="w-full mb-3 px-4 py-2 bg-purple-600 text-white text-sm font-bold rounded-lg hover:bg-purple-700 shadow-md"
                                >
                                  + Provider-Bereich hinzufügen
                                </button>
                                
                                {/* Provider-Zuweisungen */}
                                <div className="space-y-3">
                                  {autoAiConfig.provider_segmentation.map((seg, index) => {
                                    const provider = autoAiConfig.detected_providers.find(p => p.provider === seg.provider);
                                    const slotCount = seg.end_slot - seg.start_slot + 1;
                                    
                                    // Berechne E-Mails die TATSÄCHLICH in diese Slots passen
                                    const totalEmails = campaignData.central_recipients?.length || 0;
                                    const emailsPerSlot = totalEmails / autoAiConfig.slot_count;
                                    const emailsInThisSegment = Math.round(slotCount * emailsPerSlot);
                                    const emailsPerSlotInSegment = Math.round(emailsInThisSegment / slotCount);
                                    
                                    return (
                                      <div key={index} className="p-3 bg-white dark:bg-dark-background rounded-lg border-2 border-purple-200 dark:border-purple-600 shadow-sm">
                                        <div className="flex items-center space-x-2 mb-2">
                                          <select
                                            value={seg.provider}
                                            onChange={(e) => {
                                              const newSeg = [...autoAiConfig.provider_segmentation];
                                              newSeg[index].provider = e.target.value;
                                              setAutoAiConfig(prev => ({...prev, provider_segmentation: newSeg}));
                                            }}
                                            className="flex-1 px-3 py-2 border-2 border-purple-300 dark:border-purple-600 rounded-lg text-sm font-bold text-purple-900 dark:text-purple-200 dark:bg-dark-surface"
                                          >
                                            <option value="">Provider wählen</option>
                                            {autoAiConfig.detected_providers.map((prov, idx) => (
                                              <option key={idx} value={prov.provider}>
                                                {prov.provider} ({prov.count.toLocaleString()} E-Mails verfügbar)
                                              </option>
                                            ))}
                                          </select>
                                          
                                          <button
                                            onClick={() => {
                                              const newSeg = autoAiConfig.provider_segmentation.filter((_, i) => i !== index);
                                              setAutoAiConfig(prev => ({...prev, provider_segmentation: newSeg}));
                                            }}
                                            className="px-3 py-2 bg-red-500 text-white text-sm font-bold rounded-lg hover:bg-red-600 shadow-md"
                                          >
                                            ×
                                          </button>
                                        </div>
                                        
                                        <div className="flex items-center space-x-2">
                                          <span className="text-sm font-bold text-purple-900 dark:text-purple-300">Slots:</span>
                                          <input
                                            type="number"
                                            value={seg.start_slot}
                                            onChange={(e) => {
                                              const newSeg = [...autoAiConfig.provider_segmentation];
                                              newSeg[index].start_slot = parseInt(e.target.value) || 1;
                                              setAutoAiConfig(prev => ({...prev, provider_segmentation: newSeg}));
                                            }}
                                            className="w-20 px-3 py-1 border-2 border-purple-300 dark:border-purple-600 rounded-lg text-sm font-bold text-purple-900 dark:text-purple-200 dark:bg-dark-surface"
                                            min="1"
                                            max={autoAiConfig.slot_count}
                                          />
                                          
                                          <span className="text-sm font-bold text-purple-900 dark:text-purple-300">bis</span>
                                          <input
                                            type="number"
                                            value={seg.end_slot}
                                            onChange={(e) => {
                                              const newSeg = [...autoAiConfig.provider_segmentation];
                                              newSeg[index].end_slot = parseInt(e.target.value) || autoAiConfig.slot_count;
                                              setAutoAiConfig(prev => ({...prev, provider_segmentation: newSeg}));
                                            }}
                                            className="w-20 px-3 py-1 border-2 border-purple-300 dark:border-purple-600 rounded-lg text-sm font-bold text-purple-900 dark:text-purple-200 dark:bg-dark-surface"
                                            min="1"
                                            max={autoAiConfig.slot_count}
                                          />
                                        </div>
                                        
                                        {provider && (
                                          <div className="mt-2 p-2 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900 dark:to-emerald-900 rounded border border-green-300 dark:border-green-600">
                                            <div className="text-sm font-bold text-green-900 dark:text-green-300">
                                              📊 {emailsInThisSegment.toLocaleString()} E-Mails in {slotCount} Slots = ~{emailsPerSlotInSegment} E-Mails/Slot
                                            </div>
                                            <div className="text-xs text-green-700 dark:text-green-400 mt-1">
                                              Provider hat {provider.count.toLocaleString()} E-Mails verfügbar
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                                
                                {autoAiConfig.provider_segmentation.length === 0 && (
                                  <p className="text-sm text-purple-700 dark:text-purple-400 mt-2 font-semibold text-center">
                                    Klicke auf "+ Provider-Bereich hinzufügen" um loszulegen
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        )}
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                            Templates pro Slot
                          </label>
                          <select
                            value={autoAiConfig.templates_per_slot}
                            onChange={(e) => setAutoAiConfig(prev => ({...prev, templates_per_slot: parseInt(e.target.value)}))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          >
                            <option value={1}>1 Template</option>
                            <option value={2}>2 Templates (Rotation)</option>
                            <option value={3}>3 Templates (Rotation)</option>
                            <option value={5}>5 Templates (Rotation)</option>
                          </select>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                              E-Mails/Stunde (Rate-Limit)
                            </label>
                            <input
                              type="number"
                              value={autoAiConfig.template.emails_per_hour}
                              onChange={(e) => setAutoAiConfig(prev => ({...prev, template: {...prev.template, emails_per_hour: parseInt(e.target.value)}}))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                              min="1"
                              max="10000"
                              placeholder="2000"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                            Subject
                          </label>
                          <input
                            type="text"
                            value={autoAiConfig.template.subject}
                            onChange={(e) => setAutoAiConfig(prev => ({...prev, template: {...prev.template, subject: e.target.value}}))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="Ihre Nachricht - Wichtige Information"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                            Absender-Name
                          </label>
                          <input
                            type="text"
                            value={autoAiConfig.template.from_name}
                            onChange={(e) => setAutoAiConfig(prev => ({...prev, template: {...prev.template, from_name: e.target.value}}))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="Kundenservice"
                          />
                        </div>
                        
                        {/* 📧 Dynamische Absender-E-Mail */}
                        <div className="col-span-2">
                          <div className="flex items-center mb-2">
                            <input
                              type="checkbox"
                              id="auto_ai_dynamic_from_email"
                              checked={autoAiConfig.template.use_dynamic_from_email}
                              onChange={(e) => setAutoAiConfig(prev => ({...prev, template: {...prev.template, use_dynamic_from_email: e.target.checked}}))}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                            <label htmlFor="auto_ai_dynamic_from_email" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                              📧 Dynamische Absender-E-Mail (für alle Slots)
                            </label>
                          </div>
                          
                          {autoAiConfig.template.use_dynamic_from_email && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                                E-Mail-Prefix (Spintax + Variable Codes)
                              </label>
                              <input
                                type="text"
                                value={autoAiConfig.template.dynamic_from_email}
                                onChange={(e) => setAutoAiConfig(prev => ({...prev, template: {...prev.template, dynamic_from_email: e.target.value}}))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                placeholder="kontakt | {info|service} | Max($)3Z($)"
                              />
                              <p className="text-xs text-gray-500 dark:text-gray-300 mt-1">
                                Wird zu: prefix@domain.de • Spintax und Variable Codes werden unterstützt
                              </p>
                            </div>
                          )}
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                            HTML-Template
                          </label>
                          <textarea
                            value={autoAiConfig.template.message}
                            onChange={(e) => setAutoAiConfig(prev => ({...prev, template: {...prev.template, message: e.target.value}}))}
                            rows={6}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
                            placeholder="<html><body><p>{Hallo|Guten Tag},</p><p>Ihre {Nachricht|Information}...</p></body></html>"
                          />
                        </div>
                        
                        {/* ⏸️ PAUSEN-KONFIGURATION */}
                        <div className="bg-blue-50 dark:bg-dark-surface border border-blue-200 dark:border-dark-copper-dark rounded-lg p-4">
                          <h5 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center space-x-2">
                            <span>⏸️</span>
                            <span>Pausen-Optionen</span>
                          </h5>
                          
                          <div className="space-y-3">
                            {/* Pause nach jedem Slot */}
                            <div className="flex items-center space-x-3">
                              <input
                                type="checkbox"
                                checked={autoAiConfig.add_pause_after_each_slot}
                                onChange={(e) => setAutoAiConfig(prev => ({...prev, add_pause_after_each_slot: e.target.checked}))}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                              />
                              <div className="flex-1 flex items-center space-x-2">
                                <span className="text-sm text-gray-700">Pause nach jedem Slot:</span>
                                <input
                                  type="number"
                                  value={autoAiConfig.pause_duration_after_each}
                                  onChange={(e) => setAutoAiConfig(prev => ({...prev, pause_duration_after_each: parseInt(e.target.value) || 30}))}
                                  disabled={!autoAiConfig.add_pause_after_each_slot}
                                  className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                  min="1"
                                  max="3600"
                                />
                                <span className="text-sm text-gray-500">Sekunden</span>
                              </div>
                            </div>
                            
                            {/* Pause nach jedem N Slot */}
                            <div className="flex items-center space-x-3">
                              <input
                                type="checkbox"
                                checked={autoAiConfig.add_pause_after_n_slots}
                                onChange={(e) => setAutoAiConfig(prev => ({...prev, add_pause_after_n_slots: e.target.checked}))}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                              />
                              <div className="flex-1 flex items-center space-x-2">
                                <span className="text-sm text-gray-700">Pause nach jedem</span>
                                <input
                                  type="number"
                                  value={autoAiConfig.pause_after_n}
                                  onChange={(e) => setAutoAiConfig(prev => ({...prev, pause_after_n: parseInt(e.target.value) || 3}))}
                                  disabled={!autoAiConfig.add_pause_after_n_slots}
                                  className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                                  min="1"
                                  max="50"
                                />
                                <span className="text-sm text-gray-700">. Slot:</span>
                                <input
                                  type="number"
                                  value={autoAiConfig.pause_duration_after_n}
                                  onChange={(e) => setAutoAiConfig(prev => ({...prev, pause_duration_after_n: parseInt(e.target.value) || 60}))}
                                  disabled={!autoAiConfig.add_pause_after_n_slots}
                                  className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                  min="1"
                                  max="3600"
                                />
                                <span className="text-sm text-gray-500">Sekunden</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* 🎨 HTML-Obfuskations-Optionen */}
                        <div className="border border-purple-300 dark:border-dark-copper-dark rounded-lg p-4 bg-purple-50 dark:bg-dark-surface">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-medium text-purple-900 dark:text-gray-100">
                              🎨 HTML-Obfuskation (für AI-Prompt)
                            </h4>
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={autoAiConfig.obfuscation.enabled}
                                onChange={(e) => setAutoAiConfig(prev => ({
                                  ...prev,
                                  obfuscation: {...prev.obfuscation, enabled: e.target.checked}
                                }))}
                                className="h-4 w-4 text-purple-600 border-gray-300 rounded"
                              />
                              <span className="text-sm text-purple-700 dark:text-gray-200">Aktiviert</span>
                            </label>
                          </div>
                          
                          {autoAiConfig.obfuscation.enabled && (
                            <div className="grid grid-cols-2 gap-3">
                              
                              {/* Garbage Comments */}
                              <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={autoAiConfig.obfuscation.garbage_comments}
                                  onChange={(e) => setAutoAiConfig(prev => ({
                                    ...prev,
                                    obfuscation: {...prev.obfuscation, garbage_comments: e.target.checked}
                                  }))}
                                  className="h-4 w-4 text-purple-600 border-gray-300 rounded"
                                />
                                <span className="text-xs text-purple-800 dark:text-gray-200">💬 Garbage Comments</span>
                              </label>
                              
                              {/* Color Variation */}
                              <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={autoAiConfig.obfuscation.color_variation}
                                  onChange={(e) => setAutoAiConfig(prev => ({
                                    ...prev,
                                    obfuscation: {...prev.obfuscation, color_variation: e.target.checked}
                                  }))}
                                  className="h-4 w-4 text-purple-600 border-gray-300 rounded"
                                />
                                <span className="text-xs text-purple-800 dark:text-gray-200">🎨 Farbvariation (±8%)</span>
                              </label>
                              
                              {/* CSS Mixing */}
                              <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={autoAiConfig.obfuscation.css_mixing}
                                  onChange={(e) => setAutoAiConfig(prev => ({
                                    ...prev,
                                    obfuscation: {...prev.obfuscation, css_mixing: e.target.checked}
                                  }))}
                                  className="h-4 w-4 text-purple-600 border-gray-300 rounded"
                                />
                                <span className="text-xs text-purple-800 dark:text-gray-200">🔀 CSS Mixing (Inline + Classes)</span>
                              </label>
                              
                              {/* Tag Diversity */}
                              <label className="flex items-center space-x-2 cursor-pointer col-span-2">
                                <input
                                  type="checkbox"
                                  checked={autoAiConfig.obfuscation.tag_diversity}
                                  onChange={(e) => setAutoAiConfig(prev => ({
                                    ...prev,
                                    obfuscation: {...prev.obfuscation, tag_diversity: e.target.checked}
                                  }))}
                                  className="h-4 w-4 text-purple-600 border-gray-300 rounded"
                                />
                                <span className="text-xs text-purple-800 dark:text-gray-200">📋 Tag-Diversität (article, aside, section, etc.)</span>
                              </label>
                            </div>
                          )}
                          
                          <p className="text-xs text-purple-600 mt-2">
                            💡 Diese Optionen bestimmen, welche Obfuskations-Techniken die AI im Prompt verwenden soll
                          </p>
                        </div>

                        {/* 🔧 Advanced Runtime Features */}
                        <div className="border border-green-300 dark:border-dark-copper-dark rounded-lg p-4 bg-green-50 dark:bg-dark-surface">
                          <h4 className="text-sm font-medium text-green-900 dark:text-gray-100 mb-3">
                            ⚡ Advanced Runtime Features
                          </h4>
                          
                          <div className="space-y-3">
                            <p className="text-xs text-green-700 dark:text-gray-200">
                              Per-Email Processing - Jede Mail ist einzigartig!
                            </p>
                            
                            <div className="grid grid-cols-2 gap-3">
                              {/* Precedence Header */}
                              <label className="flex items-center space-x-2 cursor-pointer col-span-2">
                                <input
                                  type="checkbox"
                                  checked={autoAiConfig.runtime_obfuscation.precedence_header}
                                  onChange={(e) => setAutoAiConfig(prev => ({
                                    ...prev,
                                    runtime_obfuscation: {...prev.runtime_obfuscation, precedence_header: e.target.checked}
                                  }))}
                                  className="h-4 w-4 text-green-600 border-gray-300 rounded"
                                />
                                <span className="text-xs text-green-800 dark:text-gray-200">📧 Precedence: first-class Header</span>
                              </label>
                              
                              {/* Warm-Up */}
                              <label className="flex items-center space-x-2 cursor-pointer col-span-2">
                                <input
                                  type="checkbox"
                                  checked={autoAiConfig.runtime_obfuscation.warmup_enabled}
                                  onChange={(e) => setAutoAiConfig(prev => ({
                                    ...prev,
                                    runtime_obfuscation: {...prev.runtime_obfuscation, warmup_enabled: e.target.checked}
                                  }))}
                                  className="h-4 w-4 text-green-600 border-gray-300 rounded"
                                />
                                <span className="text-xs text-green-800 dark:text-gray-200">🔥 SMTP Warm-Up</span>
                              </label>
                              
                              {autoAiConfig.runtime_obfuscation.warmup_enabled && (
                                <div className="col-span-2 ml-6">
                                  <label className="block text-xs text-gray-700 dark:text-gray-200 mb-1">
                                    Warm-Up Dauer (Minuten):
                                  </label>
                                  <input
                                    type="number"
                                    min="1"
                                    max="30"
                                    value={autoAiConfig.runtime_obfuscation.warmup_duration}
                                    onChange={(e) => setAutoAiConfig(prev => ({
                                      ...prev,
                                      runtime_obfuscation: {...prev.runtime_obfuscation, warmup_duration: parseInt(e.target.value)}
                                    }))}
                                    className="w-24 px-2 py-1 border border-gray-300 rounded text-xs"
                                  />
                                </div>
                              )}
                            </div>
                            
                            <p className="text-xs text-green-600 dark:text-gray-300 mt-2">
                              💡 Tipp: Nutze {"{zf:Text}"} im Template für Zeichen-Splitting
                            </p>
                          </div>
                        </div>
                        
                        <div className="bg-purple-100 dark:bg-dark-surface p-4 rounded border dark:border-dark-copper-dark">
                          <p className="text-sm text-purple-900 dark:text-gray-100 font-medium mb-2">
                            📊 Zusammenfassung:
                          </p>
                          <div className="space-y-1 text-xs text-purple-800 dark:text-gray-200">
                            <div>
                              <strong>Slots:</strong> {autoAiConfig.mode === 'per_domain' 
                                ? `${[...new Set(selectableSmtpServers.map(s => s.from_email.split('@')[1].split('.').slice(-2).join('.')))].length} (automatisch pro Domain)`
                                : autoAiConfig.slot_count
                              }
                            </div>
                            <div>
                              <strong>Templates pro Slot:</strong> {autoAiConfig.templates_per_slot}
                            </div>
                            <div>
                              <strong>🤖 Total AI-Versionen:</strong> {(autoAiConfig.mode === 'per_domain' ? [...new Set(selectableSmtpServers.map(s => s.from_email.split('@')[1].split('.').slice(-2).join('.')))].length : autoAiConfig.slot_count) * autoAiConfig.templates_per_slot}
                            </div>
                            <div>
                              <strong>⏱️ Geschätzte Dauer:</strong> ~{Math.ceil((autoAiConfig.mode === 'per_domain' ? [...new Set(selectableSmtpServers.map(s => s.from_email.split('@')[1].split('.').slice(-2).join('.')))].length : autoAiConfig.slot_count) * autoAiConfig.templates_per_slot / 8 * 10 / 60)} Minuten
                            </div>
                            <div className="text-purple-600 font-medium mt-2">
                              💰 Claude Kosten: ~${((autoAiConfig.mode === 'per_domain' ? [...new Set(selectableSmtpServers.map(s => s.from_email.split('@')[1].split('.').slice(-2).join('.')))].length : autoAiConfig.slot_count) * autoAiConfig.templates_per_slot * 0.02).toFixed(2)}
                            </div>
                            {(autoAiConfig.add_pause_after_each_slot || autoAiConfig.add_pause_after_n_slots) && (
                              <div className="mt-2 pt-2 border-t border-purple-300">
                                <strong>⏸️ Pausen:</strong> 
                                {autoAiConfig.add_pause_after_each_slot && ` ${autoAiConfig.pause_duration_after_each}s nach jedem Slot`}
                                {autoAiConfig.add_pause_after_each_slot && autoAiConfig.add_pause_after_n_slots && ' + '}
                                {autoAiConfig.add_pause_after_n_slots && ` ${autoAiConfig.pause_duration_after_n}s nach jedem ${autoAiConfig.pause_after_n}. Slot`}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* 🔗 AWS/Search.app Links für Auto-AI-Slots - Eigener State wie bei Segmented */}
                        {isAdmin && (
                          <SequentialAwsSearchAppConfig 
                            autoAiConfig={autoAiConfig}
                            setAutoAiConfig={setAutoAiConfig}
                            isAdmin={isAdmin}
                          />
                        )}
                        
                        <button
                          onClick={generateAutoAiSlots}
                          className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                          disabled={!autoAiConfig.template.subject || !autoAiConfig.template.message || !autoAiConfig.template.from_name}
                        >
                          🤖 AI-Slots generieren
                      </button>
                      </div>
                    </div>
                  )}
                  
                  {/* 🎯 AUTO-SLOT-KONFIGURATION PANEL */}
                  {autoSlotMode && (
                    <div className="mt-4 bg-green-50 dark:bg-dark-surface border border-green-200 dark:border-dark-copper-dark rounded-lg p-4">
                      <h4 className="text-md font-medium text-gray-900 mb-4">
                        ⚙️ Auto-Slot-Konfiguration
                      </h4>
                      
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                              Empfänger pro Slot
                            </label>
                            <select
                              value={autoSlotSize}
                              onChange={(e) => setAutoSlotSize(parseInt(e.target.value))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            >
                              <option value={50}>50er Slots</option>
                              <option value={100}>100er Slots</option>
                              <option value={250}>250er Slots</option>
                              <option value={500}>500er Slots</option>
                              <option value={1000}>1000er Slots</option>
                              <option value={2500}>2500er Slots</option>
                              <option value={5000}>5000er Slots</option>
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                              📊 Ergebnis: {Math.ceil(campaignData.central_recipients.length / autoSlotSize)} Slots
                            </p>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                              <Target className="h-4 w-4 mr-1" />
                              SMTP-Zuordnung
                            </label>
                            <select
                              value={autoSlotSmtpMode}
                              onChange={(e) => setAutoSlotSmtpMode(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            >
                              <option value="rotation">🔄 Rotation (alle SMTPs pro Slot)</option>
                              <option value="sequential">📊 Sequential (Slot 1→SMTP 1, Slot 2→SMTP 2)</option>
                              <option value="mixed">🎲 Mixed (zufällige Zuordnung)</option>
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                              {autoSlotSmtpMode === 'rotation' && '🔄 Jeder Slot rotiert durch alle SMTPs'}
                              {autoSlotSmtpMode === 'sequential' && '📊 Jeder Slot bekommt einen festen SMTP'}
                              {autoSlotSmtpMode === 'mixed' && '🎲 Jeder Slot bekommt zufälligen SMTP'}
                            </p>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                              E-Mails pro Stunde
                            </label>
                            <input
                              type="number"
                              value={autoSlotTemplate.emails_per_hour}
                              onChange={(e) => setAutoSlotTemplate(prev => ({...prev, emails_per_hour: parseInt(e.target.value)}))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                              min="1"
                              max="10000"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                            Betreff (für alle Slots)
                          </label>
                          <input
                            type="text"
                            value={autoSlotTemplate.subject}
                            onChange={(e) => setAutoSlotTemplate(prev => ({...prev, subject: e.target.value}))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            placeholder="Betreff mit Spintax: {Option1|Option2}"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                            Absender-Name (für alle Slots)
                          </label>
                          <input
                            type="text"
                            value={autoSlotTemplate.from_name}
                            onChange={(e) => setAutoSlotTemplate(prev => ({...prev, from_name: e.target.value.substring(0, 200)}))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            placeholder="Absender mit Variablen: Max($)3Z($) Mustermann"
                            maxLength={200}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {autoSlotTemplate.from_name.length}/200 Zeichen
                          </p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                            HTML-Nachricht (für alle Slots)
                          </label>
                          <textarea
                            value={autoSlotTemplate.message}
                            onChange={(e) => setAutoSlotTemplate(prev => ({...prev, message: e.target.value}))}
                            rows={8}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 font-mono text-sm"
                            placeholder="<html><body><img src='cid:logo'><p>{Spintax|Varianten} mit ($)5($) Codes</p></body></html>"
                          />
                        </div>
                        
                        <div className="flex justify-between items-center pt-4 border-t">
                          <div className="text-sm text-gray-600">
                            📊 {campaignData.central_recipients.length} Empfänger → {Math.ceil(campaignData.central_recipients.length / autoSlotSize)} Slots à ~{autoSlotSize} Empfänger
                          </div>
                          <div className="space-x-2">
                            <button
                              onClick={() => setAutoSlotMode(false)}
                              className="px-3 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                            >
                              Abbrechen
                            </button>
                            <button
                              onClick={generateAutoSlots}
                              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center space-x-2"
                              disabled={!autoSlotTemplate.subject || !autoSlotTemplate.message || !autoSlotTemplate.from_name}
                            >
                              <Layers className="h-4 w-4" />
                              <span>Slots erstellen</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!slotSuggestions && (
                <button
                  onClick={handleSuggestSlots}
                  className="mb-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center space-x-2"
                >
                  <Target className="h-4 w-4" />
                  <span>Slot-Vorschläge generieren</span>
                </button>
              )}

              {/* Slot-Vorschläge */}
              {slotSuggestions && (
                <div className="bg-green-50 dark:bg-dark-surface p-4 rounded-lg border dark:border-dark-copper-dark">
                  <h3 className="font-medium text-green-900 dark:text-gray-100 mb-3 flex items-center">
                    <Target className="h-4 w-4 mr-2" />
                    Slot-Vorschläge ({slotSuggestions.suggested_slots.length})
                  </h3>
                  <div className="space-y-3">
                    {slotSuggestions.suggested_slots.map((suggestion, index) => (
                      <div key={index} className="bg-white dark:bg-dark-surface p-3 rounded border dark:border-dark-copper-dark">
                        <div className="font-medium text-gray-900 dark:text-gray-200">{suggestion.name}</div>
                        <div className="text-sm text-gray-600">{suggestion.description}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          Domains: {suggestion.domains.slice(0, 3).join(', ')}
                          {suggestion.domains.length > 3 && ` +${suggestion.domains.length - 3} weitere`}
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={applySuggestedSlots}
                    className="mt-3 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center space-x-2"
                  >
                    <Copy className="h-4 w-4" />
                    <span>Vorschläge übernehmen</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Schritt 3: Slots */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2 text-blue-600" />
                  {campaignData.execution_mode === 'segmented_parallel' ? (
                    <>Campaign Slots ({campaignData.slots.length} Slots aus {providerSegments.length} Segmenten)</>
                  ) : (
                    <>Campaign Slots ({campaignData.slots.length})</>
                  )}
                </h3>
                <div className="flex items-center space-x-2">
                  {campaignData.slots.length > 1 && (
                    <button
                      onClick={shuffleSlots}
                      className="px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center space-x-1"
                      title="Slot-Reihenfolge mischen"
                    >
                      <Shuffle className="h-4 w-4" />
                      <span>Mischen</span>
                    </button>
                  )}
                <button
                  onClick={addSlot}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2"
                >
                  <Plus className="h-4 w-4" />
                  <span>Slot hinzufügen</span>
                </button>
                </div>
              </div>

              {/* Empfänger-Verteilungs-Übersicht (nur bei zentraler Liste) */}
              {campaignData.central_recipients && campaignData.central_recipients.length > 0 && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 dark:text-gray-100 mb-3">📊 Empfänger-Verteilung</h4>
                  {(() => {
                    const totalCentral = campaignData.central_recipients.length;
                    let totalAssigned = 0;
                    
                    campaignData.slots.forEach(slot => {
                      // Zähle central_portion UND own_list (AI-Slots mit Recipients aus central_recipients)
                      if (slot.recipient_source === 'central_portion' && slot.max_recipients) {
                        totalAssigned += slot.max_recipients;
                      } else if (slot.recipient_source === 'own_list' && slot.recipients && slot.recipients.length > 0) {
                        // AI-Slots: Zähle die tatsächlichen Recipients
                        totalAssigned += slot.recipients.length;
                      }
                    });
                    
                    const remaining = totalCentral - totalAssigned;
                    const percentage = totalCentral > 0 ? Math.round((totalAssigned / totalCentral) * 100) : 0;
                    
                    return (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Gesamt verfügbar:</span>
                          <span className="font-medium">{totalCentral} Empfänger</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Zugewiesen:</span>
                          <span className="font-medium">{totalAssigned} Empfänger ({percentage}%)</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Verbleibend:</span>
                          <span className={`font-medium ${remaining > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                            {remaining} Empfänger
                          </span>
                        </div>
                        
                        {/* Fortschrittsbalken */}
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${
                              percentage === 100 ? 'bg-green-500' : 
                              percentage > 100 ? 'bg-red-500' : 'bg-blue-500'
                            }`}
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                          ></div>
                        </div>
                        
                        {remaining > 0 && (
                          <div className="text-xs text-orange-600 mt-2">
                            ⚠️ {remaining} Empfänger sind noch nicht zugewiesen
                          </div>
                        )}
                        {remaining < 0 && (
                          <div className="text-xs text-red-600 mt-2">
                            ❌ {Math.abs(remaining)} Empfänger zu viel zugewiesen
                          </div>
                        )}
                        {remaining === 0 && (
                          <div className="text-xs text-green-600 mt-2">
                            ✅ Alle Empfänger korrekt verteilt
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {campaignData.slots.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Layers className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Noch keine Slots erstellt</p>
                  <p className="text-sm">Klicken Sie auf "Slot hinzufügen" um zu beginnen</p>
                </div>
              ) : campaignData.execution_mode === 'segmented_parallel' ? (
                <SegmentedSlotsView 
                  slots={campaignData.slots}
                  providerSegments={providerSegments}
                  smtpServers={selectableSmtpServers}
                  onUpdateSlot={(slotId, updates) => updateSlot(slotId, updates)}
                  onRemoveSlot={(slotId) => removeSlot(slotId)}
                />
              ) : (
                <div className="space-y-4">
                  {campaignData.slots.map((slot, index) => (
                    <div key={slot.id} className="relative">
                      {/* Copy-Button für Slot */}
                      <div className="absolute top-2 right-2 z-10">
                        <button
                          onClick={() => copySlotTemplate(slot.id)}
                          className="px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700 flex items-center space-x-1"
                          title={`Template von ${slot.name} kopieren`}
                        >
                          <Copy className="h-3 w-3" />
                          <span>Copy</span>
                        </button>
                      </div>
                      
                    <CampaignSlotEditor
                      slot={slot}
                      slotNumber={index + 1}
                      centralRecipients={campaignData.central_recipients}
                      domainAnalysis={domainAnalysis}
                      smtpServers={selectableSmtpServers}
                      globalAttachments={campaignData.global_attachments || []}
                      onUpdate={(updates) => updateSlot(slot.id, updates)}
                      onRemove={() => removeSlot(slot.id)}
                    />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Schritt 4: Überprüfung */}
          {currentStep === 4 && (
            <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Campaign-Überprüfung</h3>
              
              {/* Campaign Info */}
                  <div className="bg-gray-50 dark:bg-dark-surface p-4 rounded-lg border dark:border-dark-copper-dark">
                <h4 className="font-medium mb-2 text-gray-900 dark:text-gray-100">Campaign-Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-300">Name:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-gray-200">{campaignData.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-300">Ausführung:</span>
                    <span className="ml-2 font-medium">
                      {campaignData.execution_mode === 'sequential' ? 'Sequenziell' : 'Parallel'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-300">Versandmethode:</span>
                    <span className="ml-2 font-medium">
                      {getSendMethodLabel(campaignData.send_method)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-300">Zentrale Empfänger:</span>
                    <span className="ml-2 font-medium">{campaignData.central_recipients.length}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-300">Slots:</span>
                    <span className="ml-2 font-medium">{campaignData.slots.length}</span>
                  </div>
                </div>
              </div>

              {/* Slots Übersicht */}
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900 dark:text-gray-100">Slots-Übersicht</h4>
                {campaignData.slots.map((slot, index) => (
                  <div key={slot.id} className="border border-gray-200 dark:border-dark-copper-dark rounded-lg p-4 bg-white dark:bg-dark-surface">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-medium text-gray-900 dark:text-gray-200">Slot {index + 1}: {slot.name}</h5>
                      <span className="text-sm text-gray-500 dark:text-gray-300">
                        {slot.emails_per_hour}/h
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-200 space-y-1">
                      <div>Betreff: {slot.subject || 'Nicht gesetzt'}</div>
                      <div>
                        Empfänger: {slot.recipient_source === 'own_list' 
                          ? `${slot.recipients?.length || 0} eigene` 
                          : `Domain-Filter (${slot.domain_filters?.length || 0} Domains)`
                        }
                      </div>
                      <div>
                        {slot.ai_content_variant_group ? (
                          <>🤖 AI-Content ({slot.ai_content_variant_group.toUpperCase()})</>
                        ) : (
                          <>Templates: {slot.html_templates ? slot.html_templates.filter(t => t.trim()).length : 0}</>
                        )}
                      </div>
                      <div>
                        {campaignData.send_method === 'gmx_webmail'
                          ? 'GMX-/mail.com-Accounts'
                          : campaignData.send_method === 'rambler_webmail'
                            ? 'Rambler.ru-Accounts'
                            : 'SMTP-Server'}: {slot.smtp_server_ids?.length || 'Alle verfügbaren'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Warnungen */}
              {campaignData.slots.some(slot => !slot.subject?.trim() || (slot.html_templates && !slot.html_templates[0]?.trim())) && (
                <div className="bg-yellow-50 dark:bg-dark-surface border border-yellow-200 dark:border-dark-copper-dark rounded-lg p-4">
                  <div className="flex items-center space-x-2 text-yellow-800">
                    <AlertCircle className="h-4 w-4" />
                    <span className="font-medium">Unvollständige Slots</span>
                  </div>
                  <div className="mt-2 text-sm text-yellow-700">
                    Einige Slots haben fehlende Betreff- oder Template-Angaben.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <div className="flex space-x-3">
            {currentStep > 1 && (
              <button
                onClick={prevStep}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Zurück
              </button>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            {currentStep === 2 && (campaignData.execution_mode === 'segmented_parallel' || campaignData.execution_mode === 'smtp_segmented_parallel' || campaignData.execution_mode === 'smtp_round_robin' || campaignData.execution_mode === 'burst_parallel') && (
              <label className="flex items-center space-x-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={useFastMode}
                  onChange={(e) => setUseFastMode(e.target.checked)}
                  className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                />
                <span className="text-orange-600 font-medium">⚡ Fast-Mode</span>
              </label>
            )}
            
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Abbrechen
            </button>
            
            {currentStep < 4 ? (
              <button
                onClick={nextStep}
                disabled={isNextStepLocked}
                className={`px-6 py-2 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${useFastMode && currentStep === 2 ? 'bg-orange-600 hover:bg-orange-700' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                {useFastMode && currentStep === 2 ? '⚡ Weiter (Fast)' : 'Weiter'}
              </button>
            ) : (
              <button
                onClick={handleCreateCampaign}
                disabled={createCampaignMutation.isLoading}
                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
              >
                <Play className="h-4 w-4" />
                <span>
                  {createCampaignMutation.isLoading ? 'Erstelle...' : 'Campaign erstellen'}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* 🎯 Segment-Config-Modal */}
      {segmentConfigModalOpen && currentSegment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-card rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  ⚙️ Segment-Konfiguration: {currentSegment.provider_name}
                </h2>
                <button
                  onClick={() => {
                    setSegmentConfigModalOpen(false);
                    setCurrentSegment(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-6 w-6" />
                </button>
    </div>
              
              <div className="space-y-6">
                
                {/* Versand-Einstellungen */}
                <div className="bg-blue-50 dark:bg-dark-surface p-4 rounded-lg border border-blue-200 dark:border-dark-copper">
                  <h3 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">⚡ Versand-Einstellungen</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* E-Mails pro Stunde - NUR bei SMTP */}
                    {campaignData.send_method !== 'webmail' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                        E-Mails pro Stunde
                      </label>
                      <input
                        type="number"
                        value={currentSegment.config?.versand?.rate_limit_per_hour || getDefaultRateLimitPerHour(campaignData.send_method)}
                        onChange={(e) => {
                          const updated = { ...currentSegment };
                          if (!updated.config) updated.config = { versand: {} };
                          if (!updated.config.versand) updated.config.versand = {};
                          updated.config.versand.rate_limit_per_hour = parseInt(e.target.value);
                          setCurrentSegment(updated);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded-md"
                        min="100"
                        max="20000"
                      />
                    </div>
                    )}
                    
                    {/* Anzahl Streams - NUR bei SMTP */}
                    {campaignData.send_method !== 'webmail' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                        Anzahl Streams
                      </label>
                      <input
                        type="number"
                        value={currentSegment.config?.versand?.streams_count || 1}
                        onChange={(e) => {
                          const updated = { ...currentSegment };
                          if (!updated.config) updated.config = { versand: {} };
                          if (!updated.config.versand) updated.config.versand = {};
                          updated.config.versand.streams_count = parseInt(e.target.value);
                          setCurrentSegment(updated);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded-md"
                        min="1"
                        max="5"
                      />
                    </div>
                    )}
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                        🎯 Anzahl Slots (Gesamt)
                      </label>
                      <input
                        type="number"
                        value={currentSegment.config?.versand?.total_slots || Math.ceil(currentSegment.recipient_count / 100)}
                        onChange={(e) => {
                          const updated = { ...currentSegment };
                          if (!updated.config) updated.config = { versand: {} };
                          if (!updated.config.versand) updated.config.versand = {};
                          const totalSlots = parseInt(e.target.value) || 1;
                          updated.config.versand.total_slots = totalSlots;
                          // Berechne slot_size automatisch
                          updated.config.versand.slot_size = Math.ceil(currentSegment.recipient_count / totalSlots);
                          setCurrentSegment(updated);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded-md"
                        min="1"
                        max={currentSegment.recipient_count}
                        placeholder="z.B. 100"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        📊 <strong>{Math.ceil(currentSegment.recipient_count / (currentSegment.config?.versand?.total_slots || Math.ceil(currentSegment.recipient_count / 100)))}</strong> Empfänger pro Slot
                        ({currentSegment.recipient_count} Empfänger ÷ {currentSegment.config?.versand?.total_slots || Math.ceil(currentSegment.recipient_count / 100)} Slots)
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                        Pause nach Slot (Sek)
                      </label>
                      <input
                        type="number"
                        value={currentSegment.config?.versand?.pause_after_each_slot || 5}
                        onChange={(e) => {
                          const updated = { ...currentSegment };
                          if (!updated.config) updated.config = { versand: {} };
                          if (!updated.config.versand) updated.config.versand = {};
                          updated.config.versand.pause_after_each_slot = parseInt(e.target.value);
                          setCurrentSegment(updated);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded-md"
                        min="0"
                        max="300"
                      />
                    </div>
                  </div>
                </div>
                
                {/* 📧 Dynamische Absender-E-Mail */}
                <div className="bg-green-50 dark:bg-dark-surface p-4 rounded-lg border border-green-200 dark:border-dark-copper">
                  <h3 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">📧 Absender-Einstellungen</h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`dynamic_from_${currentSegment.segment_number}`}
                        checked={currentSegment.config?.dynamic_from_email_enabled || false}
                        onChange={(e) => {
                          const updated = { ...currentSegment };
                          if (!updated.config) updated.config = {};
                          updated.config.dynamic_from_email_enabled = e.target.checked;
                          setCurrentSegment(updated);
                        }}
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`dynamic_from_${currentSegment.segment_number}`} className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                        Dynamische Absender-E-Mail verwenden
                      </label>
                    </div>
                    
                    {currentSegment.config?.dynamic_from_email_enabled && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                          E-Mail-Prefix (Domain wird von SMTP übernommen)
                        </label>
                        <input
                          type="text"
                          value={currentSegment.config?.dynamic_from_email || ''}
                          onChange={(e) => {
                            const updated = { ...currentSegment };
                            if (!updated.config) updated.config = {};
                            updated.config.dynamic_from_email = e.target.value;
                            setCurrentSegment(updated);
                          }}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-sm"
                          placeholder="z.B. info, kontakt, service"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Beispiel: "info" → info@domain.com, "team-($)3($)" → team-abc@domain.com
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* 📨 SMTP-Server Zuweisung - NUR bei SMTP */}
                {campaignData.send_method !== 'webmail' && (
                <div className="bg-blue-50 dark:bg-dark-surface p-4 rounded-lg border border-blue-200 dark:border-blue-700">
                  <h3 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">📨 SMTP-Server für dieses Segment</h3>
                  
                  <div className="space-y-3">
                    {selectableSmtpServers.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        <label className="flex items-center p-2 hover:bg-blue-100 dark:hover:bg-dark-card rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={(currentSegment.config?.smtp?.server_ids || []).length === 0}
                            onChange={(e) => {
                              const updated = { ...currentSegment };
                              if (!updated.config) updated.config = { smtp: {} };
                              if (!updated.config.smtp) updated.config.smtp = {};
                              updated.config.smtp.server_ids = e.target.checked ? [] : [selectableSmtpServers[0]?.id];
                              setCurrentSegment(updated);
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div className="ml-3">
                            <div className="font-medium text-gray-900 dark:text-gray-100">🔄 Alle SMTP-Server (Rotation)</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Automatische Rotation über alle verfügbaren Server</div>
                          </div>
                        </label>
                        
                        {selectableSmtpServers.map(smtp => (
                          <label key={smtp.id} className="flex items-center p-2 hover:bg-blue-100 dark:hover:bg-dark-card rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(currentSegment.config?.smtp?.server_ids || []).includes(smtp.id)}
                              onChange={(e) => {
                                const updated = { ...currentSegment };
                                if (!updated.config) updated.config = { smtp: {} };
                                if (!updated.config.smtp) updated.config.smtp = {};
                                const serverIds = updated.config.smtp.server_ids || [];
                                
                                if (e.target.checked) {
                                  updated.config.smtp.server_ids = [...serverIds, smtp.id];
                                } else {
                                  updated.config.smtp.server_ids = serverIds.filter(id => id !== smtp.id);
                                }
                                setCurrentSegment(updated);
                              }}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <div className="ml-3 flex-1">
                              <div className="font-medium text-gray-900 dark:text-gray-100">{smtp.from_name || smtp.from_email}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">{smtp.from_email} • {smtp.host}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">Keine SMTP-Server verfügbar. Bitte erst SMTP-Server erstellen.</p>
                    )}
                    
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      📊 Ausgewählt: {(currentSegment.config?.smtp?.server_ids || []).length === 0 ? 'Alle' : `${(currentSegment.config?.smtp?.server_ids || []).length} Server`}
                    </p>
                  </div>
                </div>
                )}
                
                {/* 📧 Webmail-Account Info - NUR bei Webmail */}
                {campaignData.send_method === 'webmail' && currentSegment.config?.webmail?.account_id && (
                <div className="bg-teal-50 dark:bg-dark-surface p-4 rounded-lg border border-teal-200 dark:border-teal-700">
                  <h3 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">📧 Webmail-Account für dieses Segment</h3>
                  <div className="flex items-center space-x-3 p-3 bg-white dark:bg-dark-card rounded-lg border border-teal-300 dark:border-teal-600">
                    <div className="flex-shrink-0">
                      <span className="text-2xl">📬</span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {currentSegment.config?.webmail?.account_email || 'Webmail-Account'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Zugewiesen durch Webmail-Segmentierung
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-teal-600 dark:text-teal-400 mt-3">
                    ℹ️ Bei Webmail-Versand wird automatisch 12.5s Cooldown zwischen Batches eingehalten (~5 Mails/min pro Account)
                  </p>
                </div>
                )}
                
                {/* 📊 Sending Pattern Strategy - NUR bei SMTP */}
                {campaignData.send_method !== 'webmail' && (
                <div className="bg-indigo-50 dark:bg-dark-surface p-4 rounded-lg border border-indigo-200 dark:border-dark-copper">
                  <h3 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">📊 Sending Pattern Strategy</h3>
                  
                  <div className="space-y-4">
                    {/* Pattern Auswahl */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Marketing-Strategie
                      </label>
                      <select
                        value={currentSegment.config?.sending_pattern?.type || 'constant'}
                        onChange={(e) => {
                          const presets = {
                            constant: { base_rate: 3000 },
                            pyramid: { warmup: { rate: 800, range: '1-20%' }, peak: { rate: 4000, range: '21-80%' }, cooldown: { rate: 1500, range: '81-100%' } },
                            inverse_pyramid: { burst: { rate: 6000, range: '1-30%' }, moderate: { rate: 3000, range: '31-70%' }, cooldown: { rate: 1500, range: '71-100%' } },
                            wave: { low: { rate: 2500 }, high: { rate: 5000 } },
                            flash_blast: { teaser: { rate: 500, range: '1-10%', pause: 600 }, blast: { rate: 8000, range: '11-60%', pause: 300 }, followup: { rate: 3000, range: '61-100%' } },
                            ghost_protocol: { min_rate: 50, max_rate: 200, duration_days: 7, jitter_ms: 2000 },
                            stealth_drip: { base_rate: 150, duration_hours: 8, jitter_ms: 1500 },
                            chaos_theory: { min_rate: 200, max_rate: 5000, jitter_ms: 3000 },
                            morning_rush: { morning: { rate: 5000, range: '1-50%' }, afternoon: { rate: 2500, range: '51-100%' } },
                            evening_blast: { buildup: { rate: 1500, range: '1-30%' }, peak: { rate: 6000, range: '31-80%' }, wind_down: { rate: 2000, range: '81-100%' } }
                          };
                          
                          const updated = { ...currentSegment };
                          if (!updated.config) updated.config = {};
                          updated.config.sending_pattern = {
                            type: e.target.value,
                            config: presets[e.target.value] || {},
                            jitter: { enabled: false, min_ms: -500, max_ms: 500 }
                          };
                          setCurrentSegment(updated);
                        }}
                        className="w-full px-2 py-1 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-sm"
                      >
                        <optgroup label="🎯 Standard">
                          <option value="constant">➡️ Constant (Gleiche Rate)</option>
                          <option value="pyramid">📈 Pyramid Warmup (4-5h, Safe)</option>
                          <option value="inverse_pyramid">📉 Inverse Pyramid (3-4h, Fast)</option>
                        </optgroup>
                        <optgroup label="🌊 Natural Patterns">
                          <option value="wave">🌊 Wave (3h, 2 Peaks)</option>
                          <option value="morning_rush">☀️ Morning Rush (4h, B2B)</option>
                          <option value="evening_blast">🌙 Evening Blast (4h, B2C)</option>
                        </optgroup>
                        <optgroup label="🚀 Aggressive">
                          <option value="flash_blast">⚡ Flash Blast (3h, Launch)</option>
                        </optgroup>
                        <optgroup label="🥷 Stealth Mode">
                          <option value="ghost_protocol">👻 Ghost Protocol (7 Tage Warmup!)</option>
                          <option value="stealth_drip">💧 Stealth Drip (8h Spread)</option>
                          <option value="chaos_theory">🎲 Chaos Theory (Random AF)</option>
                        </optgroup>
                      </select>
                    </div>
                    
                    {/* Pattern Info */}
                    {currentSegment.config?.sending_pattern?.type && currentSegment.config.sending_pattern.type !== 'constant' && (
                      <div className="bg-blue-50 dark:bg-dark-card p-3 rounded border border-blue-200 dark:border-blue-700">
                        <div className="text-xs text-gray-800 dark:text-gray-200 space-y-1">
                          {currentSegment.config.sending_pattern.type === 'pyramid' && (() => {
                            const totalRecipients = currentSegment.recipient_count || 0;
                            const warmupRecipients = Math.floor(totalRecipients * 0.2);
                            const peakRecipients = Math.floor(totalRecipients * 0.6);
                            const cooldownRecipients = totalRecipients - warmupRecipients - peakRecipients;
                            const warmupTime = warmupRecipients / 800;
                            const peakTime = peakRecipients / 4000;
                            const cooldownTime = cooldownRecipients / 1500;
                            const totalHours = warmupTime + peakTime + cooldownTime;
                            
                            return (
                              <>
                                <div>📈 Warmup (1-20%): {warmupRecipients} Empfänger @ 800/h → {Math.round(warmupTime * 60)}min</div>
                                <div>🚀 Peak (21-80%): {peakRecipients} Empfänger @ 4000/h → {Math.round(peakTime * 60)}min</div>
                                <div>📉 Cooldown (81-100%): {cooldownRecipients} Empfänger @ 1500/h → {Math.round(cooldownTime * 60)}min</div>
                                <div className="font-semibold mt-2 text-green-600">
                                  Total: ~{Math.round(totalHours * 10) / 10}h für {totalRecipients} Empfänger
                                </div>
                              </>
                            );
                          })()}
                          {currentSegment.config.sending_pattern.type === 'ghost_protocol' && (() => {
                            const totalRecipients = currentSegment.recipient_count || 0;
                            const avgRate = 125;  // (50 + 200) / 2
                            const totalHours = totalRecipients / avgRate;
                            const totalDays = totalHours / 24;
                            
                            return (
                              <>
                                <div>👻 Ultra-Stealth Mode</div>
                                <div>📧 {totalRecipients} Empfänger über {Math.round(totalDays * 10) / 10} Tage</div>
                                <div>📉 Rate: 50-200/h (Ø125/h, Random pro Slot)</div>
                                <div>🎲 Jitter: ±2000ms (±2 Sekunden pro Mail)</div>
                                <div>⏸️ Micro-Pausen: 30-180s random</div>
                                <div className="font-semibold mt-2 text-purple-600">
                                  Maximale Tarnung! ~{Math.round(totalHours)}h total
                                </div>
                              </>
                            );
                          })()}
                          {currentSegment.config.sending_pattern.type === 'flash_blast' && (() => {
                            const totalRecipients = currentSegment.recipient_count || 0;
                            const teaserRecipients = Math.floor(totalRecipients * 0.1);
                            const blastRecipients = Math.floor(totalRecipients * 0.5);
                            const followupRecipients = totalRecipients - teaserRecipients - blastRecipients;
                            const teaserTime = teaserRecipients / 500;
                            const blastTime = blastRecipients / 8000;
                            const followupTime = followupRecipients / 3000;
                            const totalTime = teaserTime + blastTime + followupTime + 0.25;  // +15min Pausen
                            
                            return (
                              <>
                                <div>🎬 Teaser (1-10%): {teaserRecipients} @ 500/h → {Math.round(teaserTime * 60)}min + 10min Pause</div>
                                <div>💥 BLAST (11-60%): {blastRecipients} @ 8000/h → {Math.round(blastTime * 60)}min</div>
                                <div>📧 Follow-up (61-100%): {followupRecipients} @ 3000/h → {Math.round(followupTime * 60)}min + 5min Pause</div>
                                <div className="font-semibold mt-2 text-orange-600">
                                  Total: ~{Math.round(totalTime * 10) / 10}h - Perfect für Launches!
                                </div>
                              </>
                            );
                          })()}
                          {/* ... andere Pattern-Infos */}
                        </div>
                      </div>
                    )}
                    
                    {/* 🎲 Random Jitter */}
                    <div className="border-t border-gray-200 dark:border-dark-copper pt-3">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <label className="text-xs font-medium text-gray-700 dark:text-gray-200">
                            🎲 Random Jitter (Anti-Detection)
                          </label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Fügt zufällige Verzögerung/Beschleunigung hinzu:<br/>
                            <strong>Min:</strong> Mail kann X ms <strong>FRÜHER</strong> gesendet werden<br/>
                            <strong>Max:</strong> Mail kann X ms <strong>SPÄTER</strong> gesendet werden
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={currentSegment.config?.sending_pattern?.jitter?.enabled || false}
                          onChange={(e) => {
                            const updated = { ...currentSegment };
                            if (!updated.config) updated.config = { sending_pattern: { jitter: {} } };
                            if (!updated.config.sending_pattern) updated.config.sending_pattern = { jitter: {} };
                            if (!updated.config.sending_pattern.jitter) updated.config.sending_pattern.jitter = {};
                            updated.config.sending_pattern.jitter.enabled = e.target.checked;
                            setCurrentSegment(updated);
                          }}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 rounded"
                        />
                      </div>
                      
                      {currentSegment.config?.sending_pattern?.jitter?.enabled && (
                        <>
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            <div>
                              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                                Früher senden (ms)
                              </label>
                              <input
                                type="number"
                                value={Math.abs(currentSegment.config?.sending_pattern?.jitter?.min_ms || -500)}
                                onChange={(e) => {
                                  const updated = { ...currentSegment };
                                  if (!updated.config.sending_pattern) updated.config.sending_pattern = { jitter: {} };
                                  updated.config.sending_pattern.jitter.min_ms = -Math.abs(parseInt(e.target.value));
                                  setCurrentSegment(updated);
                                }}
                                className="w-full px-2 py-1 border border-gray-300 dark:border-dark-copper rounded text-xs bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100"
                                min="0"
                                max="5000"
                                placeholder="500"
                              />
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Max. {Math.abs(currentSegment.config?.sending_pattern?.jitter?.min_ms || -500)}ms früher
                              </p>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                                Später senden (ms)
                              </label>
                              <input
                                type="number"
                                value={currentSegment.config?.sending_pattern?.jitter?.max_ms || 500}
                                onChange={(e) => {
                                  const updated = { ...currentSegment };
                                  if (!updated.config.sending_pattern) updated.config.sending_pattern = { jitter: {} };
                                  updated.config.sending_pattern.jitter.max_ms = Math.abs(parseInt(e.target.value));
                                  setCurrentSegment(updated);
                                }}
                                className="w-full px-2 py-1 border border-gray-300 dark:border-dark-copper rounded text-xs bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100"
                                min="0"
                                max="5000"
                                placeholder="500"
                              />
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Max. {currentSegment.config?.sending_pattern?.jitter?.max_ms || 500}ms später
                              </p>
                            </div>
                          </div>
                          
                          <div className="bg-yellow-50 dark:bg-dark-card p-2 rounded border border-yellow-200 dark:border-yellow-700 mt-2">
                            <p className="text-xs text-gray-700 dark:text-gray-200">
                              💡 <strong>Beispiel:</strong> Bei -500ms/+500ms wird jede Mail zufällig zwischen 500ms früher und 500ms später gesendet (Total: 1 Sekunde Variation)
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                )}
                
                {/* 🤖 AI-Varianten (DAS HERZSTÜCK!) */}
                <div className="bg-purple-50 dark:bg-dark-surface p-4 rounded-lg border border-purple-200 dark:border-purple-700">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-md font-semibold text-gray-900 dark:text-gray-100">🤖 AI-Content-Varianten</h3>
                    <button
                      onClick={() => {
                        // Berechne automatisch Slot-Range
                        const existingVariants = currentSegment.ai_variants || [];
                        const lastVariant = existingVariants[existingVariants.length - 1];
                        const totalSlots = currentSegment.config?.versand?.total_slots || Math.ceil(currentSegment.recipient_count / 100);
                        const rangeStart = lastVariant ? (lastVariant.slot_range_end + 1) : 1;
                        const rangeEnd = Math.min(rangeStart + 49, totalSlots);  // Default: 50 Slots oder Rest
                        
                        // ✅ AI generiert X Versionen = Anzahl Slots in Range!
                        const slotsInRange = rangeEnd - rangeStart + 1;
                        const newVariant = {
                          id: Date.now(),
                          variant_group: `input_${String.fromCharCode(97 + existingVariants.length)}`,
                          input_message: '',
                          input_subject: '',
                          input_from_name: '',
                          total_variations: slotsInRange,  // ← Eine Version pro Slot!
                          variation_level: 'medium',
                          slot_range_start: rangeStart,
                          slot_range_end: rangeEnd,
                          // 🎯 NEU: SMTP-Auswahl & Empfängeranzahl
                          recipient_count: 0,  // Wird automatisch berechnet
                          smtp_server_ids: []  // Leer = Alle Segment-SMTPs
                        };
                        
                        const updated = { ...currentSegment };
                        if (!updated.ai_variants) updated.ai_variants = [];
                        updated.ai_variants.push(newVariant);
                        setCurrentSegment(updated);
                      }}
                      className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
                    >
                      + AI-Variante hinzufügen
                    </button>
                  </div>
                  
                  {currentSegment.ai_variants && currentSegment.ai_variants.length > 0 ? (
                    <div className="space-y-4">
                      {currentSegment.ai_variants.map((variant, vIdx) => (
                        <div key={variant.id} className="bg-white dark:bg-dark-card border border-purple-200 dark:border-purple-700 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {variant.variant_group.toUpperCase()} (Slots {variant.slot_range_start}-{variant.slot_range_end})
                            </div>
                            <button
                              onClick={() => {
                                const updated = { ...currentSegment };
                                updated.ai_variants = updated.ai_variants.filter((_, i) => i !== vIdx);
                                setCurrentSegment(updated);
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                                  Von Slot #
                                </label>
                                <input
                                  type="number"
                                  value={variant.slot_range_start}
                                  onChange={(e) => {
                                    const updated = { ...currentSegment };
                                    updated.ai_variants[vIdx].slot_range_start = parseInt(e.target.value);
                                    setCurrentSegment(updated);
                                  }}
                                  className="w-full px-2 py-1 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-sm"
                                  min="1"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                                  Bis Slot #
                                </label>
                                <input
                                  type="number"
                                  value={variant.slot_range_end}
                                  onChange={(e) => {
                                    const updated = { ...currentSegment };
                                    updated.ai_variants[vIdx].slot_range_end = parseInt(e.target.value);
                                    setCurrentSegment(updated);
                                  }}
                                  className="w-full px-2 py-1 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-sm"
                                  min={variant.slot_range_start}
                                />
                              </div>
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                                Subject
                              </label>
                              <input
                                type="text"
                                value={variant.input_subject}
                                onChange={(e) => {
                                  const updated = { ...currentSegment };
                                  updated.ai_variants[vIdx].input_subject = e.target.value;
                                  setCurrentSegment(updated);
                                }}
                                className="w-full px-2 py-1 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-sm"
                                placeholder="Betreff für diese Variante"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                                HTML-Template
                              </label>
                              <textarea
                                value={variant.input_message}
                                onChange={(e) => {
                                  const updated = { ...currentSegment };
                                  updated.ai_variants[vIdx].input_message = e.target.value;
                                  setCurrentSegment(updated);
                                }}
                                rows={4}
                                className="w-full px-2 py-1 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-sm font-mono"
                                placeholder="<html>...</html>"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                                From-Name
                              </label>
                              <input
                                type="text"
                                value={variant.input_from_name}
                                onChange={(e) => {
                                  const updated = { ...currentSegment };
                                  updated.ai_variants[vIdx].input_from_name = e.target.value;
                                  setCurrentSegment(updated);
                                }}
                                className="w-full px-2 py-1 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-sm"
                                placeholder="Absender-Name"
                              />
                            </div>
                            
                            {/* 🎯 NEU: Empfängeranzahl */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                                📊 Empfängeranzahl (optional)
                              </label>
                              <input
                                type="number"
                                value={variant.recipient_count || 0}
                                onChange={(e) => {
                                  const updated = { ...currentSegment };
                                  updated.ai_variants[vIdx].recipient_count = parseInt(e.target.value) || 0;
                                  setCurrentSegment(updated);
                                }}
                                className="w-full px-2 py-1 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded text-sm"
                                placeholder="0 = Automatisch berechnet"
                                min="0"
                              />
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Leer lassen für automatische Berechnung basierend auf Slot-Range
                              </p>
                            </div>
                            
                            {/* 🎯 NEU: SMTP-Auswahl */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-2">
                                📨 SMTP-Server (optional)
                              </label>
                              <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 dark:border-dark-copper rounded p-2 bg-gray-50 dark:bg-dark-card">
                                {(() => {
                                  // Hole verfügbare Segment-SMTPs
                                  const segmentSmtpIds = currentSegment.config?.smtp?.server_ids || [];
                                  const availableSmtps = selectableSmtpServers.filter(s => segmentSmtpIds.includes(s.id));
                                  
                                  if (availableSmtps.length === 0) {
                                    return (
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Keine SMTPs im Segment konfiguriert
                                      </p>
                                    );
                                  }
                                  
                                  return availableSmtps.map(smtp => (
                                    <label key={smtp.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-surface p-1 rounded">
                                      <input
                                        type="checkbox"
                                        checked={(variant.smtp_server_ids || []).includes(smtp.id)}
                                        onChange={(e) => {
                                          const updated = { ...currentSegment };
                                          if (!updated.ai_variants[vIdx].smtp_server_ids) {
                                            updated.ai_variants[vIdx].smtp_server_ids = [];
                                          }
                                          
                                          if (e.target.checked) {
                                            updated.ai_variants[vIdx].smtp_server_ids.push(smtp.id);
                                          } else {
                                            updated.ai_variants[vIdx].smtp_server_ids = updated.ai_variants[vIdx].smtp_server_ids.filter(id => id !== smtp.id);
                                          }
                                          setCurrentSegment(updated);
                                        }}
                                        className="rounded text-purple-600 focus:ring-purple-500"
                                      />
                                      <span className="text-xs text-gray-700 dark:text-gray-200">
                                        {smtp.from_email} ({smtp.host})
                                      </span>
                                    </label>
                                  ));
                                })()}
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Leer = Alle {currentSegment.config?.smtp?.server_ids?.length || 0} Segment-SMTPs werden verwendet
                              </p>
                            </div>
                            
                            {/* Warnung falls zu viele Slots */}
                            {(() => {
                              const totalSlots = currentSegment.config?.versand?.total_slots || Math.ceil(currentSegment.recipient_count / 100);
                              const slotSize = currentSegment.config?.versand?.slot_size || Math.ceil(currentSegment.recipient_count / totalSlots);
                              const requestedSlots = variant.slot_range_end - variant.slot_range_start + 1;
                              
                              if (variant.slot_range_end > totalSlots) {
                                return (
                                  <div className="bg-red-50 dark:bg-red-900/20 p-2 rounded border border-red-200 dark:border-red-700">
                                    <p className="text-xs text-red-700 dark:text-red-300">
                                      ⚠️ <strong>Warnung:</strong> Insgesamt nur <strong>{totalSlots}</strong> Slots konfiguriert!<br/>
                                      Deine Range: Slot {variant.slot_range_start}-{variant.slot_range_end} ({requestedSlots} Slots)<br/>
                                      <strong className="text-red-600 dark:text-red-400">→ Slots {totalSlots + 1}-{variant.slot_range_end} existieren nicht!</strong>
                                    </p>
                                  </div>
                                );
                              }
                              
                              return (
                                <div className="bg-blue-50 dark:bg-dark-card p-2 rounded text-xs text-gray-700 dark:text-gray-200 border border-blue-200 dark:border-blue-700">
                                  ℹ️ Slot Range: <strong>{requestedSlots}</strong> Slots ({requestedSlots * slotSize} Empfänger)<br/>
                                  🤖 AI generiert: <strong>{requestedSlots}</strong> verschiedene Versionen von diesem Input<br/>
                                  💡 Jeder Slot bekommt 1 einzigartige Version (wie Sequential)
                                </div>
                              );

                            })()}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-600 dark:text-gray-300 text-sm">
                      Keine AI-Varianten definiert.<br/>
                      Klicke auf "+ AI-Variante hinzufügen" um verschiedene Content-Inputs zu erstellen.
                    </div>
                  )}
                </div>
                
                {/* Speichern Button */}
                {/* AWS S3 Redirects */}
                <AwsRedirectConfig
                  segment={currentSegment}
                  onUpdate={(updated) => setCurrentSegment(updated)}
                  isAdmin={isAdmin}
                />
                
                {/* Search.app Redirects */}
                <SearchAppRedirectConfig
                  segment={currentSegment}
                  onUpdate={(updated) => setCurrentSegment(updated)}
                  isAdmin={isAdmin}
                />
                
                {/* Door-Opener (Warm-Up) */}
                <DoorOpenerConfig
                  segment={currentSegment}
                  onUpdate={(updated) => setCurrentSegment(updated)}
                />
                
                {/* Mail-Fingerprint (Header + Encoding + EHLO) - NUR bei SMTP */}
                {campaignData.send_method !== 'webmail' && (
                <FingerprintConfig
                  segment={currentSegment}
                  onUpdate={(updated) => setCurrentSegment(updated)}
                />
                )}
                
                {/* Slot-Redirects (Domain-Rotation) */}
                <SlotRedirectConfig
                  segment={currentSegment}
                  onUpdate={(updated) => setCurrentSegment(updated)}
                  isAdmin={isAdmin}
                />
                
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    onClick={() => {
                      setSegmentConfigModalOpen(false);
                      setCurrentSegment(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={() => {
                      // Update Segment in providerSegments Array
                      setProviderSegments(prev => 
                        prev.map(seg => 
                          seg.segment_number === currentSegment.segment_number 
                            ? currentSegment 
                            : seg
                        )
                      );
                      setSegmentConfigModalOpen(false);
                      setCurrentSegment(null);
                      toast.success('Segment-Konfiguration gespeichert');
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                  >
                    Speichern
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Smart Replicate Modal */}
      <SmartReplicateModal
        isOpen={smartReplicateModalOpen}
        onClose={() => setSmartReplicateModalOpen(false)}
        onConfirm={handleSmartReplicate}
        segmentCount={providerSegments.length}
        sendMethod={campaignData.send_method}
      />
    </div>
  );
};

export default MultiCampaignCreator;
